// Client HTTP du back-office (MÊME ORIGINE : /v1). En dev, Vite proxifie /v1 → 127.0.0.1:3000 ; en prod,
// Fastify sert le SPA et l'API sous le même hôte.
//
// ⚠️ AUCUN décodeur d'enveloppe maison ici : il vient de la primitive partagée
// `@orgazone/api-envelope/client`, pendant exact de `applyEnvelope` côté serveur — les deux côtés du
// contrat sortent du même package, donc ne peuvent pas diverger. Un décodeur maison qui lisait
// `/v1/auth/*` à la racine a rendu la connexion impossible à l'EDI le 2026-08-01 (boucle login → 401 →
// login). Ne reste ici que ce qui est PROPRE au back-office : son préfixe, sa politique 401, ses formes.
import { createApiClient } from '@orgazone/api-envelope/client';
import { bounceOnUnauthorized } from '@orgazone/ui-web';
import type { AuthMode } from '@orgazone/auth-web/client';
import type { NegativeBalanceCause } from '@orgazone/core-loyalty';
import type { ItemKind } from '@orgazone/core-catalog';
import type { QuickPhotoSize, QuickSourceKind, QuickTileColor, QuickTileKind, QuickTileLook, QuickTileVisual, Vocabulary, VocabularyFieldKey, VocabularyLang } from '@orgazone/sync-contract';

const client = createApiClient({
  baseUrl: '/v1',
  // Politique 401 COMMUNE aux surfaces web (@orgazone/ui-web) : une session perdue en cours d'usage
  // repasse par la GARDE DE ROUTE, qui choisit SSO ou écran local, en gardant la destination.
  // ⚠️ Ne jamais viser `/login` directement : c'est l'écran de bris de glace, et y aller
  // court-circuiterait la bascule SSO. Les routes `/auth/*` en sont exemptées par le client partagé.
  onUnauthorized: bounceOnUnauthorized,
});

const req = <T>(method: string, path: string, body?: unknown): Promise<T> => client.request<T>(method, path, body);

/**
 * Le filtre de l'écran Stock, écrit UNE fois.
 * ⚠️ LES QUATRE VUES DOIVENT ENVOYER LE MÊME : deux constructions de query-string finiraient par
 * diverger sur un paramètre, et deux onglets afficheraient des totaux différents pour le même stock —
 * sans que rien ne dise lequel est le bon.
 */
/** Taille d'un lot de la liste Stock — MIROIR de `STOCK_PAGE_MAX` serveur (même nombre, dit une fois par bord). */
export const STOCK_PAGE_SIZE = 1000;

export interface StockQuery {
  q?: string; family?: string[]; brand?: string[]; hideZeros?: boolean; negatives?: boolean;
  limit?: number; offset?: number;
  rows?: string; cols?: string; measure?: string; stores?: string;
  sort?: string; dir?: string;
  /** `AAAA-MM-JJ` — le stock TEL QU'IL ÉTAIT ce jour-là. Absent = aujourd'hui. */
  asOf?: string;
}
function stockQs(f: StockQuery): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  // ⚠️ RÉPÉTÉS, pas joints : une famille peut porter une virgule dans son nom.
  for (const v of f.family ?? []) p.append('family', v);
  for (const v of f.brand ?? []) p.append('brand', v);
  if (f.hideZeros) p.set('hideZeros', '1');
  if (f.negatives) p.set('negatives', '1');
  if (f.limit != null) p.set('limit', String(f.limit));
  if (f.offset) p.set('offset', String(f.offset));
  if (f.rows) p.set('rows', f.rows);
  if (f.cols) p.set('cols', f.cols);
  if (f.measure) p.set('measure', f.measure);
  if (f.stores) p.set('stores', f.stores);
  if (f.sort) p.set('sort', f.sort);
  if (f.dir) p.set('dir', f.dir);
  if (f.asOf) p.set('asOf', f.asOf);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

/**
 * LA CLÉ D'IDEMPOTENCE D'UN GESTE — unique à CHAQUE geste, jamais à sa cible.
 *
 * ⚠️ CE QU'ELLE ÉVITE. Le moteur d'événements retrouve un identifiant déjà journalisé et rend la main
 * SANS RIEN ÉCRIRE : c'est ce qui rend un import rejouable sans doubler. Une clé stable — du genre
 * « detach:brand:JACK » — transforme donc ce no-op en PLAFOND DÉFINITIF. Détacher une marque, la
 * rattacher, puis vouloir la détacher à nouveau ne faisait plus rien, et l'écran répondait
 * « enregistré » (constaté à l'écran le 2026-08-21). Même piège sur un coefficient qu'on remet à une
 * valeur qu'il a déjà portée.
 *
 * ⚠️ L'INTENTION NE DOIT PAS PORTER LA VALEUR ÉCRITE : « coef:supplier:PVH » et non
 * « coef:supplier:PVH:25000 ». Ce qu'on veut distinguer, ce sont deux GESTES, et l'instant s'en charge.
 */
export const cleDeGeste = (intention: string, now: number): string => `${intention}:${now}`;

/** Identité de session servie par `/v1/auth/me` (socle @orgazone/auth-web). */
export interface PennylaneEtat {
  canSeal: boolean;
  configured: boolean;
  probe: { ok: boolean; detail: string } | null;
}
export interface PennylaneDepot {
  ok: boolean;
  days?: { day: string; verdict: 'depose' | 'inchange' | 'correctif_depose' | 'refuse'; detail?: string }[];
}

/** Un réglage de caisse tel que le back-office le voit : sa valeur, son sens, qui peut l'écrire. */
export interface StoreSettingRow {
  name: string;
  /** `null` = jamais posé en base (la caisse applique son repli) — différent de la chaîne vide. */
  value: string | null;
  scope: 'bo_only' | 'admin_tablet';
  note: string;
  editable: boolean;
  /** Sa valeur ne redescend JAMAIS du serveur : l'écran ne peut que la remplacer. */
  secret?: boolean;
  /** Pour un secret : une valeur est-elle enregistrée ? C'est tout ce que l'écran sait de lui. */
  pose?: boolean;
}

/**
 * UNE LIGNE DU DICTIONNAIRE DU MAGASIN — un champ d'article et les trois étages de son mot, dans UNE langue.
 * ⚠️ `tradeDefault` et `override` valent `null` quand la ligne `setting` n'est pas posée OU est vide :
 *    le résolveur du contrat traite les deux pareil (« une surcharge vide retombe sur le défaut »), la
 *    vue le dit donc de la même façon — l'écran n'annonce jamais une surcharge que rien n'affiche.
 */
export interface StoreVocabularyField {
  key: VocabularyFieldKey;
  /** Le filet de la plateforme dans cette langue — l'IDENTITÉ de la ligne à l'écran, qui ne bouge jamais. */
  platform: string;
  /** Le mot du métier, descendu par la Console (`vocab_default.<key>.<lang>`). */
  tradeDefault: string | null;
  /** Le mot du commerçant (`vocab.<key>.<lang>`). */
  override: string | null;
  /** Ce que les écrans affichent : la cascade résolue par le serveur, jamais vide, jamais la clé. */
  effective: string;
}

/** Le dictionnaire du magasin actif, dans l'ordre où une fiche présente ses champs. */
export interface StoreVocabularyView {
  lang: VocabularyLang;
  fields: StoreVocabularyField[];
}

/**
 * CE QUE « MON COMPTE » DOIT SAVOIR DE SA PROPRE SÉCURITÉ (`/v1/auth/security`, socle @orgazone/auth-web).
 * ⚠️ `localPassword` faux = un compte venu de la connexion Orgazone (SSO) : il n'a pas de mot de passe ici, et
 *    l'écran ne lui montre ni changement de mot de passe ni double authentification (décision utilisateur).
 */
export interface AccountSecurity {
  localPassword: boolean;
  /** ISO, ou `null` — un mot de passe dont aucun choix n'a été daté (les comptes d'avant la colonne). */
  passwordChangedAt: string | null;
  /** La longueur que le serveur exige — l'écran l'annonce, il ne la recopie pas. */
  passwordMinLength: number;
}

/** Une session ouverte de ce compte, telle que le socle la décrit (`/v1/auth/sessions`). */
export interface AccountSession {
  id: string;
  /** ISO. */
  createdAt: string;
  /** ISO — la dernière requête vue sur cette session. */
  lastSeenAt: string;
  ip: string | null;
  userAgent: string | null;
  /** C'est la session de ce navigateur — elle ne se ferme pas d'ici (la déconnexion est au pied du rail). */
  current: boolean;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
  /**
   * Le rôle en CLAIR (« Administrateur », « Support Orgazone »), servi par le serveur avec les
   * permissions — il est le seul à connaître la liste des rôles.
   * ⚠️ ABSENT si le rôle n'a pas de libellé : on n'affiche alors RIEN plutôt que la clé technique
   *    (« orgazone_staff » s'affichait tel quel au pied du rail, constaté 2026-09-05).
   */
  roleLabel?: string;
  status: string;
  /**
   * La langue de CE compte (SPEC §22.1) — `null` = celle de la plateforme, absente si le serveur n'en
   * tient pas. Le serveur la sert AVEC l'utilisateur pour qu'elle soit posée avant le premier rendu.
   * ⚠️ Volontairement `string` et non `VocabularyLang` : elle vient de la BASE, où une langue retirée
   *    du contrat peut survivre. C'est `setCurrentVocabularyLang` qui la ramène à ce qu'on sait rendre.
   */
  locale?: string | null;
  /**
   * ⚠️ CALCULÉES PAR LE SERVEUR, et comparées ici en chaînes EXACTES (le RBAC du BO ne pose aucun
   * joker, précisément pour ça) : l'écran ne peut donc pas offrir un bouton que l'API refusera.
   */
  permissions: string[];
}

/** Un magasin que le compte connecté a le droit d'ouvrir. ⚠️ Jamais son DSN. */
export interface AuthorizedStore {
  gln: string;
  name: string;
  sCode: string | null;
}

/**
 * L'ACCÈS CROSS-TENANT DE CETTE SESSION (doctrine d'identité §4ter).
 *
 * ⚠️ SERVI PAR LE SERVEUR, au même titre que `permissions` — jamais déduit d'un paramètre d'URL, d'un
 * `localStorage` ni d'un état de navigation. Le bandeau qu'il alimente est le SEUL témoin de « à la
 * place de qui j'agis » : falsifiable côté navigateur, il s'effacerait d'un coup d'éditeur d'adresse
 * pendant que l'accès, lui, resterait ouvert (§23.0).
 * `active: false` est la réponse normale d'une session de client.
 */
export type CrossTenantContext =
  | { active: false }
  | {
      active: true; gln: string; storeName: string; staffEmail: string; openedAt: string; expiresAt: string;
      /** Où retourner en quittant l'accès. `null` = le serveur ne connaît pas la Console (cas normal). */
      returnUrl: string | null;
    };

/**
 * LE CONTEXTE MAGASIN DE LA SESSION — magasin actif, magasins ouverts, droit d'en changer.
 *
 * ⚠️ `active` EST CALCULÉ PAR LE SERVEUR, à chaque requête, et il décide quelle BASE les écrans liront.
 * Le navigateur ne l'envoie jamais : il l'apprend. En changer n'est pas un filtre, c'est un changement
 * de base (CADRAGE §5) — d'où le rechargement pleine page côté écran.
 * ⚠️ `selectable: false` = session cross-tenant : le magasin est celui du ticket, et l'API refuse d'en
 * poser un autre. Le sélecteur n'est alors pas rendu du tout, plutôt que rendu et refusé.
 */
export interface StoreContextView {
  active: {
    gln: string; name: string; source: 'cross-tenant' | 'grant'; currency: { code: string; fraction: number };
    /** Les mots que CE magasin emploie pour ses champs — posés sur la SPA avant le premier rendu. */
    vocabulary: Vocabulary;
  } | null;
  stores: AuthorizedStore[];
  selectable: boolean;
}

/**
 * UN MODÈLE du catalogue — une ligne de l'écran Articles.
 *
 * ⚠️ UNE LIGNE = UN MODÈLE, alors que la base stocke une ligne PAR DÉCLINAISON : le regroupement est
 * fait en SQL, et la pagination porte sur les groupes. Les montants restent des ENTIERS dans leur
 * échelle d'origine (÷100 pour le TTC, ÷10 000 pour le PA fin, points de base pour la marge) : la
 * conversion appartient à l'affichage.
 * ⚠️ `*Min`/`*Max` plutôt qu'une valeur unique : deux déclinaisons d'un modèle peuvent porter des prix
 * différents, et en élire une comme « le » prix serait un chiffre inventé.
 */
export interface ArticleModel {
  modelKey: string;
  name: string;
  articleCode: string | null;
  variantCount: number;
  gencode: string | null;
  gencodeCount: number;
  family: string | null; familyMixed: boolean;
  sector: string | null; sectorMixed: boolean;
  supplier: string | null; supplierMixed: boolean;
  season: string | null; seasonMixed: boolean;
  brand: string | null; brandMixed: boolean;
  priceMinCents: number; priceMaxCents: number;
  /** Prix de vente HT et TVA contenue, en fourchette sur les déclinaisons vendables — calculés par le serveur. */
  priceExclTaxMinCents: number | null; priceExclTaxMaxCents: number | null;
  taxMinCents: number | null; taxMaxCents: number | null;
  costMinFine: number; costMaxFine: number;
  marginMinBp: number | null; marginMaxBp: number | null;
  /**
   * Taux de TVA du modele, en points de base — ce qui permet d en tirer le HT, donc le coefficient.
   * ⚠️ `vatMixed` DIT que les declinaisons n ont pas toutes le meme taux : un chiffre unique y serait
   * faux, et l ecran doit alors renvoyer a la fiche plutot que d en choisir un (ecran « Prix & marges »).
   */
  vatRateBp: number | null;
  vatMixed: boolean;
  /**
   * Régime d'achat du modèle — `corner` = le prix d'achat se DÉDUIT du coefficient contractuel, il ne
   * se lit pas au tarif (même règle que la fiche). `regimeMixed` = les déclinaisons divergent.
   */
  purchaseRegime: 'firm' | 'corner' | null;
  regimeMixed: boolean;
  /**
   * Coefficient CONTRACTUEL applicable, en points de base — clause de la MARQUE par-dessus celle du
   * fournisseur, jointes par CODE NORMALISÉ côté serveur (rapprocher les libellés à l'écran ratait
   * « TEXTIL NORD » vs « Textil Nord », en silence). `null` = pas de clause, ou dimension mêlée.
   */
  supplierCoefficientBp: number | null;
  /** `null` = AUCUNE ligne de stock pour ce modèle — distinct d'un stock mesuré à zéro. */
  stockQty: number | null;
  /**
   * L adresse de LA photo du modele — la vignette de la liste. `null` = aucune photo MARQUEE.
   * ⚠️ On ne retombe pas sur la premiere venue : l absence est ce qui donne envie d en choisir une.
   */
  mainPhotoUrl: string | null;
  updatedAt: number | null;
  activeCount: number;
  /**
   * LES TAILLES DU MODÈLE, RÉSUMÉES par le serveur : la grille, son second axe, la première et la
   * dernière taille DANS L'ORDRE DE LA GRILLE. L'écran compose « 38 → 46 × 30/32/34 » sans rien trier.
   */
  sizeGrid: string | null;
  sizeGridMixed: boolean;
  /** Le NOM de la grille du second axe — c'est LUI qui donne ses valeurs à la colonne « Tailles ». */
  sizeGrid2: string | null;
  secondAxis: boolean;
  sizeFirst: string | null;
  sizeLast: string | null;
  sizedCount: number;
}

/**
 * LA FICHE d'un modèle — ce que le clic sur une ligne ouvre.
 *
 * ⚠️ ELLE DÉCRIT LE MODÈLE ENTIER, jamais la déclinaison cherchée : les agrégats couvrent toutes les
 * déclinaisons, y compris celles qu'une recherche ou une facette avait exclues de la liste. L'écran le
 * DIT — sans quoi la fiche paraît contredire la liste dont elle sort.
 *
 * ⚠️ LES DRAPEAUX DE DIVERGENCE NE SONT PAS OPTIONNELS : un drapeau absent serait indiscernable d'un
 * « les déclinaisons s'accordent ».
 * ⚠️ Les fourchettes de prix sont `| null` : aucune déclinaison active ⇒ AUCUN prix vendable, ce qui
 * n'est pas un prix à zéro.
 */
export interface ArticleModelDetail {
  modelKey: string;
  name: string | null; nameMixed: boolean;
  articleCode: string | null;
  /** Référence commerciale du MODÈLE — ni une clé, ni une identité. */
  modelCode: string | null; modelCodeMixed: boolean;
  variantCount: number;
  gencode: string | null;
  gencodeCount: number;
  /** Déclinaisons SANS code-barres : le producteur ne sait pas les atteindre (il écrit par gencode). */
  variantsWithoutGencode: number;
  activeCount: number;
  family: string | null; familyMixed: boolean;
  /** Couleur du repère de famille. ⚠️ `null` partout aujourd'hui : rien n'écrit `family.color`. */
  familyColor: string | null;
  /** Les coloris reels du modele — la source des pastilles de l onglet Photos. */
  colors: string[];
  /**
   * Coefficient CONTRACTUEL du fournisseur, en points de base (13585 = ×1,3585).
   * ⚠️ En corner c est LUI qui commande le prix d achat ; `null` = rien au contrat, à DIRE.
   */
  supplierCoefficientBp: number | null;
  sector: string | null; sectorMixed: boolean;
  supplier: string | null; supplierMixed: boolean;
  season: string | null; seasonMixed: boolean;
  brand: string | null; brandMixed: boolean;
  subFamily: string | null; subFamilyMixed: boolean;
  collection: string | null; collectionMixed: boolean;
  priceMinCents: number | null; priceMaxCents: number | null;
  costMinFine: number | null; costMaxFine: number | null;
  vatRateBp: number | null; vatMixed: boolean;
  weightG: number | null; weightMixed: boolean;
  originCountry: string | null; originMixed: boolean;
  customsCode: string | null; customsMixed: boolean;
  composition: string | null; compositionMixed: boolean;
  /**
   * LES QUATRE DRAPEAUX. ⚠️ `discountable` porte le sens de la COLONNE (« les remises s'appliquent »),
   * pas celui de l'écran (« interdites ») : l'inversion se fait une seule fois, à l'affichage, là où
   * le libellé la rend évidente.
   * ⚠️ Les `mixed` disent « certaines oui, certaines non » — un état RÉEL sur douze déclinaisons.
   */
  webActive: boolean; webMixed: boolean;
  discountable: boolean; discountMixed: boolean;
  excludedFromStats: boolean; statsMixed: boolean;
  stockManaged: boolean; stockManagedMixed: boolean;
  purchaseRegime: string | null; purchaseRegimeMixed: boolean;
  /** La nature du modèle — `null` si la base porte une valeur hors liste. */
  itemKind: ItemKind | null; itemKindMixed: boolean;
  /** Le NOM de la grille de tailles du modèle — c'est lui qui porte l'ORDRE des colonnes de la matrice. */
  sizeGrid: string | null; sizeGridMixed: boolean;
  /**
   * Le NOM de la grille du SECOND axe (longueurs, bonnets…), ou `null`.
   * ⚠️ C'EST UN CHAMP D'ARTICLE, au même titre que la grille : il s'édite dans la même carte de
   * différences et part par le même pied. Le rattacher à la grille principale l'aurait imposé à tous
   * les modèles qui la portent.
   */
  sizeGrid2: string | null; sizeGrid2Mixed: boolean;
  stockQty: number | null;
  createdAt: number | null;
  /** ⚠️ « Dernière écriture », JAMAIS « Modifié le » : c'est le curseur de delta de la sync. */
  updatedAt: number | null;
}

/** Une page de modèles, telle que le serveur l'a RÉELLEMENT appliquée (taille plafonnée comprise). */
export interface ArticlePageView {
  rows: ArticleModel[];
  /** Total mesuré du jeu filtré — jamais `rows.length`. */
  total: number;
  page: { index: number; size: number };
  sort: { key: string; dir: 'asc' | 'desc' };
}

/** Une DÉCLINAISON, telle que le dépliage de ligne la montre. */
export interface ArticleVariant {
  id: string;
  gencode: string | null;
  color: string | null;
  size: string | null;
  size2: string | null;
  priceCents: number;
  stockQty: number | null;
  isActive: boolean;
  /** Ce qui interdit de corriger le code-barres, ou `null` si c’est permis — un MOTIF, pas un booléen. */
  renameLock: 'vendu' | 'sorti' | 'inventorie' | null;
}

/**
 * Les déclinaisons d'un modèle, AVEC leur borne.
 * ⚠️ Le serveur plafonne le dépliage ; `truncated` dit si la base en portait davantage. Sans lui,
 * l'écran présenterait une matrice amputée comme si elle était complète.
 */
/**
 * CE QU'UNE ACTION DE MASSE A RÉELLEMENT FAIT.
 * ⚠️ `variants` et non `models` est le chiffre à montrer : l'opérateur coche des LIGNES, mais ce sont
 * les déclinaisons qui changent — et il y en a souvent douze par ligne. Annoncer « 3 modifiés » quand
 * 36 déclinaisons ont bougé serait faux.
 */
/** Ce qu'une opération par critère toucherait — mesuré avant de rien écrire. */
export interface ScopePreflight {
  models: number;
  /** ⚠️ C'EST CE NOMBRE QUE LE PLAFOND BORNE, pas les modèles : un modèle porte une ou soixante lignes. */
  variants: number;
  maxVariants: number;
  runnable: boolean;
  /**
   * L'instant de la mesure, DATÉ PAR LE SERVEUR.
   * ⚠️ À RENVOYER TEL QUEL à l'écriture, jamais remplacé par l'heure du poste : la garde le compare à
   * des dates écrites par le serveur, et une horloge décalée la ferait mentir sans jamais échouer.
   */
  asOf: number;
  /**
   * Durée ATTENDUE de l opération, en secondes, tirée d un débit mesuré (530 déclinaisons/s).
   * ⚠️ UNE ESTIMATION QUI SE DIT TELLE : le serveur écrit en une transaction et ne publie aucune
   * progression. L annoncer évite qu on croie l écran figé ; la peindre en barre serait un
   * chronomètre déguisé.
   */
  estimatedSeconds: number;
}

/** Le compte rendu d'une écriture par critère — quatre nombres, et chacun a son rôle. */
export interface ScopeReport extends BulkReport {
  /** Modèles ÉCARTÉS parce qu'ils ont changé depuis l'affichage : ils n'ont PAS été touchés. */
  drifted: number;
  /** Modèles décochés à la main et respectés. */
  excluded: number;
}

export interface BulkReport {
  /**
   * Modèles DEMANDÉS.
   * ⚠️ SÉPARÉ DE `models` : c'est cette valeur-là qui partait sous le nom de l'autre, et le rapport
   * annonçait donc le demandé comme s'il l'avait constaté. Leur écart est la seule chose qui dise que
   * le catalogue a bougé sous les doigts de l'opérateur.
   */
  modelsRequested: number;
  models: number;
  variants: number;
  /** Entités RÉELLEMENT écrites. ⚠️ DISTINCT de `variants`, qui compte les déclinaisons RÉSOLUES. */
  written: number;
  /**
   * Rejeu reconnu : l’événement existait déjà, RIEN n’a été réécrit.
   * ⚠️ À AFFICHER, jamais à taire : c’est en le taisant qu’un écran annonce « Enregistré sur 12
   * déclinaisons » alors qu’il n’a rien écrit — le faux succès du 2026-08-17.
   */
  duplicates: number;
}


/** Une ligne du grand livre d'un modèle (onglet Mouvements). */
export interface StockMovementRow {
  id: string;
  at: number;
  type: string;
  gencode: string;
  color: string | null;
  size: string | null;
  qty: number;
  /** Solde du MODÈLE après cette opération — calculé par le serveur, jamais lu en base. */
  balanceAfter: number;
  reason: string | null;
  note: string | null;
  /** Le document d'origine d'une ENTRÉE — n° de BL, référence EDI. `null` si le lien n'existe pas. */
  sourceRef: string | null;
  /**
   * D'où vient l'entrée : `edi` · `manual` · `reprise_entree` / `reprise_historique_*` (la reprise
   * legacy — ⚠️ PAS `legacy`, valeur documentée un temps mais jamais écrite). `null` sans réception liée.
   */
  origin: string | null;
  supplierId: string | null;
  /** La VENTE derrière ce mouvement (`mvt:<ligne fiscale>`) — null sur un mouvement repris (le legacy
   *  ne portait pas le lien) ou hors vente. Le numéro s'affiche, l'id ouvre le journal (`?open=`). */
  saleId: string | null;
  saleNumber: string | null;
}

/** Une case de l’état de stock : un coloris × une taille (onglet Stock). */
export interface StockCell {
  color: string | null; size: string | null; gencode: string;
  entries: number;
  /** VENTES au sens strict (motif famille vente) — jamais une rétrocession, un retour ou une démarque. */
  sales: number;
  /** Les autres sorties (rétro envoyée, retour fournisseur, casse, démarque…) — détaillées au clic. */
  otherOut: number;
  adjustments: number; stock: number;
}

export interface StockStateSet {
  cells: StockCell[]; colors: string[]; sizes: string[];
  /** Stock de TOUTES les déclinaisons — calculé en base avant la troncature, jamais sur `cells`. */
  totalStock: number;
  /** Déclinaisons dont le stock ne se déduit pas du grand livre complet (écarts de reprise). */
  unexplained: number;
  /** La grille est amputée : des coloris entiers peuvent manquer. */
  truncated: boolean;
  /** Le plafond appliqué, servi par le serveur — l'écran le nomme, il ne le recopie pas. */
  limit: number;
  /** Combien de déclinaisons le modèle porte en tout — « 200 sur 468 », jamais « plus de 200 ». */
  totalCells: number;
}

export interface StockMovementSet {
  rows: StockMovementRow[];
  /** Solde courant = celui de la ligne la plus récente. La légende de l'onglet s'y appuie. */
  currentBalance: number;
  truncated: boolean;
}

/** Un visuel d’article. `url` est RELATIVE (`/v1/media/photo/<cle>`) — jamais absolue. */
export interface ArticlePhoto {
  id: string;
  color: string | null;
  url: string | null;
  /** Le rang classe DANS UN CORLORIS : 0 = le visuel de CETTE couleur. */
  rank: number;
  /**
   * LA photo du modele — la vignette des listes, une seule pour tout l article, tous coloris confondus.
   * ⚠️ Distincte du rang 0 : un modele en cinq coloris a cinq visuels de couleur et une photo de fiche.
   */
  isMain: boolean;
}

/** La performance mesurée d’un modèle (onglet Stats) — tout vient d’une table, rien n’est simulé. */
export interface ArticleStats {
  soldQty: number; entryQty: number; revenueCents: number;
  /** CA HORS TAXES, ventile par taux par `core-vat` — jamais une division par un taux en dur. */
  revenueExclTaxCents: number;
  costFine: number;
  stockQty: number; shrinkQty: number;
  /** Semaines depuis la première réception — `null` s’il n’y en a aucune, jamais zéro. */
  weeks: number | null;
  firstEntryAt: number | null; lastEntryAt: number | null;
  firstSaleAt: number | null; lastSaleAt: number | null;
  weekly: { start: number; qty: number }[];
  bySize: { label: string; qty: number }[];
  byColor: { label: string; qty: number }[];
}

/** Une modification, telle que le JOURNAL la porte (onglet Historique). */
export interface ArticleChange {
  id: string; at: number; actor: string | null; gencode: string | null;
  /** Nom technique du champ — l’écran le traduit. `__created__` = naissance de la déclinaison. */
  field: string; from: string | null; to: string | null;
  /** Date de création dans le système d’ORIGINE (reprise) — absente quand l’article est né ici. */
  origin?: number | null;
  /** Combien de déclinaisons ce MÊME changement a touchées. À 1, le gencode est affiché. */
  variants: number;
  /** Le nom du compte, résolu à la LECTURE — absent = on retombe sur l’identifiant. */
  actorName?: string | null;
}

export interface ArticleHistory {
  rows: ArticleChange[];
  /** Le plus ancien événement connu — il BORNE l’histoire que le journal peut raconter. */
  since: number | null;
  truncated: boolean;
}

/** Une réception valorisée — ce qui COMPOSE le prix moyen pondéré (onglet Prix). */
export interface PurchaseEntryRow {
  id: string;
  at: number;
  gencode: string;
  /** N° de BL, référence EDI — ou l'origine, quand la ligne reprise n'en portait aucune. */
  document: string;
  origin: string;
  supplier: string | null;
  quantity: number;
  /** Échelle FINE (×100 du centime) : le PMP se conserve en sous-centime. */
  unitPriceFine: number;
}

export interface PurchaseEntrySet {
  rows: PurchaseEntryRow[];
  /** ⚠️ Cumuls sur TOUTES les entrées, jamais sur la page rendue : un PMP tronqué serait crédible et faux. */
  totalQuantity: number;
  totalValueFine: number;
  /** `null` si rien n'est entré — « pas de PMP » n'est pas « PMP nul ». */
  /**
   * Coût du stock DÉTENU. ⚠️ Ce n’est PAS `totalValueFine ÷ totalQuantity` : ces deux-là résument les
   * ACHATS, celui-ci le stock qui reste. Ils diffèrent dès qu’une vente s’intercale entre deux prix.
   */
  pmpFine: number | null;
  /** Quantité en stock — le dénominateur du PMP. */
  stockQty: number;
  /** Valeur du stock détenu, à l’échelle fine. */
  stockValueFine: number;
  /** Déclinaisons en stock sans prix d’achat connu — exclues du PMP. */
  variantsWithoutCost: number;
  /** Déclinaisons au grand livre incohérent — exclues du PMP. */
  variantsInconsistent: number;
  truncated: boolean;
}

/**
 * LE SECOND AXE d'une grille de tailles (longueur de jambe, bonnet…) — son libellé et ses valeurs
 * dans l'ORDRE DÉCLARÉ, même règle que `sizeOrder`.
 */
export interface SecondDimension {
  /** Le NOM de la grille secondaire — l'identité d'une grille, quel que soit son axe. */
  name: string;
  label: string;
  values: string[];
}

export interface ArticleVariantSet {
  rows: ArticleVariant[];
  truncated: boolean;
  limit: number;
  /**
   * ORDRE DECLARE des tailles — jamais l ordre alphabetique.
   * ⚠️ C est une DONNEE, pas un affichage : range alphabetiquement, un tableau de tailles donnerait L
   * avant M et S avant XS, et personne ne saurait plus le lire. Vide = aucune grille rattachee.
   */
  sizeOrder: string[];
  /**
   * La seconde dimension du modèle, portée par L'ARTICLE (`size_grid2_id`) — `null` s'il n'en a pas.
   * ⚠️ C'est elle qui décide si la matrice a des lignes par couleur, jamais les déclinaisons.
   */
  dimension: SecondDimension | null;
}


// ── Journal des ventes ──────────────────────────────────────────────────────
// ⚠️ LES MONTANTS SONT DES CENTIMES ENTIERS et `datetimeFiscal` une chaîne AAAAMMJJHHMMSS : c'est
// l'heure LOCALE de la caisse. La reconvertir en `Date` la ferait réinterpréter dans le fuseau du
// navigateur — une vente de 00h30 basculerait de jour selon qui regarde.

/** Une PIÈCE telle que la liste du journal la montre. */
export interface SalesRow {
  id: string;
  pieceNumber: string;
  pieceType: string;
  operationType: string;
  registerId: string;
  datetimeFiscal: string;
  operatorName: string;
  clientName: string;
  /** HT et TVA tels que la pièce les porte ; `null` = non renseignés par le logiciel d'origine (reprise). */
  totalExclTaxCents: number | null;
  totalTaxCents: number | null;
  totalInclTaxCents: number;
  methods: string[];
  /**
   * Quantité VENDUE de la pièce (×1000) — la colonne « Art. ».
   * ⚠️ En millièmes comme la base : un article se vend parfois au poids.
   */
  qtyMilli: number;
  /** Duplicata DÉJÀ tirés — lu dans le journal des réimpressions, jamais deviné. */
  reprintCount: number;
  /** Renseigné si une pièce d'annulation a contre-passé celle-ci — qui, et quand. */
  cancelledBy: string | null;
  cancelledAt: string | null;
}

/** L'arithmétique de la PÉRIODE (jamais de l'onglet, jamais de la page). */
export interface SalesSummary {
  pieces: number;
  netCents: number;
  /** Le même périmètre en HT et en TVA ; `null` dès qu'une pièce retenue ne porte pas le sien. */
  netExclTaxCents: number | null;
  netTaxCents: number | null;
  /** Pièces retenues dont la TVA n'a pas été renseignée par le logiciel d'origine. */
  unrecordedTaxCount: number;
  cancelledCount: number;
  cancelledCents: number;
  creditCount: number;
  creditCents: number;
}

export interface SalesPageView {
  rows: SalesRow[];
  /** Total mesuré de l'onglet filtré — jamais `rows.length`. */
  total: number;
  page: { index: number; size: number };
  sort: { key: string; dir: 'asc' | 'desc' };
  summary: SalesSummary;
  /** Les caisses qui ont RÉELLEMENT écrit sur la période — le filtre ne propose que ce qui existe. */
  registers: string[];
  /** Idem pour les vendeurs et les moyens de règlement, mesurés sur la période SEULE. */
  operators: string[];
  methods: string[];
  /** Jour de la plus ancienne pièce du magasin (AAAAMMJJ) — borne basse de « Tout l'historique ». */
  firstDay: string | null;
}

/**
 * UN PANIER SUSPENDU sur une caisse.
 * ⚠️ PAS une pièce fiscale : ni numéro, ni signature, et il n'entre dans aucun total de l'écran.
 */
export interface PendingSale {
  id: string;
  registerId: string;
  operatorName: string;
  clientName: string;
  totalInclTaxCents: number;
  qtyMilli: number;
  contentSummary: string;
  heldAt: number;
}
/** L'état d'une caisse sur une journée — ce que montre une carte de l'écran « Z & clôtures ». */
export interface CashRegisterDay {
  registerId: string;
  sessionId: string | null;
  status: string | null;
  openedAtFiscal: string | null;
  closedAtFiscal: string | null;
  /** Le chiffre d'affaires de la session, SANS les titres vendus — comme le Z signé. */
  netCents: number;
  /** Les titres vendus : encaissés, hors chiffre d'affaires. */
  titlesIssuedCents: number;
  pieces: number;
  expectedCashCents: number | null;
  varianceCents: number | null;
  /** ⚠️ `true` = fermée par la caisse sans comptage du tiroir : l'écart à zéro n'est PAS un tiroir
   *  vérifié, c'est l'absence de vérification. */
  autoClosed: boolean;
  varianceCountId: string | null;
  justification: { motifCode: string; note: string | null; author: string; createdAt: number } | null;
  /** `true` = caisse issue d'une REPRISE : consultable, jamais modifiable ni justifiable. */
  reprise: boolean;
}

export interface CashDayView {
  day: string;
  registers: CashRegisterDay[];
  vat: { rateBp: number; baseExclCents: number; taxCents: number }[];
  payments: { methodCode: string; amountCents: number }[];
  totalInclCents: number;
  /** Les titres vendus du jour : encaissés, hors chiffre d'affaires. */
  titlesIssuedCents: number;
  /** ⚠️ Le référentiel du commerçant, pas une liste en dur : VIDE = aucune justification possible. */
  motifs: { code: string; label: string }[];
  /** Sessions que la caisse n'a pas encore datées — invisibles sur toutes les journées, donc dites. */
  undatedSessions: number;
  /** ⚠️ Bascule de journée fiscale (§9.4bis), en minutes : le navigateur ne peut pas la deviner. */
  dayCutoffMinutes: number;
  /** Les caisses DÉJÀ vues dans ce magasin — dessinent la journée vide sans rien inventer. */
  /**
   * Les caisses qui EXISTAIENT ce jour-là — jamais « toutes celles qu'on a connues ».
   * ⚠️ C'est ce qui empêche une caisse de reprise d'apparaître sur les ventes d'aujourd'hui, et une
   * caisse en service d'apparaître sur des mois où elle n'était pas installée.
   */
  expectedRegisters: string[];
  /** Premier jour d'activité du magasin ; avant, il n'y a rien à dessiner. */
  firstActivityDay: string | null;
}

/** Un mois de caisse : ce qu'il pèse, et s'il est scellé. */
export interface CashMonth {
  ym: string;
  pieces: number;
  netCents: number;
  taxCents: number;
  /** Somme des écarts EN VALEUR ABSOLUE — un manquant et un excédent ne s'annulent pas. */
  varianceCents: number;
  unjustified: number;
  missingZ: number;
  /** `true` = un `fiscal_grand_total` de type GTM existe : le mois est scellé par une caisse. */
  sealed: boolean;
}

export interface CashBlocker {
  day: string;
  registerId: string;
  kind: 'missing_z' | 'unjustified_variance';
  varianceCents: number | null;
}

export interface CashMonthDayRegister {
  registerId: string;
  closed: boolean;
  /** Heure locale du Z, `HHMM` — `null` si la caisse n'a pas clôturé. */
  closedAt: string | null;
  netCents: number;
  pieces: number;
  varianceCents: number | null;
  justified: boolean;
}

export interface CashMonthDay {
  day: string;
  netCents: number;
  pieces: number;
  registers: CashMonthDayRegister[];
}

/** Le Z d'une session, tel que la caisse l'a SIGNÉ — aucun champ n'est recalculé côté serveur. */
export interface SessionZ {
  zNumber: string;
  registerId: string;
  datetimeFiscal: string;
  pieceCounter: number;
  periodInclCents: number;
  periodExclCents: number;
  periodVat: { rateBp: number; baseExclCents: number; taxCents: number }[];
  /** Cumul PERPÉTUEL du registre — pas le chiffre d'affaires du jour. */
  cumulativeInclCents: number;
  signature: string;
  prevSignature: string;
  signatureAlgo: string;
}

export interface SessionCount {
  countType: string;
  datetimeFiscal: string | null;
  operator: string;
  totalCountedCents: number;
  expectedCents: number;
  varianceCents: number;
  note: string | null;
  /** Le tiroir n'a pas été compté : le compté écrit est l'attendu, l'écran dit « non compté ». */
  uncounted: boolean;
  /** Les coupures comptées, de la plus grande à la plus petite ; `null` : non compté, ou illisible. */
  denominations: DrawerDenomination[] | null;
  /** Le détail des coupures ne se lit pas — l'écran le dit. */
  unreadable: boolean;
}

/** Une coupure et son nombre. */
export interface DrawerDenomination {
  valueCents: number;
  count: number;
}

export interface SessionMovement {
  movementType: string;
  amountCents: number;
  reason: string;
  /** La catégorie d'un frais, telle que la caisse l'a écrite ; `null` hors frais. */
  expenseCategory: string | null;
  datetimeFiscal: string | null;
  operator: string;
}

export interface SessionDeposit {
  depositType: string;
  amountCents: number;
  datetimeFiscal: string | null;
  slipRef: string | null;
  /** Les coupures sorties du tiroir ; `null` quand le dépôt ne les détaille pas. */
  denominations: DrawerDenomination[] | null;
  unreadable: boolean;
}

/** La vie d'une session : ouverture, comptages, mouvements, remises, et le Z s'il existe. */
export interface SessionDetail {
  sessionId: string;
  registerId: string;
  status: string;
  openedAtFiscal: string | null;
  closedAtFiscal: string | null;
  /** Le fond déclaré à l'ouverture. */
  openingFloatCents: number;
  /** Le fond calculé que la caisse proposait, ou `null` (première ouverture, caisse d'avant). */
  carriedOverCents: number | null;
  openingOperator: string;
  closingOperator: string | null;
  counts: SessionCount[];
  movements: SessionMovement[];
  deposits: SessionDeposit[];
  /** Ce que le tiroir contenait à l'ouverture : le comptage du matin, sinon le fond déclaré. */
  openingDrawerCents: number;
  /** Les ventes en espèces nettes, déduites du théorique de clôture de la caisse ; `null` tant que rien n'est clos. */
  cashSalesCents: number | null;
  /** Le reste au tiroir après les dépôts d'espèces — le fond proposé le lendemain ; `null` tant que rien n'est clos. */
  drawerLeft: { amountCents: number; denominations: DrawerDenomination[] | null } | null;
  /** `null` = aucun Z scellé — normal tant que la session est ouverte. */
  z: SessionZ | null;
}

export interface CashMonthsView {
  months: CashMonth[];
  /** Les blocages du mois le plus ANCIEN non scellé — celui qu'on clôture ensuite. */
  blockers: CashBlocker[];
  blockingYm: string | null;
}

/** ⚠️ `supported: false` = ce magasin ne remonte pas encore ce canal — ce n'est pas « aucun panier ». */
export interface PendingSalesView { rows: PendingSale[] | null; supported: boolean }

/** Une vente abandonnée — un panier vidé avant d'être encaissé (`sales/abandoned.ts`). */
export interface AbandonedRow {
  id: string;
  /** L'heure de l'abandon, en heure locale du magasin `AAAAMMJJHHMMSS`. */
  voidedAtLocal: string;
  registerId: string;
  operatorName: string;
  lineCount: number;
  /** Ce que la caisse estimait au moment de l'abandon — rien n'a été encaissé ni scellé. */
  estimatedTotalInclTaxCents: number;
  paymentStarted: boolean;
  /** Le motif enregistré par la caisse (une clé) ; `null` pour une reprise de l'ancien logiciel. */
  reason: string | null;
  fromLegacy: boolean;
}

/** Les ventes abandonnées d'une période, leur résumé et leur résumé par vendeur. */
/** Les domaines du journal d'activité — la liste fermée du serveur (`activity/feed.ts`), que l'écran nomme. */
export type ActivityDomain = 'register' | 'objectives' | 'access';

/** D'où vient un fait : une caisse, le back-office, la Console. */
export type ActivityOrigin = { kind: 'register'; registerId: string } | { kind: 'backoffice' } | { kind: 'console' };

/** Une ligne du journal d'activité : des FAITS et un code d'action, jamais une phrase — l'écran la compose. */
export interface ActivityRowView {
  key: string;
  atMs: number;
  /** L'instant en heure locale du magasin `AAAAMMJJHHMMSS`. */
  atLocal: string;
  domain: ActivityDomain;
  kind: string;
  actor: string;
  origin: ActivityOrigin;
  /** Le fait cite sa pièce au journal fiscal. */
  nf525: boolean;
  facts: Record<string, string | number | boolean | null>;
  /** Le texte d'un accès, tel que le journal d'accès l'a écrit. */
  text?: string;
}

/** Une page du journal d'activité. `count` et `actors` n'arrivent qu'avec la première page. */
export interface ActivityPageView {
  rows: ActivityRowView[];
  next: string | null;
  count: number | null;
  actors: string[] | null;
  domains: ActivityDomain[];
}

/** Ce que l'écran demande : une période entière ou aucune, des domaines, un auteur, une recherche, un curseur. */
export interface ActivityParams {
  from?: string;
  to?: string;
  domains?: ActivityDomain[];
  actor?: string;
  q?: string;
  before?: string;
  size?: number;
}

export interface AbandonedPageView {
  rows: AbandonedRow[];
  page: { index: number; size: number };
  summary: { count: number; totalCents: number; paymentStartedCount: number };
  byOperator: { operatorName: string; count: number; totalCents: number; paymentStartedCount: number }[];
  registers: string[];
  operators: string[];
}

/** Ce qu'il y avait au panier d'une vente abandonnée. */
export interface AbandonedDetailView {
  row: AbandonedRow;
  lines: { lineNo: number; gencode: string | null; label: string; qtyMilli: number; unitPriceInclTaxCents: number }[];
}

/** Le détail scellé d'une pièce : ce que le dépliage montre. */
export interface SalesDetail {
  piece: SalesRow & {
    seqNo: number;
    totalExclTaxCents: number;
    totalTaxCents: number;
    totalDiscountCents: number;
    signature: string;
    prevSignature: string;
    signatureAlgo: string;
    appVersion: string;
    /** Le motif de la remise globale (libellé au référentiel, ou son code) — `null` sans motif. */
    globalDiscountReason: string | null;
  };
  lines: {
    lineNo: number; label: string; gencode: string | null; lineType: string;
    qtyMilli: number; unitPriceInclTaxCents: number; lineDiscountCents: number;
    /** Le motif de la remise saisie au comptoir — `null` sans motif. */
    discountReason: string | null;
    totalInclTaxCents: number; vatRateBp: number; vatCode: string;
  }[];
  payments: { methodCode: string; amountCents: number; ref: string | null }[];
  taxes: { rateBp: number; vatCode: string; baseExclTaxCents: number; taxCents: number }[];
  /**
   * Duplicata tirés, un par un.
   * ⚠️ AUCUN ÉCRAN NE LES REND aujourd’hui : la modale suit la maquette, qui n’affiche pas
   * l’empreinte fiscale d’une vente (elle la loge sur l’écran « Z & clôtures »). La LIGNE du
   * journal en montre le COMPTE (`reprintCount`, pastille « DUP ×n »), qui est bien de la maquette.
   * Le détail reste servi pour l’API interne — c’est un état voulu, pas un oubli.
   */
  reprints: { reprintNo: number; channel: string; reason: string | null; operatorId: string; datetimeFiscal: string }[];
}

/** Ce que l'écran Journal demande. ⚠️ AUCUN GLN : le magasin lu est celui de la session. */
/**
 * LES TRIS DU JOURNAL QUE LE SERVEUR ACCEPTE (`SALES_SORT_COLUMNS`) — gardés égaux par `tris-journal-accord.test.ts`.
 * ⚠️ Une clé de colonne de l'écran qui n'y est pas fait REFUSER la lecture : c'est ce qui arrivait au clic sur « Caisse ».
 */
export const SALES_SORT_KEYS = ['heure', 'piece', 'caisse', 'operateur', 'client', 'articles', 'ht', 'tva', 'total'] as const;
export type SalesSortKey = (typeof SALES_SORT_KEYS)[number];
export const isSalesSortKey = (v: string): v is SalesSortKey => (SALES_SORT_KEYS as readonly string[]).includes(v);

export interface SalesQueryParams {
  page: number;
  size: number;
  sort: SalesSortKey;
  dir: 'asc' | 'desc';
  tab: 'all' | 'cancelled' | 'credits';
  /** Bornes AAAAMMJJ calculées par le NAVIGATEUR — le serveur ne devine aucune journée locale. */
  from?: string;
  to?: string;
  /** Caisses retenues, séparées par des virgules — vide = toutes. */
  register?: string;
  /** Vendeur retenu — vide = tous. */
  operator?: string;
  /** Code de règlement retenu — vide = tous. */
  method?: string;
  q?: string;
}

/** Ce que l'écran Articles demande au serveur. ⚠️ AUCUN GLN : le magasin lu est celui de la session. */
/** Une valeur proposée par un filtre, et le nombre de MODÈLES qu'elle porte dans le catalogue. */
export interface FacetValue { value: string; count: number }

/** Les dimensions filtrables de l'écran Articles. */
/**
 * Les dimensions de choix. ⚠️ DEUX SOURCES : `fam`/`sup`/`sais`/`sect`/`marq` sont du TEXTE sur
 * l'article (l'ensemble des valeurs EST le catalogue) ; `sfam`/`coll` viennent d'une TABLE de
 * référentiel — une collection neuve doit être choisissable avant que le premier article ne la porte.
 */
export type ArticleFacet = 'fam' | 'sup' | 'sais' | 'sect' | 'marq' | 'sfam' | 'coll' | 'coul';

/**
 * Ce que les filtres ont le droit d'offrir.
 * ⚠️ Les compteurs portent LE CATALOGUE, pas la vue filtrée — mesurés sur la vue, ils finiraient
 * par n'offrir que la valeur déjà choisie (défaut corrigé sur le sélecteur de caisse du journal).
 */
/** Une grille de tailles offerte au rattachement d'un modèle. */
/** Une marque du référentiel — `coefficientBp` à `null` = elle hérite de son fournisseur. */
export interface MarqueDuReferentiel {
  code: string;
  label: string;
  coefficientBp: number | null;
  /** Combien de MODÈLES la portent — jamais des déclinaisons, qui gonfleraient avec la gamme. */
  models: number;
}

/**
 * Conditions et coordonnées d'un fournisseur — `null` partout tant que rien n'est saisi.
 * ⚠️ `null` n'est pas zéro : « aucune remise saisie » ne pré-remplit rien, « remise 0 % » si.
 */
export interface SupplierTerms {
  discountBp: number | null;
  shippingCents: number | null;
  earlyPaymentBp: number | null;
  earlyPaymentDays: number | null;
  accountCode: string | null;
  address1: string | null;
  address2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  orderEmail: string | null;
}

export interface ReferentialSupplier extends MarqueDuReferentiel {
  brands: MarqueDuReferentiel[];
  terms: SupplierTerms;
}

/**
 * UN MOYEN DE RÈGLEMENT tel que l'onglet le voit.
 * ⚠️ `sealedPayments` N'EST PAS UNE STATISTIQUE : c'est lui qui décide du geste offert. Au-dessus de
 *    zéro, le moyen a encaissé un règlement SCELLÉ, donc il ne se supprime plus jamais (append-only
 *    NF525) — l'écran n'offre alors que la désactivation, et dit pourquoi.
 */
export interface ReferentialPaymentMethod {
  code: string;
  label: string;
  /** Code UNTDID 4461 (Factur-X) — `null` quand rien n'a été précisé. */
  method4461: number | null;
  isActive: boolean;
  sortOrder: number;
  sealedPayments: number;
}

/**
 * UN MOTIF DE JUSTIFICATION tel que l'onglet le voit.
 * ⚠️ `usages` NE BLOQUE RIEN : il dit ce que la suppression COÛTERA. Le schéma pointe le référentiel
 *    au lieu de figer le libellé — renommer suit donc l'historique, supprimer le fait retomber sur
 *    le code brut.
 */
/**
 * UNE CATÉGORIE DE FRAIS du magasin (`catalog/referential-expense-categories.ts`) — `usages` compte les frais du tiroir qui
 * la citent : une catégorie employée ne se supprime pas, le producteur la refuserait.
 */
export interface ReferentialExpenseCategory {
  code: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  usages: number;
}

/** UN TAUX DE TVA et ce qui le porte (`catalog/referential-vat.ts`). */
export interface ReferentialVatRate {
  rateBp: number;
  /** Un taux de la loi (proposable) ; sinon un taux ancien porté par des articles repris. */
  legal: boolean;
  offered: boolean;
  /** Modèles en service qui le portent. */
  models: number;
  /** Familles dont c'est le taux par défaut. */
  families: number;
}

/** Une famille et son taux par défaut — `null` : celui du magasin. */
export interface ReferentialVatFamily {
  code: string;
  label: string;
  color: string | null;
  isActive: boolean;
  models: number;
  vatRateBp: number | null;
}

/** Un modèle dont une déclinaison s'écarte du taux par défaut de sa famille. */
export interface ReferentialVatOffModel {
  modelKey: string;
  name: string;
  articleCode: string | null;
  family: string | null;
  ratesBp: number[];
  defaultBp: number;
  variants: number;
}

/** RÉFÉRENTIELS › TAUX DE TVA, en une lecture. */
export interface ReferentialVat {
  franchise: boolean;
  /** `rateBp` `null` : la valeur posée (`stored`) est illisible. */
  storeDefault: { stored: string | null; rateBp: number | null };
  /** `ratesBp` `null` : la valeur posée est illisible, les menus proposent tous les taux de la loi. */
  offered: { stored: string | null; ratesBp: number[] | null };
  rates: ReferentialVatRate[];
  families: ReferentialVatFamily[];
  /** Les premiers modèles hors défaut, et les comptes entiers. */
  off: { models: number; variants: number; rows: ReferentialVatOffModel[] };
}

/** Ce qu'un écran de création lit pour proposer un taux. */
/** Un modèle d'article qu'aucune remise n'atteint : la fiche l'interdit (pour tout ou partie), ou prix libre. */
export interface NonDiscountableModel {
  name: string;
  code: string;
  reason: 'flagged' | 'partly_flagged' | 'free_price';
}

/** Les modèles qu'aucune remise n'atteint — les premiers, et combien en tout. */
export interface NonDiscountableView {
  total: number;
  rows: NonDiscountableModel[];
  limit: number;
}

export interface VatDefaults {
  offeredRatesBp: number[] | null;
  storeDefaultBp: number | null;
  /** Les familles qui portent un taux ; les autres suivent le magasin. */
  families: { code: string; vatRateBp: number }[];
}

export interface ReferentialJustification {
  code: string;
  label: string;
  /** La portée (`JUSTIFICATION_SCOPES`) — `null` ou un domaine historique écrit en clair : proposé à aucun geste. */
  scope: string | null;
  sortOrder: number;
  /** Proposé — `false` = masqué : plus offert, l'historique garde son libellé. */
  isActive: boolean;
  /** Marqué comme motif par défaut de sa portée ; le défaut effectif suit la règle du contrat (`effectiveDefaultReason`). */
  isDefault: boolean;
  /** Toutes les écritures qui le citent, ventes annulées comprises — `> 0` interdit la suppression. */
  usages: number;
  /** Ce que chaque geste a fait avec lui, hors ventes annulées : lignes et tickets remisés, écarts justifiés, crédits. */
  uses: { lines: number; tickets: number; cashNotes: number; stockNotes: number; credits: number };
}

/**
 * CE QUE LA REPRISE DE LA LISTE DE LA CAISSE AJOUTERAIT — les libellés de son ancien réglage `discount.reasons` absents
 * du référentiel, et ce qu'elle n'a pas su lire (dit, jamais tu).
 */
export interface RegisterReasonsPending {
  pending: string[];
  unreadable: string[];
}

/**
 * UNE UNITÉ DE MESURE DU MAGASIN — celle d'un prix à l'unité (« 9,96 €/kg »), semée par la Console.
 * ⚠️ `calculable: false` (m², m³) : l'unité s'affiche mais ne donne aucun prix à l'unité — personne n'a
 *    tranché son échelle de calcul, et le producteur refuse d'y rattacher un article.
 */
export interface MeasurementUnitUsage {
  code: string;
  label: string;
  /** Ce qui s'IMPRIME après le prix (« kg », « m² »). */
  symbol: string;
  calculable: boolean;
  isActive: boolean;
  /** Modèles EN SERVICE dont une déclinaison porte l'unité. */
  models: number;
}

/** Une liste que la colonne des Référentiels compte — une par onglet construit. */
export type ReferentialOverviewList =
  | 'suppliers' | 'sectors' | 'families' | 'subFamilies' | 'seasons' | 'sizeGrids' | 'measurementUnits' | 'customFields'
  | 'vatRates' | 'discountCeilings' | 'paymentMethods' | 'clientQualifications' | 'expenseCategories' | 'justifications' | 'vocabulary';

/**
 * CE QUE LA COLONNE DES RÉFÉRENTIELS SAIT D'UNE LISTE — son compte (les lignes de son onglet), ce qui y reste à traiter
 * (marques sans fournisseur, catégories de motifs vides), et les valeurs que la recherche de la colonne fouille.
 */
export interface ReferentialOverviewEntry {
  list: ReferentialOverviewList;
  count: number;
  toProcess: number;
  values: string[];
}

/**
 * UN PÉRIPHÉRIQUE DU PARC — TPE ou imprimante, tel que le siège le voit.
 * ⚠️ `registerId` = la caisse à laquelle il est BRANCHÉ, `null` = au parc. Il se CHOISIT dans la
 *    liste des caisses déclarées, jamais ne se saisit : un numéro tapé désigne mal (une tablette
 *    déclarée « 1 » côté Console s'annonce « 01 »).
 * ⚠️ `drawerOpenLevel` est une mesure PHYSIQUE faite sur l'appareil : le siège la LIT, il ne l'écrit
 *    pas — personne là-bas ne peut en vérifier le résultat (§19.1).
 */
export interface PeripheriqueDuParc {
  kind: string;
  name: string;
  ip: string | null;
  port: string | null;
  series: string | null;
  concertVersion: string | null;
  registerId: string | null;
  isDefault: boolean;
  drawerOpenLevel: string | null;
}

export interface ParcPeripheriques {
  rows: PeripheriqueDuParc[];
  /** Les caisses DÉCLARÉES — la seule liste où un rattachement puisse se choisir. */
  registers: string[];
}

/**
 * UNE ÉCHÉANCE DE RAPPEL, vue du siège.
 * ⚠️ `overdue` est DÉRIVÉ à la lecture, jamais stocké : une échéance devient en retard sans que
 *    personne n'écrive rien, donc un drapeau en base serait faux dès le lendemain.
 * ⚠️ `expectedMethod` est une ANNONCE du client, pas un règlement constaté — l'écran ne doit pas
 *    le rendre comme un moyen de paiement.
 */
export interface RelanceDuSiege {
  id: string;
  dueDate: number;
  amountCents: number;
  expectedMethod: string | null;
  clientId: string | null;
  clientName: string | null;
  note: string | null;
  status: string;
  overdue: boolean;
  createdAt: number;
  createdBy: string;
  doneAt: number | null;
  sourcePieceNumber: string | null;
}

export interface EcheancierDuSiege {
  rows: RelanceDuSiege[];
  /** Ce qui RESTE à encaisser — le chiffre que le siège vient chercher. */
  dueCents: number;
  /** La part de ce reste DÉJÀ en retard. */
  overdueCents: number;
  /** Combien d'échéances ont été réglées en tout — `rows` n'en porte que les plus récentes. */
  settledTotal: number;
}

export interface ReferentialSuppliers {
  rows: ReferentialSupplier[];
  /** Marques rattachées à aucun fournisseur — elles existent, l’écran ne les cache pas. */
  orphanBrands: MarqueDuReferentiel[];
  truncated: boolean;
  limit: number;
}

/** Une grille du RÉFÉRENTIEL — avec ce que le catalogue en fait. */
export interface ReferentialGrid {
  name: string;
  /** Les tailles DANS LEUR ORDRE : c’est la donnée, pas un affichage. */
  sizes: string[];
  /** Les familles des articles qui la portent — dérivées, jamais déclarées. */
  families: string[];
  /** Combien de MODÈLES la portent. 0 = elle est libre, donc supprimable. */
  models: number;
  /** La GRILLE SECONDAIRE rattachée (longueur, bonnet…), ou null — résolue par le serveur, par son nom. */
  dimension: { name: string; dimensionLabel: string } | null;
  /**
   * LES TAILLES QUE DES ARTICLES PORTENT SANS QUE LA GRILLE LES CONNAISSE — « hors champ ».
   * ⚠️ Elles sont INVISIBLES en matrice et en inventaire, aujourd’hui, sans que rien ne le dise.
   */
  orphanSizes: string[];
}

/** Une grille SECONDAIRE — longueurs de jambe, bonnets — même entité qu’une grille, un autre axe. */
export interface ReferentialDimension {
  /** Son NOM — l’identité d’une grille, quel que soit son axe : c’est lui qui voyage au rattachement. */
  name: string;
  /** Le libellé de la RUBRIQUE à l’écran (« Longueur », « Bonnet ») — c’est lui qui justifie l’axe. */
  dimensionLabel: string;
  /** Les valeurs DANS LEUR RANG — les lignes actives de la grille. */
  values: string[];
  /** Les noms des grilles principales rattachées. Vide = libre, donc supprimable. */
  links: string[];
  /** Combien de modèles portent une grille rattachée. */
  models: number;
}

export interface ReferentialGrids {
  rows: ReferentialGrid[];
  /** Les grilles secondaires, servies dans le MÊME chargement que les grilles principales. */
  dimensions: ReferentialDimension[];
  truncated: boolean;
  limit: number;
}

/**
 * UNE VALEUR D'UNE FACETTE DU RÉFÉRENTIEL (secteur, famille, sous-famille, saison) — DÉFINIE par le serveur
 * (`catalog/referential-facets.ts`).
 * ⚠️ `models` est ce que l'écran affiche (modèles EN SERVICE) ; `carriers` est la mesure de la garde de suppression
 *    (archives et ciblages de promotion compris). La corbeille ne s'offre qu'à `carriers === 0`.
 */
export interface ReferentialFacetEntry {
  code: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  models: number;
  carriers: number;
  /** SAISONS seulement : les dates posées (`AAAAMMJJ`), `null` quand la saison prend celles de son code. */
  startDay?: string | null;
  endDay?: string | null;
}

/** Une valeur portée par des articles en service, qu'aucune ligne du référentiel ne désigne. */
export interface ReferentialFacetOrphan {
  value: string;
  models: number;
}

export interface ReferentialFacetListing {
  rows: ReferentialFacetEntry[];
  orphans: ReferentialFacetOrphan[];
}

/** Une grille rattachée à une famille, avec ses bornes (« XS → XL »). */
export interface ReferentialGridRef {
  name: string;
  first: string | null;
  last: string | null;
}

export interface ReferentialFamilyEntry extends ReferentialFacetEntry {
  /** `#RRGGBB`, ou `null` tant qu'aucune couleur n'a été posée. */
  color: string | null;
  /** Pièces en stock dans ce magasin (quantités positives). */
  stock: number;
  grids: ReferentialGridRef[];
}

/** Une teinte de la palette proposée aux familles. */
export interface ReferentialPaletteEntry {
  code: string;
  label: string;
  color: string;
  families: number;
}

export interface ReferentialFamilyListing {
  rows: ReferentialFamilyEntry[];
  orphans: ReferentialFacetOrphan[];
  palette: ReferentialPaletteEntry[];
  /** Les grilles PRINCIPALES qu'on peut rattacher à une famille. */
  grids: (ReferentialGridRef & { isActive: boolean })[];
}

/** Un masque d’import mémorisé — la correspondance colonne → champ, et rien d’autre. */
export interface ImportMask {
  name: string;
  /** Libellé du fournisseur, ou chaîne vide — jamais une clé (le référentiel vit dans l’autre base). */
  supplier: string;
  mapping: Record<string, string>;
  updatedAt: string;
}

/** Une ligne du fichier, passée par le masque : chaque champ sous son nom, tel que le fichier l'écrit. */
export interface ImportArticleRow {
  /** Le numéro de ligne DANS LE FICHIER — celui que l'opérateur retrouvera dans son tableur. */
  line: number;
  gencode?: string; name?: string; code?: string; family?: string; color?: string;
  size?: string; size2?: string; price?: string; cost?: string; brand?: string; sector?: string;
  vat?: string; season?: string; web?: string; qty?: string;
}

/** Où vont les quantités du fichier — les quatre cartes de l'étape « Destination ». */
export type ImportDestinationKind = 'bl' | 'cmd' | 'stock' | 'ref';

export interface ImportDestination {
  kind: ImportDestinationKind;
  /** Bon ou commande : un document NEUF, ou un document existant que les quantités complètent. */
  mode?: 'new' | 'existing';
  /** Le numéro saisi ou proposé (vide = attribué) ; celui du document choisi pour un existant. */
  number?: string | null;
}

/** La valeur de correspondance qui CRÉE la famille telle que le fichier l'écrit — la même que le serveur. */
export const CREATE_FAMILY = '+';

export interface ImportArticlesOptions {
  /** La grille de tailles du FICHIER, par son nom — pour ce que ni l'article ni ses sœurs ne placent. */
  sizeGrid?: string | null;
  supplier?: string | null;
  /** Un article DÉJÀ en base : `update` le complète (« Écraser »), `skip` le laisse tel quel (« Ignorer »). */
  duplicates?: 'update' | 'skip';
  /** Libellé de famille du fichier → code d'une famille du magasin ; `''` = ne pas classer ; `CREATE_FAMILY` = la créer. */
  families?: Record<string, string>;
  /** Mémoriser pour ce fournisseur les correspondances choisies à la main. */
  learnFamilies?: boolean;
  destination?: ImportDestination;
}

/** Les familles que l'étape « Doublons » propose, et ce que le fournisseur a déjà appris. */
export interface ImportFamilies {
  families: { id: string; code: string; label: string }[];
  learned: { alias: string; familyCode: string }[];
}

/** Un document ouvert du fournisseur — ce que « Rattacher à un document existant » propose. */
export interface ImportOpenDocument {
  number: string;
  supplierLabel: string | null;
  /** Pièces attendues (bon) ou encore attendues (commande). */
  pieces: number;
  /** Le nombre de lignes du document. */
  lines: number;
  /** Le jour du document : la création d'un bon, la date de commande d'une commande (sa création à défaut). */
  date: number;
}

/** Ce que l'import a RÉELLEMENT fait — le rapport de l'étape 6. */
export interface ImportArticlesReport {
  received: number;
  written: number;
  created: number;
  updated: number;
  skippedExisting: number;
  refused: { line: number; gencode: string | null; reason: string }[];
  sizesAdded: { grid: string; values: string[] }[];
  secondAxisCreated: string[];
  /** Lignes qui portaient une quantité que la destination choisie n'emploie pas (« Articles seulement »). */
  quantitiesIgnored: number;
  /** Modèles archivés que le fichier a remis en service. */
  restoredModels: number;
  /** Lignes sans code-barres rapprochées d'un article existant (code article + couleur + taille). */
  matchedWithoutBarcode: number;
  /** Les gencodes INTERNES attribués, ligne par ligne — ceux qu'il faudra étiqueter. */
  generatedGencodes: { line: number; gencode: string; name: string }[];
  /** Articles créés avec un code article proposé d'après leur désignation. */
  generatedCodes: number;
  /** Modèles créés (codes article neufs) — `created` compte les déclinaisons. */
  createdModels: number;
  /** Correspondances de famille mémorisées pour le fournisseur. */
  familiesLearned: number;
  /** Ce que les quantités sont devenues — `null` quand rien n'est parti. */
  destination: { kind: ImportDestinationKind; number: string | null; lines: number; pieces: number } | null;
  /** Les modèles du LOT — ceux dont l'import a écrit une déclinaison, que « Voir les articles » montre. */
  batchModels: number;
  /** L'envoi avait déjà été traité sous cette clé : le compte rendu est celui de la première fois. */
  replayed: boolean;
}

export interface SizeGridOption {
  name: string;
  sizes: string[];
  /** Combien de modèles la portent — 0 est une valeur normale pour une grille neuve. */
  models: number;
}

/**
 * UNE GRILLE SECONDAIRE OFFERTE — ce qu'un ARTICLE peut prendre comme second axe.
 * ⚠️ Servie AVEC les grilles, et INDÉPENDANTE d'elles : les deux listes se choisissent séparément,
 * parce que c'est l'article qui apparie. Un même jeu de longueurs sert des grilles principales
 * différentes, sans qu'aucune n'ait à le déclarer.
 */
export interface SecondAxisOption {
  name: string;
  /** La RUBRIQUE affichée en tête de matrice (« Longueur », « Bonnet ») — retombe sur le nom. */
  label: string;
  values: string[];
  /** Combien de grilles principales la rattachent déjà — 0 = libre. */
  links: number;
}

export interface ArticleFacets {
  values: Record<ArticleFacet, FacetValue[]>;
  truncated: Record<ArticleFacet, boolean>;
  limit: number;
}

/**
 * Ce qu'un écran mémorise pour son utilisateur.
 * ⚠️ DEUX PORTÉES : `layout` et `pageSize` suivent la PERSONNE (ils la suivent d'un magasin à
 * l'autre) ; `filters` suit le MAGASIN où ils ont été posés — une valeur de référentiel n'a aucun
 * sens ailleurs. Le serveur applique cette séparation ; l'écran n'a rien à décider.
 */
export interface TablePrefs {
  layout?: { order: string[]; hidden: string[] };
  pageSize?: number;
  filters?: Record<string, string>;
}

/**
 * LA MÉTÉO DU MAGASIN, ou le motif précis de son absence.
 * ⚠️ Les quatre motifs ne se corrigent pas au même endroit — l'écran doit donc les distinguer, pas
 * les fondre en « indisponible » : clé au serveur, code postal dans la fiche du magasin, pays hors
 * couverture, panne du fournisseur.
 */
export type WeatherView =
  | {
      ok: true; city: string; tempC: number; feelsC: number; description: string;
      kind:
        | 'clear' | 'fewClouds' | 'clouds'
        | 'drizzle' | 'rain' | 'freezingRain'
        | 'sleet' | 'snow'
        | 'storm' | 'fog' | 'haze' | 'wind' | 'tornado';
      /** ⚠️ Imposée par la licence du plan gratuit : la carte la REND, elle ne l'ignore pas. */
      attribution: string;
    }
  | { ok: false; reason: 'not_configured' | 'no_location' | 'unsupported_country' | 'provider_failed'; detail?: string };

/** La famille d'un poste du plan comptable — elle décide de sa place à l'écran et du tamis du numéro. */
export type PosteKind = 'journal' | 'vat_sales' | 'vat_collected' | 'payment' | 'variance';

/**
 * L'état d'un poste. ⚠️ `suggere` EST LE CAS QUI COMPTE : le champ est rempli, mais par le plan
 * comptable général, pas par le cabinet. Il a l'apparence d'une décision sans en être une.
 */
export type EtatPoste = 'confirme' | 'suggere' | 'vide';

/** Un poste du plan comptable, composé : ce qu'il désigne, ce qui est saisi, et d'où vient la valeur. */
export interface PostePlanComptable {
  key: string;
  kind: PosteKind;
  /** Points de base pour la TVA, code de règlement pour un encaissement. */
  discriminant: string;
  label: string;
  sens: 'debit' | 'credit' | 'both';
  suggestion: string;
  /** Le poste vient-il des VENTES du magasin (par opposition à son référentiel) ? */
  observe: boolean;
  account: string;
  confirmedAt: number | null;
  etat: EtatPoste;
}

/** Le plan comptable du magasin, et ce qui l'empêche d'ouvrir l'export. */
export interface PlanComptableView {
  postes: PostePlanComptable[];
  etat: { complet: boolean; vides: string[]; aConfirmer: string[] };
  /** ⚠️ Sur un magasin sans vente, les postes viennent TOUS du référentiel : l'écran doit le dire. */
  aDesVentes: boolean;
}

// ── ÉCRITURE COMPTABLE ──────────────────────────────────────────────────────────

/** Une ligne d'écriture — un compte, un libellé, un montant d'un seul côté. */
export interface EntryLine {
  account: string;
  label: string;
  debitCents: number;
  creditCents: number;
  /** La clé de poste dont vient la ligne — elle répond à « pourquoi ce compte porte-t-il ce montant ». */
  origin: string;
}

/**
 * L'état de la période au moment de l'extraction.
 * ⚠️ Exporter un mois OUVERT est autorisé ; le danger est qu'un cabinet intègre un mois incomplet
 * SANS LE SAVOIR. Cette provenance est la contrepartie de cette liberté — elle se rend, toujours.
 */
export interface PeriodProvenance {
  month: string;
  closed: boolean;
  /** Caisses sans clôture — NOMMÉES : « 2 caisses ouvertes » ne dit pas laquelle relancer. */
  openRegisters: string[];
  extractedAt: number;
}

/** Ce qui empêche de tenir l'écriture pour exploitable. */
export interface EntryProblem {
  kind: 'missing_account' | 'unbalanced';
  key: string | null;
  message: string;
}

/** L'écriture d'UNE journée fiscale. */
export interface DayEntry {
  /** ⚠️ `provenance.month` porte ici LA JOURNÉE (`AAAAMMJJ`) : c'est elle qui date l'écriture. */
  provenance: PeriodProvenance;
  journal: string;
  lines: EntryLine[];
  totalDebitCents: number;
  totalCreditCents: number;
  balanced: boolean;
  problems: EntryProblem[];
}

/**
 * LES ÉCRITURES D'UNE PÉRIODE — UNE PAR JOURNÉE FISCALE.
 *
 * ⚠️ AU JOUR, JAMAIS AU MOIS (décision utilisateur 2026-08-23). Un mois n'est qu'un CUMUL de
 * journées : agréger d'abord fait perdre la seule granularité qui existe en caisse — celle que le Z
 * scelle, celle qu'un comptable rapproche d'un relevé. Les cumuls d'ici sont DÉRIVÉS des journées.
 */
export interface EntryView {
  month: string;
  days: DayEntry[];
  planComplet: boolean;
  /** Bascule appliquée — l'écran la DIT, sinon un décalage de journée passe pour un bug. */
  dayCutoffMinutes: number;
  totalDebitCents: number;
  totalCreditCents: number;
  /** `true` si CHAQUE journée s'équilibre — une seule qui cloche suffit à fermer l'export. */
  balanced: boolean;
}

// ── FORMATS D'EXPORT ────────────────────────────────────────────────────────────────────────────

/** Ce qu'une colonne du fichier rend. */
export type ChampExport = 'journal' | 'date' | 'compte' | 'libelle' | 'debit' | 'credit' | 'montant' | 'sens' | 'devise';

/** Comment le sens de la ligne s'écrit — les trois conventions du marché. */
export type ConventionSens = 'colonnes' | 'signe' | 'lettre';

export interface ColonneSpec { champ: ChampExport; heading: string; largeur?: number }

/** Tout ce qui fait un format, et rien qui ne soit un choix du cabinet. */
export interface FormatSpec {
  nom: string;
  separateur: ';' | ',' | '\t' | '|' | 'fixed';
  encodage: 'utf-8' | 'utf-8-bom' | 'windows-1252';
  extension: string;
  enTete: boolean;
  masqueDate: string;
  decimale: ',' | '.';
  sens: ConventionSens;
  colonnes: ColonneSpec[];
  finDeLigne: '\r\n' | '\n';
  devise: string;
}

/** Un format offert à l'écran. */
export interface FormatDisponible {
  code: string;
  label: string;
  spec: FormatSpec;
  /** `true` = livré avec le produit, non modifiable (il vit dans le code). */
  livre: boolean;
  /** Magasin dont il a été repris — ⚠️ une COPIE datée, jamais un héritage vivant. */
  sourceGln: string | null;
  sourceAt: number | null;
  /** Motif quand la forme stockée n'est pas exploitable — l'écran le DIT plutôt que de la masquer. */
  illisible: string | null;
}

/** Un export déjà produit — c'est lui qui permet de prévenir avant un rejeu. */
export interface ExportPasse {
  period: string;
  formatCode: string;
  formatLabel: string;
  /** Empreinte du contenu comptable : deux exports identiques la partagent. */
  fingerprint: string;
  lineCount: number;
  periodClosed: boolean;
  exportedAt: number;
  exportedBy: string;
}

/**
 * UNE PROMOTION telle que l'écran la reçoit — la règle, et son PÉRIMÈTRE MESURÉ.
 * ⚠️ `articleCount` vient de la base, il ne s'estime pas au navigateur : c'est lui qui fait choisir une
 * cible, et le back-office ne tient pas le catalogue en mémoire (leçon du comptage des articles rapides).
 */
// ⚠️ LE MODÈLE D'ÉTIQUETTE VIENT DU CŒUR, jamais redéclaré ici : l'éditeur, le générateur
// d'impression et la validation serveur doivent s'accorder au dixième de millimètre (§24.6).
import type { LabelTemplate } from '@orgazone/core-catalog';
export type { LabelTemplate };

/** Une ligne imprimable — ⚠️ le prix arrive en CENTIMES : c'est l'écran qui connaît la devise. */
export interface LabelSampleRow {
  gencode: string; barcode: string; desig: string; code: string; priceCents: number;
  size: string; color: string; brand: string; sup: string; origin: string; season: string;
}

export interface PromotionRow {
  id: string;
  title: string;
  /** `pct` · `amount` · `xy` — le vocabulaire de `core-pricing.PromoForm`. */
  form: 'pct' | 'amount' | 'xy';
  /** Points de base pour `pct`/`xy`, centimes pour `amount`. L'unité suit la forme. */
  value: number;
  buyQty: number;
  discountQty: number;
  startsAt: number | null;
  endsAt: number | null;
  /** `best` · `stack` · `none` ; `null` = on suit la politique du magasin. */
  combineMode: string | null;
  priority: number;
  isActive: boolean;
  targetDim: string | null;
  targetValue: string | null;
  articleCount: number;
  /** L'écran n'affiche qu'une cible ; s'il en cache, il doit le dire. */
  extraTargets: number;
  /** Lignes d'EXCLUSION portées par la promotion (soldes) — déjà déduites d'articleCount. */
  exclusionCount: number;
}

/** Ce que l'écran envoie. Sans `id` = création. */
export interface PromotionSave {
  id?: string;
  title: string;
  form: string;
  value: number;
  buyQty?: number;
  discountQty?: number;
  startsAt: number | null;
  endsAt: number | null;
  combineMode: string;
  targetDim: string;
  targetValue: string;
}

/**
 * LES TRIS QUE LE SERVEUR ACCEPTE — la liste FERMÉE de `ARTICLE_SORT_COLUMNS`.
 *
 * ⚠️ ELLE EXISTE PARCE QU'UN TRI INVENTÉ EST PASSÉ (2026-08-29). L'écran « Prix & marges » demandait
 * un tri « n » — lu sur la maquette, jamais confronté au serveur. `sort: string` laissait passer
 * n'importe quelle chaîne : rien ne pouvait le voir avant que l'écran, en production, n'affiche
 * « Tri "n" inconnu de cette liste ». Le serveur a bien refusé et bien nommé son refus ; ce qui
 * manquait était de le savoir AVANT de livrer.
 * ⚠️ SI UN TRI EST AJOUTÉ AU SERVEUR, il s'ajoute ici — sinon l'écran ne pourra pas le demander, ce
 * qui est le bon sens du couplage : mieux vaut un tri indisponible qu'un tri refusé à l'exécution.
 */
export const ARTICLE_SORT_KEYS = [
  'designation', 'ean', 'fam', 'sect', 'sup', 'sais', 'marq',
  'pa', 'pvht', 'tva', 'pv', 'marge', 'stock', 'modif',
] as const;
export type ArticleSortKey = (typeof ARTICLE_SORT_KEYS)[number];

/**
 * Ce tri est-il de ceux que le serveur accepte ?
 *
 * ⚠️ POUR LE POINT DE PASSAGE OÙ LE TYPE NE SUFFIT PAS : la table partagée rend un `SortState` dont la
 * clé est un `string` (elle sert aussi au journal, qui trie sur autre chose). Plutôt qu'un cast — qui
 * rendrait au compilateur une certitude qu'il n'a pas — l'écran RETOMBE sur son tri par défaut si la
 * clé n'est pas des siennes. Une liste triée autrement qu'on ne l'a demandé se voit ; une liste qui
 * refuse de se charger arrête le travail.
 */
export const isArticleSortKey = (v: string): v is ArticleSortKey =>
  (ARTICLE_SORT_KEYS as readonly string[]).includes(v);


/** Une ligne d'aperçu de la modification de masse — un MODÈLE, ses bornes avant/après. */
export interface MassPreviewRow {
  modelKey: string; name: string; code: string; variants: number;
  curMinCents: number; curMaxCents: number; nextMinCents: number; nextMaxCents: number;
  /** Pour dériver la marge à l'écran (prix-derive) — null si TVA mêlée sur le modèle. */
  vatRateBp: number | null; costMinFine: number | null; corner: boolean;
}
export interface MassPreview {
  rows: MassPreviewRow[];
  more: number;
  totals: { models: number; variants: number; changed: number; avgDeltaBp: number | null };
}
export interface MassApplyReport {
  written: number; models: number; variants: number; unchanged: number; drifted: number; excluded: number;
}
export type MassRounding = 'non' | '99' | '95' | '90' | 'ent' | 'dem';
export interface MassChange { mode: 'pct' | 'eur'; value: number }


// ── Soldes — dates légales calculées serveur, démarques matérialisées en promotions ──
export interface FenetreSoldes {
  saison: 'hiver' | 'ete'; year: number; label: string; rule: string; startMs: number; endMs: number;
  /** true = fenêtre issue d'un ARRÊTÉ saisi (remplace la règle nationale, pour SON année). */
  arretee?: boolean;
}
/** L'arrêté d'une saison — dates CIVILES (AAAA-MM-JJ), fin incluse, pour UNE année. */
export interface DatesArretees { year: number; start: string; end: string }
/** Une exclusion de campagne : UNE dimension, UNE valeur — « toute la boutique SAUF … ». */
export interface ExclusionSoldes { dim: 'sais' | 'fam' | 'sfam' | 'marq' | 'sect' | 'code'; value: string }
export interface CampagneSoldes {
  saison: 'hiver' | 'ete'; year: number; active: boolean;
  marks: { pct: number; fromMs: number; toMs: number }[];
  /** Les exclusions ÉCRITES avec la campagne (relues des lignes `is_exclusion`). */
  exclusions: ExclusionSoldes[];
}
/**
 * Un article exclu des soldes, avec son MOTIF. ⚠️ CONTRAT BO serveur ↔ BO web (même déploiement) :
 * `why` est le FAIT (« Exclu de campagne », « Non remisable », « Prix libre »), jamais la phrase
 * complète — le serveur ne nomme pas la dimension, c'est l'écran qui le fait avec le mot du MAGASIN
 * (`exclusionReason`), par le même résolveur que le panneau d'exclusions. `dim` (la clé de dimension)
 * n'accompagne qu'une exclusion de CAMPAGNE ; `kind` sépare les TROIS familles de motifs que l'écran
 * distingue à l'œil (couleurs côté web).
 */
export interface SoldesExcludedRow {
  name: string; code: string; why: string; kind: 'campagne' | 'fiche' | 'prix_libre'; dim?: ExclusionSoldes['dim'];
}
export interface ComptesSoldables {
  eligibles: number; exclusNonRemisables: number; exclusPrixLibre: number;
  /** Modèles écartés par les EXCLUSIONS de la campagne (déjà déduits d'`eligibles`). */
  exclusCampagne: number;
  exclusRows: SoldesExcludedRow[];
}
export interface ApercuSolde { name: string; code: string; refCents: number; refDifferente: boolean; markedCents: number[] }
export interface SoldesState {
  saisons: FenetreSoldes[]; campagne: CampagneSoldes | null; comptes: ComptesSoldables; apercu: ApercuSolde[];
  /** Les fenêtres de chaque démarque — calculées SERVEUR (la même fonction que l’écriture). */
  repartition: { pct: number; fromMs: number; toMs: number }[];
  /** Les arrêtés saisis, par saison — null = la règle nationale s'applique. */
  arretes: { hiver: DatesArretees | null; ete: DatesArretees | null };
  /** true = la campagne ACTIVE porte d'anciennes dates (arrêté posé après) — « Activer » la met à jour. */
  desync: boolean;
  /** Les exclusions RÉSOLUES par le serveur (frappe `excl` de l'écran, sinon celles de la campagne). */
  exclusions: ExclusionSoldes[];
}

export interface ArticleQueryParams {

  page: number;
  size: number;
  sort: ArticleSortKey;
  dir: 'asc' | 'desc';
  q: string;
  /** Valeur EXACTE par dimension (jamais un motif) — absente = dimension non filtrée. */
  fam?: string;
  sup?: string;
  sais?: string;
  sect?: string;
  marq?: string;
  /** `all` (défaut) · `active` · `inactive` — jugé sur le MODÈLE, pas sur la déclinaison. */
  actif?: 'all' | 'active' | 'inactive';
  /** Ne garder que les modèles dont AU MOINS une déclinaison reste en stock. */
  inStock?: boolean;
  /** Ne garder que les modèles dont la grille porte un second axe (« Tailles 2 uniquement »). */
  secondAxis?: boolean;
  /** Ne garder que le LOT d'un import, désigné par sa clé (« Voir les articles »). */
  importBatch?: string;
}

/** Une intervention consignée au journal d'accès du magasin — ce que le CLIENT lit de nous. */
export interface AccessEvent {
  id: string;
  at: string;
  gln: string;
  storeName: string;
  text: string;
  actor: string | null;
  ip: string | null;
}

/** Ce que le serveur dit du parcours de réinitialisation, AVANT toute connexion. */
export interface RecoveryStatus {
  available: boolean;
  /** Motif réel de l'indisponibilité — affiché tel quel ; `null` quand le parcours est praticable. */
  reason: string | null;
}

// ⚠️ Le type du mode d'auth n'est PAS redéclaré : c'est le contrat de plateforme, servi par le package
// qui sert aussi la route. Le redéclarer autoriserait une dérive silencieuse.
export type { AuthMode };

// ── Tableau de bord ──
/** Les mesures d'une période — le cœur `core-analytics` les définit, le serveur les assemble. */
export interface PeriodTotals {
  caTtcCents: number;
  caHtCents: number;
  marginHtCents: number | null;
  /**
   * Pourquoi la marge est incalculable — `null` quand elle l'est : les lignes sans coût connu, comptées par cause
   * (prix d'achat manquant, code inconnu du catalogue). L'encart commun les dit et ouvre la liste des articles.
   */
  marginGap: SalesMarginGap | null;
  nbSales: number;
  panierMoyenCents: number;
  /** Indice de vente ×10 (4,2 articles par vente → 42) — même échelle qu'en caisse. */
  idvX10: number;
  tauxClientsIdentifiesBp: number;
  /** Ventes rattachées à une fiche — le NUMÉRATEUR du taux, servi tel quel (jamais reconstitué). */
  nbSalesIdentifiees: number;
}


/** Les totaux d'une fenêtre du Résumé (centimes ; taux en points de base). `null` = non mesurable — l'écran dit « — ». */
export interface SalesSummaryTotals {
  netTtcCents: number;
  grossTtcCents: number;
  netHtCents: number;
  marginHtCents: number | null;
  purchaseHtCents: number | null;
  tickets: number;
  qtySold: number;
  passages: number | null;
  basketCents: number | null;
  /** Articles par ticket × 100. */
  idvX100: number | null;
  marginRateBp: number | null;
  discountRateBp: number | null;
  conversionBp: number | null;
}

export type SalesSummaryAxis = 'fam' | 'sup' | 'marq' | 'sect';
export type SalesSummaryFilterKey = 'sup' | 'marq' | 'sect' | 'fam' | 'sfam' | 'sais' | 'art';

/**
 * STATISTIQUES › VENTES › RÉSUMÉ — tout ce que l'onglet affiche, DÉFINI par le serveur (`sales/stats-summary.ts`).
 * ⚠️ L'écran dessine ces nombres, il n'en recalcule aucun : un taux recalculé à l'écran serait une seconde définition.
 */
/**
 * Une saison offerte comme période dans la barre des statistiques (`sales/season-periods.ts`) : ses jours `AAAAMMJJ`,
 * bornes incluses — posés au référentiel, sinon portés par le code — et les codes de même dates qu'elle réunit.
 */
export interface SeasonPeriod {
  from: string;
  to: string;
  codes: string[];
}

export interface SalesSummaryView {
  period: { from: string; to: string };
  /** Aujourd'hui quand la période, qui finissait dans le futur, a été arrêtée « à date » ; `null` sinon. */
  asOf: string | null;
  reference: { from: string; to: string; kind: 'n1' | 'custom' };
  grain: 'day' | 'week' | 'month';
  totals: SalesSummaryTotals;
  refTotals: SalesSummaryTotals;
  growth: Record<'netTtc' | 'margin' | 'basket' | 'idv' | 'conversion' | 'discount', number | null>;
  buckets: { key: string; a: SalesSummaryTotals; r: SalesSummaryTotals | null }[];
  axis: { key: SalesSummaryAxis; rows: { label: string; netTtcCents: number; shareBp: number | null; marginRateBp: number | null; qtySold: number; growthBp: number | null }[] };
  label100: { discount: number; vat: number; purchase: number; margin: number } | null;
  /** Quand la marge ne se mesure pas : remises, TVA, et le reste HT non partagé entre achats et marge. */
  label100Partial: { discount: number; vat: number; rest: number } | null;
  /** Ce qui empêche la marge quand elle n'est pas mesurable : les lignes sans coût connu, comptées par cause. */
  marginGap: SalesMarginGap | null;
  heatmap: {
    weekdays: number[];
    firstHour: number;
    lastHour: number;
    cells: Record<number, number[]>;
    dayTotals: Record<number, number>;
    hours: { open: string; close: string } | null;
  };
  pareto: {
    references: number;
    count80: number;
    curve: { rankBp: number; shareBp: number }[];
    top: { code: string; label: string; netTtcCents: number; shareBp: number }[];
  };
  signals: {
    discountRateBp: number | null;
    worstMargin: { label: string; marginRateBp: number } | null;
    bestGrowth: { label: string; growthBp: number } | null;
  };
  scope: { references: number; rows: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
  /** L'objectif de la période choisie — servi par le serveur (`sales/objectives.ts`), jamais recalculé ici. */
  objective: SummaryObjectiveView;
}

/**
 * L'OBJECTIF DE LA PÉRIODE DU RÉSUMÉ.
 * `filtered` : une recherche ou un filtre est posé, l'objectif du magasin ne se compare pas à une sélection ·
 * `none` : aucun objectif sur la période · `partial` : des mois de la période n'en ont pas, ne se répartissent pas, ou ont
 * des jours fixés qui dépassent leur objectif.
 */
export type SummaryObjectiveView =
  | { state: 'filtered' }
  | { state: 'unreadable'; reason: string }
  | { state: 'none' }
  | { state: 'partial'; missing: string[]; undistributed: string[]; overcommitted: string[] }
  | {
    state: 'set';
    objectiveCents: number;
    objectiveToDateCents: number;
    realizedHtCents: number;
    attainmentBp: number;
    daysWithObjective: number;
    daysElapsed: number;
    daysLeft: number;
    pacePerDayCents: number | null;
    needPerDayCents: number | null;
    objectivePerDayCents: number;
    projectionBp: number | null;
    closed: boolean;
  };

/** Ce que chaque geste sur les objectifs inscrit à l'historique. */
export type ObjectiveChangeKind = 'month' | 'prefill' | 'copy' | 'weights' | 'weights_reset' | 'day_added' | 'day_removed'
  | 'day_fixed' | 'day_unfixed' | 'month_from_days' | 'days_unfixed';

/** Une ligne de l'historique des objectifs — des faits, que l'écran compose en phrase. */
export interface ObjectiveChangeView {
  at: number;
  actor: string;
  kind: ObjectiveChangeKind;
  subject: string | null;
  beforeCents: number | null;
  afterCents: number | null;
  changeBp: number | null;
  dayKind: string | null;
  count: number | null;
}

/** Un jour exceptionnel : une fermeture, ou un événement et sa variation. */
export interface ObjectiveExceptionView { day: string; kind: 'closed' | 'event'; changeBp: number | null }

/** Un jour de la grille d'un mois. */
export interface ObjectiveDayView {
  day: string;
  /** Le montant fixé, ou la part du reste du mois ; `null` quand le jour n'en porte pas. */
  objectiveCents: number | null;
  fixed: boolean;
  closed: boolean;
  changeBp: number | null;
}

/** Un mois de l'exercice. */
export interface ObjectiveMonthView {
  month: string;
  state: 'past' | 'current' | 'future';
  /** L'objectif qui s'applique : celui saisi, ou la somme des jours quand ils sont tous fixés (`fromDays`). */
  objectiveCents: number | null;
  realizedHtCents: number | null;
  rateBp: number | null;
  fromDays: boolean;
  fixedDays: number;
  fixedCents: number;
  /** Ce qui reste du mois pour les jours non fixés — `null` sans objectif mensuel, ou quand les jours fixés le dépassent. */
  freeCents: number | null;
  freeDays: number;
  overCents: number | null;
  days: ObjectiveDayView[];
}

/** L'exercice d'un magasin (Ventes & fiscal › Objectifs). */
export interface ObjectiveYearView {
  year: number;
  today: string;
  months: ObjectiveMonthView[];
  /** Les seuls mois ÉCOULÉS ET BUDGÉTÉS. */
  total: { months: number; objectiveCents: number; realizedHtCents: number; rateBp: number | null };
  /** `manual` : saisis · `profile` : le profil des 52 dernières semaines · `flat` : aucune vente à lire, 100 partout. */
  weights: { source: 'manual' | 'profile' | 'flat'; values: number[] };
  openDays: number[];
  /** L'objectif d'un jour ordinaire de chaque jour de semaine du mois en cours, lundi d'abord. */
  example: { month: string; byWeekday: (number | null)[] } | null;
  exceptions: ObjectiveExceptionView[];
  undistributed: string[];
  overcommitted: string[];
  history: ObjectiveChangeView[];
}

/** Ce qu'un geste sur les objectifs a changé — `kept` : les mois laissés tels quels parce que leurs jours fixés décident. */
export interface ObjectiveWriteReport { changed: boolean; written?: number; skipped?: number; kept?: number }

/** Un jour du suivi (Statistiques › Ventes › Objectifs). */
export interface ObjectiveTrackingRow {
  day: string;
  n1Day: string;
  closed: boolean;
  exception: { kind: 'closed' | 'event' | 'fixed'; changeBp: number | null; amountCents: number | null } | null;
  future: boolean;
  objectiveCents: number | null;
  a: SalesSummaryTotals | null;
  n1: SalesSummaryTotals | null;
  attainmentBp: number | null;
  growthBp: number | null;
  cumObjectiveCents: number | null;
  cumRealizedHtCents: number | null;
  cumGapCents: number | null;
}

/** Le suivi jour après jour d'une fenêtre — ramenée à deux mois au plus (`capped`). */
export interface ObjectiveTrackingView {
  period: { from: string; to: string };
  capped: boolean;
  today: string;
  weightsSource: 'manual' | 'profile' | 'flat';
  rows: ObjectiveTrackingRow[];
  totals: {
    objectiveCents: number;
    objectiveToDateCents: number;
    a: SalesSummaryTotals | null;
    n1: SalesSummaryTotals | null;
    attainmentBp: number | null;
    gapCents: number | null;
    growthBp: number | null;
    daysWithObjective: number;
    daysLeft: number;
    needPerDayCents: number | null;
    projectionCents: number | null;
    projectionBp: number | null;
  };
  missing: string[];
  undistributed: string[];
  overcommitted: string[];
}

/** Ce que l'onglet Résumé demande : période (`AAAAMMJJ`), comparaison facultative, axe, recherche et filtres. */
export interface SalesSummaryParams {
  from: string;
  to: string;
  cmpFrom?: string;
  cmpTo?: string;
  axis: SalesSummaryAxis;
  q: string;
  filters: Partial<Record<SalesSummaryFilterKey, string[]>>;
}

/** Ce que le Résumé du stock demande : la période, la comparaison facultative, la recherche et les filtres des Ventes. */
export type StockSummaryParams = Omit<SalesSummaryParams, 'axis'>;

/** Les totaux de remise d'une fenêtre (`sales/stats-discounts.ts`) — centimes, taux en points de base, `null` = non mesurable. */
export interface SalesDiscountTotals {
  /** CA brut − CA net : le même nombre que la part « remises » du Résumé. */
  totalCents: number;
  grossTtcCents: number;
  rateBp: number | null;
  tickets: number;
  discountedTickets: number;
  discountedShareBp: number | null;
  averageCents: number | null;
  promoCents: number;
  clientCents: number;
  loyaltyCents: number;
  withReasonCents: number;
  withoutReasonCents: number;
  undetailedCents: number;
  promoShareBp: number | null;
  withoutReasonShareBp: number | null;
}

/** Un vendeur du Résumé des vendeurs (`sales/stats-sellers.ts`) — par identifiant d'opérateur, sous son nom actuel. */
export interface SalesSellerRow {
  id: string;
  name: string;
  totals: SalesSummaryTotals;
  refNetTtcCents: number;
  growthBp: number | null;
  shareBp: number | null;
  /** Son CA net TTC par jour de semaine (1 = lundi … 7 = dimanche) et sa part du CA de ce jour. */
  weekdays: { weekday: number; netTtcCents: number; shareBp: number | null }[];
  /** Son CA net TTC par seau, dans l'ordre des seaux de la vue. */
  buckets: number[];
}

/**
 * STATISTIQUES › VENDEURS › RÉSUMÉ — qui fait le chiffre, comment chacun vend, et quels jours, DÉFINI par le serveur.
 * ⚠️ L'écran dessine ces nombres, il n'en recalcule aucun.
 */
export interface SalesSellersView {
  period: { from: string; to: string };
  asOf: string | null;
  reference: { from: string; to: string; kind: 'n1' | 'custom' };
  grain: 'day' | 'week' | 'month';
  totals: SalesSummaryTotals;
  refTotals: SalesSummaryTotals;
  growth: { netTtc: number | null; tickets: number | null; basket: number | null; idv: number | null; discount: number | null };
  activeSellers: number;
  basketSpreadCents: number | null;
  sellers: SalesSellerRow[];
  bestGrowthId: string | null;
  weekdays: number[];
  dayLeader: { sellerId: string; weekday: number; shareBp: number } | null;
  /** Les seaux de la période, avec les totaux du magasin. */
  buckets: { key: string; totals: SalesSummaryTotals }[];
  scope: { references: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/** Les parts d'une remise qui ne sont pas des motifs. */
export type SalesDiscountPart = 'client' | 'loyalty' | 'promo' | 'withoutReason' | 'undetailed';

/**
 * STATISTIQUES › VENTES › REMISES — le total des remises du Résumé, réparti selon les parts scellées, DÉFINI par le serveur.
 * ⚠️ L'écran dessine ces nombres, il n'en recalcule aucun.
 */
export interface SalesDiscountsView {
  period: { from: string; to: string };
  asOf: string | null;
  reference: { from: string; to: string; kind: 'n1' | 'custom' };
  grain: 'day' | 'week' | 'month';
  totals: SalesDiscountTotals;
  refTotals: SalesDiscountTotals;
  growth: { total: number | null; rate: number | null; discountedTickets: number | null; average: number | null; promo: number | null; withoutReason: number | null };
  /** Un motif par ligne : `label` `null` pour un code que le référentiel ne connaît plus. */
  reasons: { code: string; label: string | null; cents: number; refCents: number; growthBp: number | null; shareBp: number | null; lines: number; tickets: number; freePriceLines: number }[];
  others: Record<SalesDiscountPart, { cents: number; refCents: number; shareBp: number | null; growthBp: number | null }>;
  sellers: {
    columns: { key: string; kind: 'reason' | 'others' | 'none'; code: string | null; label: string | null }[];
    rows: { id: string; name: string; cells: number[]; gesturesCents: number; grossTtcCents: number; rateBp: number | null; gapBp: number | null; flagged: boolean }[];
    storeRateBp: number | null;
  };
  buckets: { key: string; totals: SalesDiscountTotals; byReason: Record<string, number> }[];
  scope: { references: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/**
 * Les indicateurs d'une fenêtre du Résumé du stock (`sales/stats-stock.ts`) : le stock à la FIN de la fenêtre, ce qui s'est
 * vendu, et ce qui en découle. Centimes, points de base ; `null` = non mesurable — l'écran dit « — » et pourquoi.
 */
export interface StockSummaryTotals {
  pieces: number;
  valueCents: number | null;
  /** Déclinaisons en stock sans PMP défendable : ce qui rend la valeur `null`. */
  unvaluedRefs: number;
  /** Modèles en stock. */
  references: number;
  soldQty: number;
  days: number;
  /** Jours de vente en stock, arrondis au jour. */
  coverageDays: number | null;
  sellThroughBp: number | null;
  /** Renouvellements par an × 10. */
  rotationX10: number | null;
  slow: { valueCents: number | null; references: number; shareBp: number | null };
  opening: { pieces: number; valueCents: number | null };
  flows: { entriesQty: number; exitsQty: number; entriesValueCents: number | null; exitsValueCents: number | null };
}

/** Un seau de la courbe des mouvements : le niveau à sa fin, ses entrées et ses sorties, le taux de sortie cumulé. */
export interface StockSummaryBucket {
  pieces: number;
  valueCents: number | null;
  entriesQty: number;
  exitsQty: number;
  entriesValueCents: number | null;
  exitsValueCents: number | null;
  soldQty: number;
  cumSellThroughBp: number | null;
}

/** Un regroupement de « Combien de jours de vente en stock » (`sales/stats-stock.ts`). */
/** L'âge d'un stock, lu sur la saison d'origine de l'article. */
export type StockAge = 'next' | 'current' | 'previous' | 'old' | 'permanent' | 'undated';

/** Un âge de « L'âge du stock ». */
export interface StockAgeBucket {
  age: StockAge;
  pieces: number;
  valueCents: number | null;
  /** Part du stock, en valeur — ou en pièces quand la valeur ne se mesure pas (`age.basis`). */
  shareBp: number;
  /** Les codes de saison de l'âge, du plus récent au plus ancien ; `UNSET_VALUE` pour les fiches sans saison. */
  codes: string[];
}

/** L'écoulement de 12 semaines d'un ensemble : ses semaines, son stock, sa vitesse (× 10) et sa couverture en semaines. */
export interface StockPace {
  weeks: number[];
  pieces: number;
  velocityX10: number;
  coverageWeeks: number | null;
}

/** Une saison de « Ce qui s'écoule » : les codes qui partagent les mêmes dates, le permanent, ou le non daté. */
export interface StockSellThroughRow extends StockPace {
  /** `AAAAMMJJ-AAAAMMJJ` pour une saison datée, `permanent`, `undated`. */
  key: string;
  from: string | null;
  to: string | null;
  age: StockAge;
  codes: string[];
  soldQty: number;
  sellThroughBp: number | null;
  zone: 'fast' | 'steady' | 'slow' | null;
}

/** Une taille de « La courbe des tailles » : son stock et ses ventes, et leurs parts dans la grille. */
export interface StockSizeRow {
  size: string;
  pieces: number;
  soldQty: number;
  /** `null` quand la grille n'a rien en stock. */
  stockShareBp: number | null;
  /** `null` quand la grille n'a rien vendu. */
  salesShareBp: number | null;
  /** Part du stock moins part des ventes, en points entiers ; `null` quand l'une des deux ne se mesure pas. */
  gapPoints: number | null;
}

/** « La courbe des tailles » d'une grille. */
export interface StockSizeCurve {
  id: string;
  name: string;
  pieces: number;
  soldQty: number;
  /** Pièces hors de la courbe : sans taille, ou d'une taille d'une autre grille. */
  unsizedPieces: number;
  rows: StockSizeRow[];
  /** Le rang de la taille la plus surreprésentée, de la plus sous-représentée — `null` sans écart d'au moins 3 points. */
  over: number | null;
  under: number | null;
}

/** Une référence de « Ce qui dort en réserve ». */
export interface StockDormantRow {
  model: string;
  label: string;
  pieces: number;
  valueCents: number | null;
  soldQty: number;
  sellThroughBp: number | null;
  slow: boolean;
  coverageDays: number | null;
}

export interface StockCoverageRow {
  /** La valeur du regroupement — `UNSET_VALUE` pour les fiches sans ce champ. */
  label: string;
  pieces: number;
  valueCents: number | null;
  soldQty: number;
  /** Jours de vente en stock ; `null` sans vente sur la période. */
  coverageDays: number | null;
  zone: 'shortage' | 'balanced' | 'overstock';
}

export interface StockSummaryView {
  period: { from: string; to: string };
  asOf: string | null;
  reference: { from: string; to: string; kind: 'n1' | 'custom' };
  grain: 'day' | 'week' | 'month';
  totals: StockSummaryTotals;
  refTotals: StockSummaryTotals;
  growth: Record<'value' | 'pieces' | 'coverage' | 'sellThrough' | 'rotation' | 'slow', number | null>;
  /** Le stock en fin de période face à son début. */
  levelChange: { valueBp: number | null; piecesBp: number | null };
  buckets: { key: string; a: StockSummaryBucket; r: StockSummaryBucket | null }[];
  /** Les plus gros stocks de chaque axe et les seuils qui les jugent — servis par le serveur, jamais recopiés ici. */
  coverage: {
    thresholds: { slowSellThroughBp: number; fastSellThroughBp: number; shortageBelowDays: number; overstockAboveDays: number; coverageCapDays: number; coverageRows: number; dormantRows: number;
      sizeGapPoints: number };
    axes: Record<SalesSummaryAxis, { rows: StockCoverageRow[]; overstock: number | null; shortage: number | null }>;
  };
  age: { basis: 'value' | 'pieces'; buckets: StockAgeBucket[] };
  sellThrough: { weekStarts: string[]; all: StockPace; rows: StockSellThroughRow[] };
  /** Une courbe par grille principale qui a au moins deux tailles, la plus stockée d'abord. */
  sizes: { grids: StockSizeCurve[] };
  /** Les modèles qui dorment le plus, et ce qu'ils immobilisent ensemble — en pièces quand la valeur ne se mesure pas. */
  dormant: { basis: 'value' | 'pieces'; rows: StockDormantRow[]; pieces: number; valueCents: number | null; shareBp: number | null };
  scope: { references: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/** Un indicateur du Reporting ventes — ses colonnes, dans l'ordre du serveur (`sales/stats-report.ts`). */
export type SalesReportMetric =
  | 'grossTtcCents' | 'netTtcCents' | 'netHtCents' | 'discountRateBp' | 'qtySold' | 'purchaseHtCents' | 'marginHtCents'
  | 'marginRateBp' | 'coefBp' | 'shareNetBp' | 'shareQtyBp' | 'shareMarginBp';

/** Les indicateurs qui peuvent ne pas se mesurer : un taux sans base, une marge dont un coût manque, une part sur rien. */
type SalesReportNullable =
  | 'discountRateBp' | 'purchaseHtCents' | 'marginHtCents' | 'marginRateBp' | 'coefBp' | 'shareNetBp' | 'shareQtyBp' | 'shareMarginBp';

/** Les indicateurs d'une ligne sur UNE fenêtre ; `null` = non mesurable (« — »). */
export type SalesReportMeasures = Record<Exclude<SalesReportMetric, SalesReportNullable>, number> & Record<SalesReportNullable, number | null>;

/** La période, la comparaison quand on compare, et l'écart relatif de chaque indicateur. */
export interface SalesReportValues {
  a: SalesReportMeasures;
  b: SalesReportMeasures | null;
  growth: Record<SalesReportMetric, number | null> | null;
}

/** Un niveau de ventilation affiché : un critère de filtre, ou le vendeur. */
export type SalesReportLevel = SalesSummaryFilterKey | 'seller';

/**
 * STATISTIQUES › VENTES › REPORTING VENTES — DÉFINI par le serveur : sommes, taux, parts et écarts. Le Reporting vendeurs
 * rend la même forme, ventilée par vendeur, sans bloc.
 * ⚠️ L'écran trie et dessine ; il ne recalcule aucun nombre.
 */
export interface SalesReportView {
  period: { from: string; to: string };
  /** Aujourd'hui quand la période, qui finissait dans le futur, a été arrêtée « à date » ; `null` sinon. */
  asOf: string | null;
  reference: { from: string; to: string } | null;
  /** Les niveaux affichés ; `seller` seul pour le Reporting vendeurs (Statistiques › Vendeurs), jamais au Reporting ventes. */
  axes: SalesReportLevel[];
  total: SalesReportValues;
  /** Le bloc Stock du total ; `null` quand il n'a pas été demandé (`stock`). */
  stockTotal: SalesStockValues | null;
  /** Le bloc Commandes du total ; `null` quand il n'a pas été demandé (`orders`). */
  ordersTotal: SalesOrderValues | null;
  /**
   * `stock` / `orders` : les blocs de la ligne ; `null` sans bloc demandé, ou pour une ligne sans stock ni mouvement, sans
   * commande.
   */
  rows: (SalesReportValues & {
    parts: { value: string; label: string }[]; articles: number; stock: SalesStockValues | null; orders: SalesOrderValues | null;
  })[];
  scope: { references: number; rows: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/** Les colonnes du bloc Stock du Reporting ventes — celles du Reporting stock, quantités et valeurs à part. */
export type SalesStockMetric =
  | 'entriesQty' | 'entriesValueCents' | 'exitsQty' | 'exitsValueCents' | 'stockQty' | 'shareStockBp' | 'stockValueCents' | 'sellThroughBp'
  | 'retroQty';

/** Ce que le grand livre apporte à une ligne du Reporting ventes : le Reporting stock, et les pièces rétrocédées. */
export interface SalesStockMeasures extends StockReportMeasures {
  /** Les pièces parties en rétrocession (la règle de l'onglet Taux de sortie) — hors chiffre d'affaires. */
  retroQty: number;
}

/** Le bloc Stock d'une ligne du Reporting ventes : les indicateurs du Reporting stock, sur les deux fenêtres, et leur écart. */
export interface SalesStockValues {
  a: SalesStockMeasures;
  b: SalesStockMeasures | null;
  growth: Record<SalesStockMetric, number | null> | null;
}

/** Les colonnes du bloc Commandes du Reporting ventes. */
export type SalesOrderMetric = 'orderedValueCents' | 'remainingValueCents' | 'orderedQty' | 'remainingQty';

/**
 * Le bloc Commandes d'une ligne : les commandes passées dans chaque fenêtre (HT, nettes de leur remise, port exclu ;
 * `null` dès qu'une ligne n'a pas de prix) et ce qu'elles attendent encore AUJOURD'HUI — un état, sans écart.
 */
export interface SalesOrderValues {
  a: Record<SalesOrderMetric, number | null> & { orderedQty: number; remainingQty: number };
  b: (Record<SalesOrderMetric, number | null> & { orderedQty: number; remainingQty: number }) | null;
  growth: Record<SalesOrderMetric, number | null> | null;
}

/** Les indicateurs d'un lot sur UNE fenêtre au Reporting stock ; `null` = non mesurable (« — »). */
export interface StockReportMeasures {
  /** Toutes les entrées de la fenêtre, et leur valeur — `null` quand un niveau du lot n'est pas valorisé. */
  entriesQty: number;
  entriesValueCents: number | null;
  /** Toutes les sorties de la fenêtre, ventes nettes comprises. */
  exitsQty: number;
  exitsValueCents: number | null;
  /** Le stock à la FIN de la fenêtre, et sa valeur au PMP. */
  stockQty: number;
  stockValueCents: number | null;
  soldQty: number;
  /** Part du stock (en pièces) dans celui de la même fenêtre. */
  shareStockBp: number | null;
  /** Vendu ÷ (vendu + stock). */
  sellThroughBp: number | null;
}

/** Les indicateurs du Reporting stock dont l'écart se lit (en pièces, comme le dessin). */
export type StockReportMetric = 'entriesQty' | 'exitsQty' | 'stockQty' | 'shareStockBp' | 'sellThroughBp';

export interface StockReportValues {
  a: StockReportMeasures;
  b: StockReportMeasures | null;
  growth: Record<StockReportMetric, number | null> | null;
}

/**
 * STATISTIQUES › STOCK › REPORTING STOCK — DÉFINI par le serveur (`sales/stats-stock-report.ts`) : flux, stock de fin,
 * parts, taux et écarts. ⚠️ L'écran trie et dessine ; il ne recalcule aucun nombre.
 */
export interface StockReportView {
  period: { from: string; to: string };
  asOf: string | null;
  reference: { from: string; to: string } | null;
  axes: SalesSummaryFilterKey[];
  total: StockReportValues;
  rows: (StockReportValues & { parts: { value: string; label: string }[]; articles: number })[];
  scope: { references: number; rows: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/** Les indicateurs du Reporting taux de sortie sur UNE fenêtre, en pièces ; `null` = non mesurable (« — »). */
export interface StockExitMeasures {
  soldQty: number;
  /** Les entrées ACHETÉES (la règle du Reporting articles). */
  receivedQty: number;
  /** Les pièces parties en rétrocession. */
  retroQty: number;
  /** Le stock à la FIN de la fenêtre. */
  stockQty: number;
  /** Vendu ÷ (vendu + stock) — rétrocessions exclues, le taux du Résumé. */
  sellThroughBp: number | null;
  /** (Vendu + rétrocédé) ÷ (vendu + rétrocédé + stock) — rétrocessions comptées. */
  exitRateBp: number | null;
}

export type StockExitMetric = keyof StockExitMeasures;

export interface StockExitValues {
  a: StockExitMeasures;
  b: StockExitMeasures | null;
  growth: Record<StockExitMetric, number | null> | null;
}

/**
 * STATISTIQUES › STOCK › REPORTING TAUX DE SORTIE — DÉFINI par le serveur (`sales/stats-stock-report.ts`), les deux taux
 * compris : l'écran choisit celui qu'il montre, il ne le calcule pas. Les seuils de couleur sont SERVIS.
 */
export interface StockExitView {
  period: { from: string; to: string };
  asOf: string | null;
  reference: { from: string; to: string } | null;
  axes: SalesSummaryFilterKey[];
  thresholds: { fastSellThroughBp: number; slowSellThroughBp: number };
  total: StockExitValues;
  rows: (StockExitValues & { parts: { value: string; label: string }[]; articles: number })[];
  scope: { references: number; rows: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/** Une taille d'une ligne au Stock par tailles : créée au catalogue ou non, son stock de fin, sa valeur, son vendu, son taux. */
export interface StockSizeCell {
  made: boolean;
  stockQty: number;
  stockValueCents: number | null;
  soldQty: number;
  sellThroughBp: number | null;
}

/**
 * STATISTIQUES › STOCK › STOCK PAR TAILLES — DÉFINI par le serveur (`sales/stats-stock-sizes.ts`) : les grilles proposées
 * (la plus stockée d'abord), la grille lue, ses tailles, chaque cellule et ce qui est hors des colonnes.
 */
export interface StockSizesView {
  period: { from: string; to: string };
  asOf: string | null;
  grids: { id: string; name: string; sizes: string[]; models: number; pieces: number }[];
  grid: string | null;
  sizes: { id: string; label: string; active: boolean }[];
  axes: SalesSummaryFilterKey[];
  total: { cells: StockSizeCell[]; all: StockSizeCell };
  rows: { parts: { value: string; label: string }[]; articles: number; cells: StockSizeCell[]; all: StockSizeCell }[];
  unsizedPieces: number;
  thresholds: { fastSellThroughBp: number; slowSellThroughBp: number };
  scope: { references: number; rows: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/** Un état du stock : les pièces, et leur valeur au PMP — `null` = non mesurable (« — »). */
export interface StockPeriodCell {
  qty: number;
  valueCents: number | null;
}

/** Le grain des colonnes du Stock par périodes : la semaine ISO, le mois, l'année. */
export type StockPeriodGrain = 'w' | 'm' | 'y';

/** Ce que mesure une fenêtre du Résumé des clients (`sales/stats-clients.ts`) — centimes, points de base, `null` = non mesurable. */
export interface ClientsWindowView {
  tickets: number;
  identifiedTickets: number;
  identifiedTicketsBp: number | null;
  activeClients: number;
  newClients: number;
  buyers: number;
  repeatBuyers: number;
  repurchaseBp: number | null;
  netTtcCents: number;
  identifiedNetTtcCents: number;
  identifiedRevenueBp: number | null;
  identifiedBasketCents: number | null;
  anonymousBasketCents: number | null;
  /** L'écart du panier encarté face au panier anonyme. */
  basketLiftBp: number | null;
}

/**
 * STATISTIQUES › CLIENTS › RÉSUMÉ — DÉFINI par le serveur (décision S4) : encartement, clients actifs et nouveaux, taux de
 * réachat, panier et chiffre encartés, qui encarte, joignables, segments RFM du cœur, cohortes.
 */
export interface ClientsSummaryView {
  period: { from: string; to: string };
  asOf: string | null;
  reference: { from: string; to: string; kind: 'n1' | 'custom' };
  grain: SalesSummaryView['grain'];
  a: ClientsWindowView;
  r: ClientsWindowView;
  growth: { identifiedTickets: number | null; activeClients: number | null; repurchase: number | null; identifiedBasket: number | null; identifiedRevenue: number | null };
  buckets: { key: string; tickets: number; identifiedTickets: number; identifiedTicketsBp: number | null; activeClients: number }[];
  sellers: { id: string; name: string; tickets: number; identifiedTickets: number; identifiedTicketsBp: number | null }[];
  reach: {
    files: number; sms: number; email: number; wallet: number; any: number;
    shares: { sms: number | null; email: number | null; wallet: number | null; any: number | null; none: number | null };
  };
  segments:
    | { state: 'ok'; windowDays: number; population: number; rows: { segment: string; clients: number; clientsBp: number | null; revenueCents: number; revenueBp: number | null; basketCents: number | null }[] }
    | { state: 'refused'; reason: string };
  cohorts: { month: string; size: number; returnBp: (number | null)[] }[];
  /** La part des clients recrutés revenue acheter trois mois plus tard ; `null` sans ce recul. */
  cohortReturn3Bp: number | null;
  scope: { references: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/** La cagnotte d'une fenêtre de l'onglet Fidélité — centimes, points de base (`null` = rien de distribué). */
export interface LoyaltyStatsWindowView {
  earnedCents: number;
  /** La part de la distribuée que le magasin a offerte : bonus de bienvenue et crédits posés à la main. */
  offeredCents: number;
  /** Déduite d'une vente ou convertie en bon d'achat. */
  returnedCents: number;
  usageBp: number | null;
  creditedClients: number;
}

/**
 * STATISTIQUES › CLIENTS › FIDÉLITÉ (`sales/stats-clients-loyalty.ts`) — la cagnotte distribuée et utilisée face à la
 * comparaison, six mois de cagnotte, et à date l'encours, les paliers et les titres. DÉFINI par le serveur.
 */
export interface ClientsLoyaltyView {
  period: { from: string; to: string };
  asOf: string | null;
  reference: { from: string; to: string; kind: 'n1' | 'custom' };
  grain: SalesSummaryView['grain'];
  a: LoyaltyStatsWindowView;
  r: LoyaltyStatsWindowView;
  growth: { earned: number | null; returned: number | null };
  buckets: { key: string; earnedCents: number; returnedCents: number }[];
  /** `offeredCents` : la part de la distribuée offerte par le magasin (bonus de bienvenue, crédits posés à la main). */
  months: { month: string; earnedCents: number; offeredCents: number; returnedCents: number; partial: boolean }[];
  monthsUsageBp: number | null;
  outstanding: { balanceCents: number; holders: number; negatives: number };
  tiers:
    | { state: 'off' }
    | {
      state: 'on'; files: number; withoutTier: number;
      rows: {
        id: string; label: string; color: string | null; fromMinor: number | null; toMinor: number | null; rateBp: number | null;
        clients: number; clientsBp: number | null; revenueCents: number; revenueBp: number | null;
      }[];
    };
  titles: {
    rows: { type: string; count: number; issuedCents: number; usedCents: number; liveCents: number; expiredCents: number }[];
    liveCents: number; liveCount: number; expiredCents: number; expiredCount: number;
  };
  seasonPeriods: SeasonPeriod[];
}

/** Une campagne de l'onglet Campagnes : ce qui est parti, les destinataires revenus, son témoin et son effet. */
export interface CampaignStatRowView {
  id: string;
  name: string;
  channel: string;
  status: string;
  startedAt: number | null;
  createdAt: number;
  sent: number;
  served: number;
  buyers: number;
  buyersBp: number | null;
  holdout: { size: number; buyers: number; buyersBp: number | null } | null;
  lift: CampaignLift | null;
}

/**
 * STATISTIQUES › CLIENTS › CAMPAGNES (`sales/stats-clients-campaigns.ts`) — les campagnes du magasin sans brouillon, leurs
 * destinataires revenus dans la fenêtre d'attribution et leur effet face au témoin ; totaux et canaux DÉFINIS par le serveur.
 */
export interface ClientsCampaignsView {
  windowDays: number;
  totals: { campaigns: number; inProgress: number; sent: number; served: number; buyers: number; buyersBp: number | null; measured: number; proven: number };
  rows: CampaignStatRowView[];
  channels: { channel: string; campaigns: number; sent: number; served: number; buyers: number; buyersBp: number | null; measured: number; proven: number }[];
  truncated: boolean;
}

/** Une ligne du Reporting clients : un groupe de fiches et ce qu'elles pèsent. */
export interface ClientReportRowView {
  /** Le code du groupe (tranche, segment, palier, ville) ; `''` = non renseigné. */
  key: string;
  /** Le nom que la donnée porte (ville, palier du barème) ; `null` quand l'écran nomme le code. */
  label: string | null;
  clients: number;
  revenueCents: number;
  visits: number;
  basketCents: number | null;
  visitsPerClient: number | null;
  loyaltyCents: number;
  reachable: number;
  reachableBp: number | null;
  lastAt: string | null;
}

/**
 * STATISTIQUES › CLIENTS › REPORTING CLIENTS (`sales/stats-clients-report.ts`) — les fiches vivantes regroupées par un axe,
 * chaque client lu comme sa fiche ; `refused` = la segmentation impossible et son motif. DÉFINI par le serveur.
 */
export interface ClientsReportView {
  axis: 'segment' | 'city' | 'age' | 'tier' | 'seniority';
  asOf: string;
  rows: ClientReportRowView[];
  total: ClientReportRowView;
  refused?: string;
}

/** Une cellule de Vendeurs par période : les mesures de la vente (`null` = non mesurable) ; `lines` à zéro = aucune vente. */
export interface SellerPeriodCell {
  netTtcCents: number;
  marginHtCents: number | null;
  qtySold: number;
  marginRateBp: number | null;
  coefBp: number | null;
  lines: number;
}

/**
 * STATISTIQUES › VENDEURS › VENDEURS PAR PÉRIODE (`sales/stats-sellers-periods.ts`) — une ligne par vendeur, une colonne par
 * période (ses jours lus, ramenés à la période choisie), la colonne Total, somme des périodes affichées. DÉFINI par le serveur.
 */
export interface SellerPeriodsView {
  period: { from: string; to: string };
  asOf: string | null;
  grain: StockPeriodGrain;
  buckets: { key: string; from: string; to: string; partial: boolean; isoWeek: number | null }[];
  truncated: boolean;
  axes: ['seller'];
  total: { cells: SellerPeriodCell[]; all: SellerPeriodCell };
  rows: { parts: { value: string; label: string }[]; articles: number; cells: SellerPeriodCell[]; all: SellerPeriodCell }[];
  scope: { references: number; rows: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/**
 * STATISTIQUES › STOCK › STOCK PAR PÉRIODES — DÉFINI par le serveur (`sales/stats-stock-periods.ts`) : les colonnes (leur
 * période, le jour où chacune se lit), le stock de la fin de chacune et l'état du jour. ⚠️ L'écran nomme et dessine.
 */
export interface StockPeriodsView {
  period: { from: string; to: string };
  asOf: string | null;
  grain: StockPeriodGrain;
  buckets: { key: string; from: string; to: string; readOn: string; partial: boolean; isoWeek: number | null }[];
  /** Vrai quand le plafond de colonnes a coupé la période choisie. */
  truncated: boolean;
  today: string;
  axes: SalesSummaryFilterKey[];
  total: { cells: StockPeriodCell[]; today: StockPeriodCell };
  rows: { parts: { value: string; label: string }[]; articles: number; cells: StockPeriodCell[]; today: StockPeriodCell }[];
  scope: { references: number; rows: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/**
 * La requête d'un reporting — période, comparaison, niveaux, recherche, filtres —, écrite une fois pour les reportings des
 * ventes et du stock : deux écritures enverraient deux lectures du même geste.
 */
const reportSearch = (p: SalesReportParams): string => new URLSearchParams({
  from: p.from, to: p.to, axes: p.axes.join(','),
  ...(p.cmpFrom && p.cmpTo ? { cmpFrom: p.cmpFrom, cmpTo: p.cmpTo } : {}),
  ...(p.q ? { q: p.q } : {}),
  ...(Object.values(p.filters).some((v) => v && v.length) ? { filters: JSON.stringify(p.filters) } : {}),
  ...(p.stock ? { stock: '1' } : {}),
  ...(p.orders ? { orders: '1' } : {}),
}).toString();

/** Les onglets qui ont une bibliothèque de vues enregistrées — les huit reportings (`sales/stats-views.ts`). */
export type StatsViewTab =
  | 'sales-report' | 'seller-report' | 'seller-periods' | 'article-report' | 'stock-report' | 'stock-exit' | 'stock-periods' | 'stock-sizes';

/** Une fenêtre, sa comparaison, la recherche et les filtres — la barre partagée des statistiques, en paramètres. */
const windowSearch = (p: StockSummaryParams): string => new URLSearchParams({
  from: p.from, to: p.to,
  ...(p.cmpFrom && p.cmpTo ? { cmpFrom: p.cmpFrom, cmpTo: p.cmpTo } : {}),
  ...(p.q ? { q: p.q } : {}),
  ...(Object.values(p.filters).some((v) => v && v.length) ? { filters: JSON.stringify(p.filters) } : {}),
}).toString();

/** Une fenêtre telle que la barre la tient — la même forme que `StatsWindow` de l'écran. */
export type StatsViewWindow = { mode: 'dates'; from: string; to: string } | { mode: 'season'; from: string; to: string; codes: string[] };

/**
 * CE QU'UNE VUE REPREND — la période, la recherche, les filtres, les niveaux et le tri, plus ce que l'onglet règle de plus.
 * Les champs propres à un onglet n'existent que sur lui ; le serveur refuse les autres.
 */
export interface StatsViewSettings {
  a: StatsViewWindow;
  /** La comparaison, sur un onglet qui compare seulement. */
  b?: StatsViewWindow | null;
  q: string;
  filters: Partial<Record<SalesSummaryFilterKey, string[]>>;
  axes: string[];
  /** `null` : l'ordre de la ventilation. */
  sort: { k: string; d: 1 | -1; man: boolean } | null;
  display?: 'a' | 'b' | 'd';
  columns?: { order: string[]; hidden: string[] };
  receptions?: StatsViewWindow;
  retro?: 'in' | 'out';
  grain?: StockPeriodGrain;
  grid?: string | null;
  measures?: string[];
}

/** Une vue de la bibliothèque. `dropped` compte les réglages qui ne se lisent plus et que le serveur a écartés. */
export interface StatsView {
  name: string;
  createdBy: string;
  updatedAt: string;
  settings: StatsViewSettings;
  dropped: number;
}

/** Ce que l'onglet Reporting ventes demande : la période, la comparaison s'il y en a une, les niveaux et le périmètre. */
export interface SalesReportParams {
  from: string;
  to: string;
  cmpFrom?: string;
  cmpTo?: string;
  axes: SalesSummaryFilterKey[];
  q: string;
  filters: Partial<Record<SalesSummaryFilterKey, string[]>>;
  /** Le bloc Stock — demandé seulement quand l'écran en affiche une colonne : le rejeu du grand livre est la lecture la plus lourde. */
  stock?: boolean;
  /** Le bloc Commandes — demandé seulement quand l'écran en affiche une colonne. */
  orders?: boolean;
}

// ── Statistiques › Ventes › Reporting articles ──

/** Les niveaux du reporting articles : les sept critères, plus le coloris et le gencode. */
export type ArticleReportAxis = SalesSummaryFilterKey | 'coul' | 'ean';

/**
 * Les indicateurs d'une ligne — montants en centimes, taux en points de base, dates en jour local `AAAAMMJJ`.
 * ⚠️ `null` = non mesurable (un coût inconnu, un stock sans PMP, une vente libre sans fiche) : l'écran affiche « — ».
 */
export interface ArticleReportMeasures {
  lastReception: string | null;
  lastSale: string | null;
  receivedQty: number;
  receivedHtCents: number;
  soldCostHtCents: number | null;
  qtySold: number;
  netHtCents: number;
  netTtcCents: number;
  stockQty: number | null;
  stockValueHtCents: number | null;
  receivedSoldBp: number | null;
  marginHtCents: number | null;
  marginRateBp: number | null;
  coefBp: number | null;
}
export type ArticleReportMetric = keyof ArticleReportMeasures;

/** Le reporting articles : deux fenêtres, les niveaux, le total, une ligne par combinaison. Le serveur définit chaque nombre. */
export interface ArticleReportView {
  period: { from: string; to: string };
  receptions: { from: string; to: string };
  asOf: string | null;
  axes: ArticleReportAxis[];
  /** Ce que compte une ligne : des articles (modèles), des coloris ou des déclinaisons. */
  grain: 'art' | 'coul' | 'ean';
  total: ArticleReportMeasures;
  rows: { parts: { value: string; label: string }[]; units: number; m: ArticleReportMeasures }[];
  scope: { units: number; rows: number };
  filterOptions: Record<SalesSummaryFilterKey, { value: string; label: string }[]>;
  seasonPeriods: SeasonPeriod[];
}

/** Ce que l'onglet demande : la fenêtre Ventes (la période des onglets), la fenêtre Réceptions, les niveaux, le périmètre. */
export interface ArticleReportParams {
  from: string;
  to: string;
  recFrom: string;
  recTo: string;
  axes: ArticleReportAxis[];
  q: string;
  filters: Partial<Record<SalesSummaryFilterKey, string[]>>;
}

export interface DashboardView {
  period: { from: string; to: string };
  previousPeriod: { from: string; to: string };
  totals: PeriodTotals;
  previous: PeriodTotals;
  growth: { caBp: number; panierBp: number; salesBp: number };
  /** Nom d'ENSEIGNE du magasin — distinct de la raison sociale. `null` = réglage non posé. */
  storeName: string | null;
  /**
   * Fidélité de la période, comptée par `@orgazone/core-loyalty`.
   * ⚠️ `null` = le magasin n'a PAS de programme (table absente) — à distinguer d'un programme sans
   * mouvement, qui rend des totaux à zéro.
   * ⚠️ `usageBp` nul = rien n'a été acquis : « 0 % » affirmerait un programme inutilisé.
   */
  loyalty: {
    earnedCents: number;
    /** La part de l'acquis que le magasin a offerte (bonus de bienvenue, crédits posés à la main) — déjà dans `earnedCents`. */
    offeredCents: number;
    /** Déduit en caisse (« cashback »). */
    spentCents: number;
    /** Converti en bon d'achat remis au client. */
    vouchersCents: number;
    /** ⚠️ CE QUE LE COMMERÇANT APPELLE « remises de fidélité » : déduit + converti en bons. Le seul
     *  débit annonçait 1,4 % d'usage là où le programme en rend près de 20 %. */
    returnedCents: number;
    usageBp: number | null;
  } | null;
  chart: {
    /**
     * Le GRAIN est renvoyé par le SERVEUR : l'écran ne le redevine pas depuis les bornes.
     * ⚠️ Déduit de l'étendue demandée — six mois en barres journalières ne se lisent pas.
     */
    grain: 'hour' | 'day' | 'week' | 'month';
    /** Chaque point porte les quatre mesures — une sparkline par tuile de KPI. */
    points: Array<{
      key: string; caTtcCents: number; nbSales: number; panierMoyenCents: number; idvX10: number;
      /** CA du même intervalle un an plus tôt. ⚠️ `null` = pas de vis-à-vis, jamais « zéro vente ». */
      prevCaTtcCents: number | null;
    }>;
  };
  sellers: Array<{ seller: string; caTtcCents: number; nbSales: number }>;
  payments: Array<{ method: string; amountCents: number; shareBp: number }>;
  topArticles: Array<{
    label: string; gencode: string | null;
    /** Rayon de l'article. ⚠️ `null` = pas de famille au catalogue, jamais un « Divers » inventé. */
    family: string | null;
    qty: number; caTtcCents: number;
  }>;
  /**
   * De quoi écrire l'accroche, MESURÉE. `registers` = caisses ayant ENCAISSÉ sur la période (le
   * back-office ne sait pas si une caisse est allumée) ; `lastSaleAt` n'est pas borné à la période.
   */
  activity: {
    registers: number;
    lastSaleAt: string | null;
    /** Parc en ce moment (battement de synchro). ⚠️ `null` = mesure impossible, PAS « aucune caisse ». */
    fleet: { online: number; known: number; terminals?: { online: number; known: number } } | null;
    /**
     * PASSAGES comptés à l'entrée sur la période — le dénominateur du taux de transformation.
     * ⚠️ `null` = aucun compteur pour ce magasin, JAMAIS « zéro passage ». Un zéro affirmerait qu'un
     * magasin ouvert n'a vu entrer personne ; l'absence de capteur ne dit rien du trafic.
     */
    footfall: number | null;
    /**
     * QUAND LE COMPTEUR A COMPTÉ POUR LA DERNIÈRE FOIS — toutes périodes confondues.
     * ⚠️ Il sépare deux absences que `footfall: null` confond : jamais de compteur, ou un compteur
     * qui s'est tu. L'écran ne doit pas dire la même chose des deux.
     */
    footfallLastAt: number | null;
  };
}


// ── BONS DE LIVRAISON (Commandes & réceptions, onglet BL) ─────────────────────────────────────────

/** Les états d’un bon, DÉRIVÉS par le serveur de ses lignes — jamais stockés. */
// ── Fichier clients — la liste de l'écran /clients et la recherche de la palette ⌘K ──────────────
export type ClientChip = 'gain' | 'wallet' | 'bmois' | 'noemail' | 'notel' | 'rgpd';
export type ClientSortKey = 'name' | 'card' | 'tel' | 'email' | 'b' | 'g' | 'v' | 'ca' | 'qualif';

export interface ClientRow {
  id: string;
  lastName: string;
  firstName: string | null;
  clientNumber: string | null;
  loyaltyNumber: string | null;
  email: string | null;
  phone: string | null;
  birthdate: string | null;
  tier: string | null;
  /** Le palier tel qu'il s'affiche, résolu au serveur sur le barème du magasin — `null` sans palier. */
  tierDisplay: { label: string; color: string | null } | null;
  qualifications: string[];
  wallet: boolean;
  pro: boolean;
  /** Fiche anonymisée (RGPD) : identité détachée, historique conservé — l'écran la grise. */
  erased: boolean;
  loyaltyCents: number;
  /** Pourquoi la cagnotte est négative (code du cœur) — `null` si elle ne l'est pas. */
  negativeCause: NegativeBalanceCause | null;
  /** Ventes rattachées (tickets et factures) — dérivées des pièces fiscales, jamais recopiées. */
  visits: number;
  caCents: number;
}

export interface ClientsSummary {
  records: number;
  cagnotteCents: number;
  joignablesEmail: number;
  annivMois: number;
  chips: Record<ClientChip, number>;
}

export interface ClientsPage {
  rows: ClientRow[];
  total: number;
  /** `null` sur un lot de continuation — jamais des zéros qui auraient l'air vrais. */
  summary: ClientsSummary | null;
}

/** Un retour qui n'a pas défait la fidélité de sa vente — Fidélité › Retours à corriger (`crm/return-loyalty-repair.ts`). */
export interface ReturnLoyaltyRepair {
  returnId: string;
  returnNumber: string;
  /** Horodatage fiscal du retour, AAAAMMJJHHMMSS. */
  returnAt: string;
  registerId: string;
  originNumber: string;
  clientId: string;
  clientName: string;
  /** Gain à reprendre — 0 : rien ne manque dans ce sens. */
  clawbackCents: number;
  /** Remise fidélité à rendre — 0 : rien ne manque dans ce sens. */
  refundCents: number;
  /** Faux : la caisse n'a encore rien envoyé de plus récent, son écriture peut être en route. */
  ready: boolean;
}

/** Ce que le geste a fait — et ce qu'il n'a PAS fait, avec la raison. */
export interface ReturnLoyaltyRepairReport {
  repaired: { returnId: string; returnNumber: string; clawbackCents: number; refundCents: number }[];
  waiting: string[];
  nothingToDo: string[];
}

/** L'état de la numérotation des fiches — et ce que l'outil n'a PAS pu traiter, qui compte autant. */
export interface ClientNumberingReport {
  missing: number;
  numbered: number;
  remaining: number;
  dryRun: boolean;
  /** Motif RÉEL d'un refus (code « S » du magasin inconnu) — jamais « indisponible ». */
  blocked?: string;
}

/** Une commune proposée pour l'adresse d'une fiche. */
export interface CommuneOption { postalCode: string; city: string }
/** Ce qu'une recherche de communes rend — `referentialMissing` quand le magasin n'a aucun référentiel. */
export interface CommuneSearchResult { communes: CommuneOption[]; referentialMissing: boolean }

// ── La FICHE client (modale 5 onglets) ────────────────────────────────────────────────────────────

export interface ClientFiche {
  id: string;
  clientNumber: string | null;
  title: string | null;
  lastName: string;
  firstName: string | null;
  email: string | null;
  mobilePhone: string | null;
  landlinePhone: string | null;
  birthdate: string | null;
  address1: string | null;
  postalCode: string | null;
  city: string | null;
  loyaltyNumber: string | null;
  loyaltyCents: number;
  /** Pourquoi la cagnotte est négative (code du cœur) — `null` si elle ne l'est pas. */
  negativeCause: NegativeBalanceCause | null;
  /** La remise permanente, en points de base — 0 = aucune. */
  permanentDiscountBp: number;
  tier: string | null;
  /** Le palier tel qu'il s'affiche, résolu au serveur sur le barème du magasin — `null` sans palier. */
  tierDisplay: { label: string; color: string | null } | null;
  isWeb: boolean;
  isPro: boolean;
  companyLegalName: string | null;
  siret: string | null;
  vatNumber: string | null;
  naf: string | null;
  optInEmail: boolean;
  optInSms: boolean;
  optInWallet: boolean;
  /** Les qualifications de la fiche — code + libellé résolu (`null` = code hors référentiel). */
  qualifications: { code: string; label: string | null; color: string | null }[];
  consentAt: number | null;
  consentChannel: string | null;
  consentBy: string | null;
  erased: boolean;
  erasedAt: number | null;
  createdAt: number;
}

export interface ClientWalletState {
  hasPass: boolean; passActive: boolean; passRequested: boolean; platform: string | null;
  /** Quand le pass a été émis (`null` sans pass) — émis, pas installé. */
  issuedAt: number | null;
}
export interface ClientLedgerLine { at: number; label: string; deltaCents: number }
export interface ClientHistoryLine {
  at: string; pieceId: string; pieceNumber: string; label: string;
  /** Code PRODUIT lu sur l'étiquette — ce que le comptoir cherche, jamais le gencode. */
  articleCode: string | null; brand: string | null; color: string | null;
  size: string | null; qty: number; totalCents: number;
}
export interface ClientHistoryStats { tickets: number; items: number; caCents: number; ca12mCents: number; firstAt: string | null; lastAt: string | null }
/** L'état d'un titre — celui du CŒUR `core-vouchers`, jamais un statut local. */
export type VoucherState = 'issued' | 'partial' | 'redeemed' | 'expired';
export interface ClientTitreLine { type: string; nature: string; code: string; amountCents: number; remainingCents: number; issuedAt: number; expiresAt: number | null; state: VoucherState }

// ── TITRES & BONS — le registre unifié par nature fiscale ─────────────────────────────────────────
export type TitreKind = 'titre' | 'remise';
export type TitreSortKey = 'code' | 'type' | 'client' | 'issued' | 'expires' | 'amount' | 'remaining';

export interface TitreRow {
  id: string;
  code: string;
  type: string;
  kind: TitreKind;
  state: VoucherState;
  clientId: string | null;
  clientName: string | null;
  amountCents: number;
  remainingCents: number;
  /** Remise en POINTS DE BASE (1000 = 10 %) — `null` pour un titre en €. */
  discountPctBp: number | null;
  issuedAt: number;
  validFrom: number | null;
  expiresAt: number | null;
  redeemedAt: number | null;
  originSaleId: string | null;
  redeemedSaleId: string | null;
  /** Titre INJECTÉ en caisse (anti-trou de caisse) — émission sans vente d'origine. */
  passePartout: boolean;
  /** Titre REPRIS de l'ancien logiciel — hors chaîne de signature locale. */
  legacy: boolean;
}

export interface TitresSummary {
  enCirculationCents: number;
  enCirculation: number;
  emis30j: number;
  utilises30j: number;
  expirent60j: number;
  states: Record<VoucherState, number>;
  /** Compteurs par TYPE (avoir, cadeau…) — les puces de l'écran. */
  types: Record<string, number>;
  /** ⚠️ Les repères n'ont vu qu'une partie du registre : l'engagement est un MINIMUM, pas un total. */
  tronque: boolean;
  /** Lignes réellement lues pour ces repères. */
  lues: number;
}

export interface TitresPage {
  rows: TitreRow[];
  total: number;
  summary: TitresSummary | null;
}

/** Un fait daté de la vie d'un titre — émission, consommation, extinction. */
export interface TitreEvent {
  at: number;
  label: string;
  /** Signé : + à l'émission, − à la consommation. `null` = fait sans montant. */
  deltaCents: number | null;
  pieceId: string | null;
  pieceNumber: string | null;
}

export interface TitreDetail {
  row: TitreRow;
  events: TitreEvent[];
  childCode: string | null;
  parentCode: string | null;
  /** La règle du magasin en toutes lettres — un REPÈRE, jamais l'échéance de CE titre. */
  validityRule: string | null;
  /** D'où vient l'échéance (calculée à la reprise), ou pourquoi il n'y en a pas. `null` = rien à dire. */
  expiryNote: string | null;
}

/** Une qualification du référentiel, avec le nombre de fiches qui la portent. */
export interface ClientQualification {
  code: string; label: string; sortOrder: number; isActive: boolean; usage: number;
  /** Repère visuel choisi par le magasin (hex) — `null` = ton neutre, jamais une couleur inventée. */
  color: string | null;
}

/**
 * Ce qui manque à la marge : les lignes sans coût d'achat et les codes distincts en cause, comptés par cause. Les codes
 * eux-mêmes se lisent dans la liste (`salesMissingCosts`).
 */
export interface SalesMarginGap {
  lines: number; codeCount: number;
  /** Les codes distincts par cause : sans prix d'achat (la fiche existe), ou inconnus du catalogue (la fiche manque). */
  byCause: { purchasePrice: number; unknownArticle: number };
}

/** Un article dont le coût manque — ce que la liste « Voir les articles » montre (`sales/sale-lines.ts`). */
export interface MissingCostArticleView {
  /** Le code vendu : le gencode, ou le libellé d'une vente sans code. */
  code: string;
  label: string;
  cause: 'purchase_price' | 'unknown_article';
  lines: number;
  netHtCents: number;
}

/** La période et les filtres dont on veut les articles sans coût — ceux de l'écran qui l'affiche. */
export interface MissingCostsParams { from: string; to: string; q?: string; filters?: SalesSummaryParams['filters'] }

export interface ClientMonthSpend { month: string; totalCents: number }
/** Une part du panier par famille — « sans famille » et « le reste » sont des SORTES, l'écran les nomme. */
export interface ClientFamilyShare { kind: 'family' | 'unset' | 'others'; family: string; totalCents: number }

export interface ClientDetail {
  record: ClientFiche;
  wallet: ClientWalletState;
  ledger: ClientLedgerLine[];
  /** Le grand livre compte plus de lignes que celles rendues — leur total ne redonne pas le solde. */
  ledgerTruncated: boolean;
  /** Le total de TOUS les mouvements, calculé par le serveur — il se compare au solde, liste tronquée ou non. */
  ledgerTotalCents: number;
  history: ClientHistoryLine[];
  historyStats: ClientHistoryStats;
  vouchers: ClientTitreLine[];
  monthlySpend: ClientMonthSpend[];
  familyBreakdown: ClientFamilyShare[];
  /** Demande d'effacement RGPD REÇUE AU COMPTOIR et pas encore traitée. */
  erasureRequest: { requestedAt: number; requestedBy: string | null; channel: string; note: string | null } | null;
  historyTruncated: boolean;
  titresTruncated: boolean;
}

/** Les champs que l'onglet Général enregistre — fermés, le serveur ne copie jamais le corps. */
export interface ClientInput {
  title?: string | null;
  lastName: string;
  firstName?: string | null;
  email?: string | null;
  mobilePhone?: string | null;
  landlinePhone?: string | null;
  birthdate?: string | null;
  address1?: string | null;
  postalCode?: string | null;
  city?: string | null;
  /** La remise permanente en points de base ; absente = la fiche garde la sienne. */
  permanentDiscountBp?: number;
  isWeb?: boolean;
  isPro?: boolean;
  companyLegalName?: string | null;
  siret?: string | null;
  vatNumber?: string | null;
  naf?: string | null;
  optInEmail?: boolean;
  optInSms?: boolean;
  optInWallet?: boolean;
  /**
   * Les codes de qualification. ⚠️ ABSENT = ne pas y toucher ; tableau VIDE = les retirer toutes.
   * Sans cette nuance, l'écran ne pourrait jamais enlever la dernière.
   */
  qualifications?: string[];
}

export type EtatDuBon = 'to_check' | 'ready' | 'partial' | 'received' | 'closed';
export type SourceDuBon = 'edi' | 'import' | 'manual' | 'scan' | 'mobile-partner' | 'legacy';

export interface ResumeDuBon {
  etat: EtatDuBon;
  expected: number;
  checked: number;
  received: number;
  hasVariance: boolean;
  uncheckedLines: number;
  unknownLines: number;
}

export interface BonResume {
  number: string;
  supplierId: string | null;
  supplierLabel: string | null;
  source: SourceDuBon;
  sourceRef: string | null;
  orderRef: string | null;
  deliveredOn: number | null;
  parcels: string | null;
  createdAt: number;
  closedAt: number | null;
  resume: ResumeDuBon;
  /** Des corrections post-intégration existent — « Intégré — corrigé » à l'écran. */
  corrected: boolean;
  /** La marque qu'un BL de l'éditeur attend encore pour avoir un fournisseur — tant qu'elle est là, il n'entre pas en stock. */
  awaitingBrand: string | null;
}

/** Une ligne d'un contrôle Mobile Partner : le code, l'article s'il est connu, sa marque. */
export interface ControlLine { gencode: string; qty: number; known: boolean; name: string | null; brand: string | null }

/**
 * UN CONTRÔLE DE RÉCEPTION EN ATTENTE — une zone scannée en aveugle par un terminal, qu'un humain
 * rapproche d'un bon ou transforme en bon depuis le bandeau de l'onglet BL (maquette `osRows`).
 */
export interface ReceptionControl {
  zoneId: string;
  label: string;
  /** « MP02 » — `null` pour un appareil que nous n'avons pas fourni (l'écran dit « appareil inconnu »). */
  equipmentCode: string | null;
  submittedAt: number;
  pieces: number;
  unknownCodes: number;
  lines: ControlLine[];
  /** Les marques des articles CONNUS — ce que l'opérateur reconnaît d'un coup d'œil. */
  brands: string[];
  supposedSupplier: { id: string | null; label: string } | null;
  /** Le bon proposé au rapprochement (même fournisseur, à contrôler, ≤ 7 jours), ou `null`. */
  proposedNumber: string | null;
}

/** Ce qu'un clic sur une tuile a fait : le bon touché, créé ou rejoint. */
export interface ControlSettlement {
  number: string;
  state: EtatDuBon;
  lines: number;
  checked: number;
  unexpected: number;
  created: boolean;
}

/** Une ligne : attendu / contrôlé (`null` = pas encore) / déjà entré, et le prix avec son ORIGINE. */
export interface DeliveryNoteLine {
  gencode: string;
  expectedQty: number;
  checkedQty: number | null;
  receivedQty: number;
  unitPriceFine: number | null;
  /** `order` · `bl` · `fiche` · `manual` · null — un prix dont on ignore l’origine est un prix qu’on ne croit plus. */
  priceSource: string | null;
  /** `false` = code-barres scanné que le catalogue ignore : il se voit, il ne s’intègre pas. */
  known: boolean;
  name: string | null;
  color: string | null;
  size: string | null;
  /** Cumul des corrections post-intégration déjà écrites (0 sans correction) — base de la modale acFix. */
  fixedDelta: number;
}

export interface DeliveryNoteDetail extends BonResume { note: string | null; lines: DeliveryNoteLine[] }

export interface RapportDIntegration {
  movements: number; pieces: number; piecesSansPrix: number;
  /** Modèles archivés remis en service parce que de la marchandise vient d'entrer pour eux. */
  restoredModels: number;
  bon: DeliveryNoteDetail;
}

export interface RapportDeCorrection { movements: number; bon: DeliveryNoteDetail }

// ── Commandes fournisseur — la promesse ; le reçu se DÉRIVE des BL rattachés ──────────────────────
/** `draft` = un brouillon de l'éditeur : il n'a encore rien promis au fournisseur. */
export type EtatDeCommande = 'draft' | 'waiting' | 'partial' | 'delivered' | 'closed';
export type SourceDeCommande = 'edi' | 'import' | 'manual' | 'legacy';

export interface ResumeDeCommande {
  etat: EtatDeCommande;
  ordered: number;
  received: number;
  remaining: number;
  unknownLines: number;
}

export interface CommandeResume {
  number: string;
  supplierId: string | null;
  supplierLabel: string | null;
  source: SourceDeCommande;
  sourceRef: string | null;
  note: string | null;
  orderedOn: number | null;
  createdAt: number;
  closedAt: number | null;
  /** Livraison prévue, date civile `AAAA-MM-JJ`. */
  expectedOn: string | null;
  /** Conditions figées sur la commande — `null` = aucune. */
  discountBp: number | null;
  shippingCents: number | null;
  earlyPaymentBp: number | null;
  earlyPaymentDays: number | null;
  updatedAt: number | null;
  /** Le dernier ENVOI réel du bon de commande par e-mail — `null` tant qu'aucun n'est parti. */
  sentAt: number | null;
  sentTo: string | null;
  resume: ResumeDeCommande;
  /** Les BL rattachés — c'est d'eux que le reçu vient. */
  deliveryNotes: string[];
  /** Nombre de lignes — la colonne « Lignes » de la liste. */
  lineCount: number;
}

export interface LigneDeCommande {
  gencode: string;
  orderedQty: number;
  receivedQty: number;
  unitPriceFine: number | null;
  priceSource: string | null;
  known: boolean;
  name: string | null;
  color: string | null;
  size: string | null;
  /** Le second axe — l'éditeur remet la ligne dans sa grille. */
  size2: string | null;
  /** La clé du modèle — `null` si l'article a quitté le catalogue. */
  modelKey: string | null;
}

/** Le prix d'achat pré-rempli par l'éditeur, et d'où il vient. */
export interface LastPurchasePrice { unitPriceFine: number; origin: 'order' | 'sheet'; at: number | null }

/** Une case de la grille de l'éditeur : une déclinaison commandable (avec code-barres). */
export interface OrderModelCell { gencode: string; color: string | null; size: string | null; size2: string | null }

/** Un modèle prêt pour la grille de l'éditeur, avec son fournisseur DÉDUIT (marque d'abord) et ses conditions. */
/** La marque d'un modèle, telle que le référentiel la connaît. */
export interface OrderBrand { id: string; code: string; label: string }

export interface OrderModel {
  modelKey: string;
  name: string;
  articleCode: string | null;
  family: string | null;
  brand: string | null;
  supplier: { id: string; label: string; terms: SupplierTerms } | null;
  /** La marque du référentiel — celle qu'on rattache quand aucun fournisseur ne se déduit. */
  brandRef: OrderBrand | null;
  lastPrice: LastPurchasePrice | null;
  /** Le prix d'achat de la FICHE — celui que l'éditeur de BL pré-remplit, une livraison n'étant pas une commande. */
  sheetPrice: number | null;
  sizeOrder: string[];
  dimension: SecondDimension | null;
  cells: OrderModelCell[];
}

/** Un résultat de la recherche de l'éditeur. */
export interface OrderModelHit {
  modelKey: string;
  name: string;
  articleCode: string | null;
  family: string | null;
  supplierLabel: string | null;
  brandLabel: string | null;
  colors: number;
  sizes: number;
  lastPrice: LastPurchasePrice | null;
  /** Le prix de la fiche — celui que l'éditeur de BL affiche. */
  sheetPrice: number | null;
  scanned: OrderModelCell | null;
}

/** Une commande de l'éditeur : les articles d'UN fournisseur et les conditions figées pour elle. */
export interface OrderGroupInput {
  draftNumber?: string | null;
  /** La version du brouillon lue à son ouverture — obligatoire avec `draftNumber`. */
  draftUpdatedAt?: number | null;
  /** Vide pour une marque sans fournisseur — le groupe porte alors `brandId`, et ne part qu'en brouillon. */
  supplierId: string;
  supplierLabel: string;
  brandId?: string | null;
  /** Le numéro proposé par l'écran, gardé ou changé par l'opérateur — le serveur le refuse s'il est pris, jamais ne le change. */
  number: string;
  /** La date de commande : le minuit LOCAL du jour choisi (époque, ms). */
  orderedOn: number;
  discountBp?: number | null;
  shippingCents?: number | null;
  earlyPaymentBp?: number | null;
  earlyPaymentDays?: number | null;
  lines: { gencode: string; orderedQty: number; unitPriceFine: number | null }[];
}

/** Un BL de l'éditeur : les articles reçus d'UN fournisseur, au réel, et la commande qu'il solde. */
export interface DeliveryGroupInput {
  /** Le numéro proposé par l'écran, gardé ou changé — refusé s'il est pris, jamais changé. */
  number: string;
  /** La date du BL : le minuit LOCAL du jour choisi (époque, ms) — l'entrée en stock est datée d'elle. */
  deliveredOn: number;
  /** Vide pour une marque sans fournisseur — le BL porte alors `brandId`, et ne s'intègre pas. */
  supplierId: string;
  supplierLabel: string;
  brandId?: string | null;
  /** La commande reprise par « Une commande ouverte » — absente : livraison à la volée. */
  orderRef?: string | null;
  lines: { gencode: string; qty: number; unitPriceFine: number | null }[];
}

/** Ce que le lot de BL a écrit — un BL par fournisseur, et ce que son entrée en stock a fait. */
export interface SavedDelivery {
  number: string;
  supplierLabel: string;
  integrated: boolean;
  pieces: number;
  piecesWithoutPrice: number;
  restoredModels: number;
}

/** Un BL rattaché, détaillé pour la modale de commande — source, état dérivé, progression. */
export interface BonRattache { number: string; source: string; etat: EtatDuBon; received: number; expected: number }

/** Un écart FIGÉ à l'intégration d'un BL rattaché : « attendu N, reçu M » (bandeau ambre). */
export interface EcartDeCommande { gencode: string; name: string | null; size: string | null; expected: number; checked: number }

export interface CommandeDetaillee extends CommandeResume {
  lines: LigneDeCommande[];
  deliveryNoteRows: BonRattache[];
  variances: EcartDeCommande[];
}

// ── Rétrocessions — le même document dans deux bases ; le sens et les gestes sont dérivés ─────────
export type EtatDeRetrocession = 'dem' | 'prep' | 'transit' | 'done' | 'ref' | 'anl';
export type NatureDeRetrocession = 'env' | 'dem';

export interface GestesRetro {
  accept: boolean; refuseRequest: boolean; ship: boolean; receive: boolean; refuseShipment: boolean;
  cancel: boolean; arbitrate: boolean;
}

export interface RetroResume {
  id: string; number: string;
  creatorGln: string; senderGln: string;
  /** `null` = destinataire HORS PLATEFORME (D38) — le document ne vit que chez l'émetteur. */
  receiverGln: string | null;
  external: boolean;
  senderLabel: string | null; receiverLabel: string | null;
  kind: NatureDeRetrocession; note: string | null;
  createdAt: number; createdBy: string;
  acceptedAt: number | null; shippedAt: number | null; receivedAt: number | null;
  refusedAt: number | null; refusedReason: string | null; cancelledAt: number | null;
  direction: 'out' | 'in';
  resume: { etat: EtatDeRetrocession; qty: number; received: number; variance: number; unresolvedVariances: number; valueFine: number | null };
  gestes: GestesRetro;
}

export interface LigneDeRetro {
  gencode: string; qty: number; checkedQty: number | null; receivedQty: number;
  unitPriceFine: number | null; known: boolean;
  varianceResolution: 'recount' | 'loss' | null;
  name: string | null; color: string | null; size: string | null;
}

export interface RetroDetaillee extends RetroResume { lines: LigneDeRetro[] }

// ── Retours fournisseur (D13/D39) — l'annulation d'un achat, servie au menu Rétrocessions ─────────
export type EtatDeRetourFournisseur = 'prep' | 'shipped' | 'cancelled';

export interface RetourFournisseurResume {
  id: string; number: string;
  supplierId: string | null; supplierLabel: string | null; note: string | null;
  createdAt: number; createdBy: string;
  shippedAt: number | null; cancelledAt: number | null;
  etat: EtatDeRetourFournisseur;
  qty: number; valueFine: number | null; lignesSansPrix: number;
  gestes: { ship: boolean; cancel: boolean; setPrice: boolean };
}

export interface LigneDeRetourFournisseur {
  gencode: string; qty: number; unitPriceFine: number | null;
  /** `purchase` = retrouvé dans les achats · `manual` = corrigé · null = aucun coût connu (dit). */
  priceSource: string | null; known: boolean;
  name: string | null; color: string | null; size: string | null;
}

export interface RetourFournisseurDetaille extends RetourFournisseurResume { lines: LigneDeRetourFournisseur[] }

// ── Centre d'écarts — tout est DÉRIVÉ côté serveur, l'écran ne recalcule rien ──
export type SourceDEcart = 'stockneg' | 'recep' | 'retro' | 'caisse';
// ── État du stock ────────────────────────────────────────────────────────────────────────────────
/** Une déclinaison en rayon. `pmpFine`/`valueCents` à `null` = NON VALORISABLE, jamais « vaut zéro ». */
export interface StockRow {
  gencode: string; articleCode: string; name: string;
  family: string | null; supplier: string | null; brand: string | null;
  color: string | null; size: string | null;
  qty: number; salePriceCents: number | null;
  pmpFine: number | null; valueCents: number | null;
  /** `null` pour un article qui n'a jamais bougé. */
  updatedAt: number | null; isActive: boolean;
  /** Au catalogue, jamais entré en stock : sa correction crée sa première entrée, valorisée au prix d'achat de la fiche. */
  neverMoved: boolean;
}
export interface StockSummary {
  references: number; atZero: number; negatives: number; pieces: number;
  valueCents: number;
  /** Combien de références on n'a PAS su valoriser — sans ce chiffre, le total ment par omission. */
  unvalued: number;
  /** Valorisations au prix de VENTE — HT déduit du taux de l'article, TTC tel qu'affiché. */
  valueSaleHtCents: number;
  valueSaleTtcCents: number;
}
export interface StockPage {
  rows: StockRow[];
  /** ⚠️ `null` sur un lot de continuation (`offset > 0`) — résumé, total et facettes vivent au
   * PREMIER lot ; une chaîne ne relit que `rows`. Pas des zéros : ils auraient l'air vrais. */
  summary: StockSummary | null;
  total: number;
  families: string[]; brands: string[];
}
/** Une case de grille : `qty: null` = cette taille n'existe PAS dans ce coloris (jamais « zéro »). */
export interface StockGridCell { size: string; gencode: string | null; qty: number | null; neverMoved: boolean }
export interface StockGridRow { name: string; color: string | null; cells: StockGridCell[]; total: number }
export interface StockGridSection { title: string; sizes: string[]; rows: StockGridRow[] }
/** Les axes du croisé — fermés côté serveur : l'écran ne peut pas en inventer un. */
export type StockPivotDimension = 'family' | 'brand' | 'size' | 'color' | 'season';
export type StockPivotMeasure = 'qty' | 'valuePmp' | 'valueSale';
export interface StockPivot {
  rowLabels: string[]; colLabels: string[]; cells: number[][];
  rowTotals: number[]; colTotals: number[]; grandTotal: number;
  /** Références que la mesure n'a pas su valoriser — le total le DIT au lieu de les taire. */
  unvalued: number;
}
export interface StockRemoteStore {
  gln: string; name: string; sections: StockGridSection[];
  /** Un magasin injoignable se DIT — il ne disparaît pas de la comparaison. */
  error: string | null;
}

/** Un croisement nommé, PARTAGÉ par le magasin (maquette : « partagées avec toute l'équipe »). */
export interface SavedStockView {
  name: string; rowDim: StockPivotDimension; colDim: StockPivotDimension;
  measure: StockPivotMeasure; createdBy: string;
}
/** Les colonnes triables de la liste — vocabulaire FERMÉ, comme côté serveur. */
export type StockSortKey = 'name' | 'qty' | 'family' | 'brand' | 'pv' | 'value';

export interface StockLedgerLine {
  id: string; createdAt: number; type: string; reason: string | null; note: string | null;
  qty: number; qtyAfter: number | null; by: string;
  /** La nature (badge) — calculée par le serveur avec le MÊME CASE que le journal : une seule vérité. */
  kind: MovementKind;
}
// ── Le JOURNAL des mouvements (écran `/mouvements`) ─────────────────────────────────────
/** Les cinq natures — vocabulaire FERMÉ, dérivé côté serveur de type+motif. */
export type MovementKind = 'vente' | 'rec' | 'trf' | 'inv' | 'adj';
export type MovementSortKey = 'at' | 'type' | 'art' | 'piece' | 'by' | 'q';
export interface MovementRow {
  id: string; createdAt: number; kind: MovementKind;
  gencode: string; articleName: string; articleCode: string;
  note: string | null; reason: string | null; by: string;
  /** SIGNÉE : + entrée / − sortie. */
  qty: number;
}
export interface MovementsPage {
  rows: MovementRow[];
  /** ⚠️ `null` sur un lot de continuation (`offset > 0`) — même convention que l'état du stock. */
  summary: { entered: number; exited: number; count: number; kinds: Record<MovementKind, number> } | null;
  total: number;
}
export interface MovementsQuery {
  q?: string; days?: number; kind?: MovementKind; direction?: 'in' | 'out';
  sort?: MovementSortKey; dir?: 'asc' | 'desc'; offset?: number;
}
/** Taille d'un lot du journal — miroir de `MOVEMENTS_PAGE_MAX` serveur. */
export const MOVEMENTS_PAGE_SIZE = 1000;

/** Motifs de correction — FERMÉS : un motif libre rend le grand livre illisible à l'analyse. */
export type StockCorrectionReason = 'inventory' | 'breakage' | 'theft' | 'manual_correction';

/** Une campagne d'inventaire, dans son cycle « préparée → en cours → clôturée ». */
export interface InventoryCampaign {
  id: string;
  label: string;
  /**
   * ⚠️ HUIT VALEURS DONT TROIS HÉRITÉES (`supplier`, `partial`, `spot`) : le wizard n'en propose que cinq, mais des
   * campagnes closes portent l'ancien vocabulaire — les retirer rendrait leur ligne d'historique illisible.
   * ⚠️ MIROIR DE `CAMPAIGN_KINDS` (serveur, `stock/inventory.ts`) : un type ajouté là-bas sans l'être ici arrive quand
   *    même, et l'écran le traite en inconnu — c'est ce qui était arrivé à `brand`.
   */
  kind: 'full' | 'rolling' | 'targeted' | 'brand' | 'sector' | 'supplier' | 'partial' | 'spot';
  scope: Record<string, string[] | undefined>;
  /** ⚠️ « abandonné » n'est pas « clôturé » : aucun mouvement écrit, aucun rapport à relire. */
  status: 'prepared' | 'running' | 'closed' | 'abandoned';
  /** Le moment où le THÉORIQUE a été figé — `null` tant que la campagne n'a pas démarré. */
  startedAt: number | null;
  closedAt: number | null;
  closedBy: string | null;
  /** ⚠️ `null` tant que la campagne n'est pas close — JAMAIS 0 : « pas encore chiffré » ≠ « écart nul ». */
  deltaValueCents: number | null;
  createdAt: number;
  createdBy: string;
  /**
   * LA DATE DE CLÔTURE VISÉE — informative, affichée au bandeau (SPEC §2/§3).
   * ⚠️ Chaîne ISO AAAA-MM-JJ, jamais un instant : une date de calendrier n’a pas d’heure, et un
   * horodatage basculerait d’un jour selon le fuseau de qui lit. Vide = pas de date visée.
   */
  targetDate: string;
  snapshotCount: number;
  countedCount: number;
  /** Références comptées JETÉES par un abandon — figé au moment du geste, 0 partout ailleurs. */
  discardedCount: number;
  /** Références en écart, arrêtées par le procès-verbal — `null` tant qu'il n'y en a pas. */
  varianceCount: number | null;
}

/** L'en-tête d'un procès-verbal SCELLÉ. */
/** Ce qu'une zone a compté, scellé avec le document. */
export interface InventoryReportZone {
  zone: string;
  refCount: number;
  qtyMinor: number;
  /** Les opérateurs qui y ont compté — une zone en compte parfois plusieurs. */
  countedBy: string;
}

export interface InventoryReportHeader {
  campaignId: string;
  label: string;
  kind: string;
  scope: Record<string, string[] | undefined>;
  startedAt: number | null;
  sealedAt: number;
  sealedBy: string;
  linesCount: number;
  countedCount: number;
  notCountedCount: number;
  excludedCount: number;
  varianceCount: number;
  zeroedCount: number;
  theoreticalQty: number;
  countedQty: number;
  deltaQty: number;
  deltaValueCents: number;
  /**
   * CE QUE VALAIT LE STOCK AVANT, ET CE QU'IL VAUT APRÈS — au coût, sur le périmètre.
   * ⚠️ `null` = CE DOCUMENT NE LES PORTAIT PAS (scellé avant leur existence). `0` dirait « stock sans
   * valeur », qui est une affirmation — l'écran doit répéter qu'il ne sait pas.
   */
  theoreticalValueCents: number | null;
  finalValueCents: number | null;
  /** La date de clôture VISÉE au lancement — vide si la campagne n'en portait pas. */
  targetDate: string;
  sizeInversions: { articleCode: string; overSize?: string; underSize?: string; qtyMinor: number }[];
  /** Le magasin a-t-il vendu pendant le comptage ? C'est la première question d'un contrôleur. */
  liveDuring: boolean;
}

/** Une ligne du procès-verbal, figée au scellement. */
export interface InventoryReportLine {
  gencode: string;
  articleCode: string;
  name: string;
  sizeLabel: string;
  theoreticalQtyMinor: number;
  theoreticalAtCount: number;
  countedQtyMinor: number | null;
  /**
   * CE QUI A ÉTÉ COMPTÉ DANS LA ZONE ACTIVE — et rien qu'elle ; null si aucune zone n'est choisie.
   * ⚠️ La saisie directe REMPLACE cette valeur-là, jamais le total : le champ affichait le total
   * toutes zones et écrivait dans la zone active, donc corriger un 6 en 8 depuis une autre zone
   * donnait 14 (défaut trouvé 2026-09-05).
   */
  countedInZoneMinor: number | null;
  deltaQtyMinor: number;
  deltaValueCents: number;
  /**
   * CE QUE VALAIT LE STOCK AVANT, ET CE QU'IL VAUT APRÈS — au coût, sur le périmètre.
   * ⚠️ `null` = CE DOCUMENT NE LES PORTAIT PAS (scellé avant leur existence). `0` dirait « stock sans
   * valeur », qui est une affirmation — l'écran doit répéter qu'il ne sait pas.
   */
  theoreticalValueCents: number | null;
  finalValueCents: number | null;
  /** La date de clôture VISÉE au lancement — vide si la campagne n'en portait pas. */
  targetDate: string;
  unitCostFine: number;
  excluded: boolean;
  excludedReason: string;
  uncertain: boolean;
}

/** Une zone scannée sur un Mobile Partner et soumise au serveur. */
export interface InventoryZone {
  id: string;
  label: string;
  mode: string;
  /** L'identifiant LOCAL de la personne sur le terminal — opaque, et ce n'est pas un nom d'appareil. */
  operatorId: string;
  /**
   * L'ÉQUIPEMENT QUI A COMPTÉ — son IDENTITÉ MAGASIN (« MP01 »), résolue par le serveur.
   * ⚠️ C'EST UN NOM, PAS DU MATÉRIEL : le n° de série appartient à l'inventaire du parc, qui vit à
   * la Console et n'en sort pas (SPEC tronc §1, « Identité MAGASIN d'un équipement »).
   * ⚠️ `null` EST UN CAS NORMAL : un appareil que nous n'avons pas fourni n'a pas de fiche à la
   * Console, donc pas de code. L'écran dit « appareil inconnu » et n'affiche jamais un identifiant
   * technique à la place d'un nom.
   */
  equipmentCode: string | null;
  /**
   * OUVERTE = le terminal a annoncé qu'il commençait ; rien n'est encore transmis.
   *
   * ⚠️ SON ABSENCE NE VEUT RIEN DIRE. L'annonce exige du réseau AU MOMENT de l'ouverture : un scanner
   * en réserve sans signal comptera très bien et ne s'annoncera jamais. Lire « aucune zone ouverte »
   * comme « personne ne compte » serait faux exactement quand quelqu'un travaille (§23.0).
   */
  open: boolean;
  /** Quand le terminal s'est annoncé — sert à PÉRIMER l'indicateur d'activité. */
  openedAt: number | null;
  submittedAt: number | null;
  lineCount: number;
  totalQtyMinor: number;
  /** Déjà versée dans les comptages de CETTE campagne. */
  attached: boolean;
  /** Codes-barres hors du périmètre figé : ils ne seront PAS versés, et c'est dit avant le geste. */
  outOfScope: number;
  /** Ouverte au clavier depuis le back-office : ni terminal, ni lignes à verser. */
  fromBackOffice: boolean;
  /** Pièces réellement apportées à l'inventaire — même source que la modale de détail (§5). */
  piecesCounted: number;
  /** Quand quelqu'un a DÉCLARÉ la zone terminée — `null` tant qu'on peut y revenir. */
  finishedAt: number | null;
}

/** Une ligne du rapprochement. */
export interface InventoryReconRow {
  gencode: string;
  articleCode: string;
  name: string;
  sizeLabel: string;
  /** Ce que la base disait AU DÉMARRAGE — figé. */
  theoreticalQtyMinor: number;
  /** Mouvements entre le démarrage et le PREMIER comptage : la correction « portes ouvertes ». */
  movementsBeforeCount: number;
  /** Le théorique RAMENÉ au moment du comptage — c'est LUI qui se compare au compté. */
  theoreticalAtCountMinor: number;
  /** ⚠️ Correction APPROXIMATIVE : compté dans plusieurs zones, avec du mouvement entre les deux. */
  uncertain: boolean;
  /** ⚠️ `null` = PAS ENCORE COMPTÉ, ce qui n'est pas « compté à zéro » — voir l'écran. */
  countedQtyMinor: number | null;
  /**
   * CE QUI A ÉTÉ COMPTÉ DANS LA ZONE ACTIVE — et rien qu'elle ; null si aucune zone n'est choisie.
   * ⚠️ La saisie directe REMPLACE cette valeur-là, jamais le total : le champ affichait le total
   * toutes zones et écrivait dans la zone active, donc corriger un 6 en 8 depuis une autre zone
   * donnait 14 (défaut trouvé 2026-09-05).
   */
  countedInZoneMinor: number | null;
  /**
   * CE QU'ON DOIT TROUVER EN RAYON MAINTENANT — compté + mouvements depuis le comptage.
   * ⚠️ AFFICHAGE PUR : n'entre NI dans l'écart NI dans la clôture. Elle répond à « que dois-je trouver
   * si je vais voir ? », pas à « y a-t-il de la démarque ? ».
   */
  expectedOnShelfMinor: number | null;
  /** EXCLU : il gardera son théorique à la clôture, au lieu d'être remis à zéro. */
  excluded: boolean;
  excludedReason: string;
  unitCostFine: number;
  deltaQtyMinor: number;
  deltaValueCents: number;
  /**
   * CE QUE VAUT LE THÉORIQUE DE CETTE LIGNE, au coût — PAS l’écart valorisé.
   * ⚠️ Sur une référence jamais bipée l’écart vaut zéro (rien n’a été comparé) alors que la clôture
   * s’apprête à remettre tout ce stock à zéro : c’est ce montant-là qui est en jeu.
   */
  theoreticalValueCents: number;
  /**
   * LE CLASSEMENT DE L’ARTICLE — ce par quoi on retrouve un rayon entier (§7).
   * ⚠️ LU AU CATALOGUE, PAS FIGÉ DANS LA PHOTO : ce qui est figé, c’est ce que l’inventaire COMPARE
   * (quantité et coût). Vide quand l’article a quitté le catalogue — la ligne reste, seule sa
   * poignée de rangement manque.
   */
  brand: string;
  family: string;
  familyColor: string;
  season: string;
}

/**
 * UNE VALEUR DE FILTRE ET SON POIDS — jamais une liste nue.
 * ⚠️ Le compte fait partie de l'option : sans lui, on essaie les marques une par une.
 */
export interface InventoryFacet {
  value: string;
  count: number;
  /** Repère visuel de la famille (hexadécimal) — absent pour les autres classements. */
  color?: string;
}

/**
 * CE QUI DÉSIGNE UN SOUS-ENSEMBLE DU RAPPROCHEMENT — la vue, la recherche, et les trois classements.
 *
 * ⚠️ UN SEUL TYPE POUR LA LECTURE ET POUR L'EXCLUSION EN MASSE, et c'est délibéré : ce qu'on affiche et
 * ce qu'on exclut doivent se désigner de la même façon, sinon le bouton « exclure tout ce qui est
 * filtré » finira par porter sur un autre ensemble que le tableau sous les yeux.
 */
export interface InventoryFilter {
  view?: string;
  q?: string;
  /**
   * LA ZONE OÙ L'ÉCRAN TRAVAILLE — elle ne filtre rien, elle renseigne chaque ligne sur ce que la
   * saisie directe y remplacera. ⚠️ Absente de l'exclusion en masse : on n'exclut pas « par zone ».
   */
  zone?: string;
  brand?: string;
  family?: string;
  season?: string;
}

/** Un résultat de « Retrouver un article » — l’article, et OÙ il a été compté. */
export interface InventorySearchRow {
  gencode: string;
  articleCode: string;
  name: string;
  /** Coloris et taille — ce qui distingue deux lignes de même libellé, donc ce qu’on cherche. */
  color: string;
  sizeLabel: string;
  theoreticalQtyMinor: number;
  /** Où, et combien. Vide = jamais compté nulle part. */
  zones: { zone: string; qtyMinor: number }[];
  excluded: boolean;
}

export interface InventorySearchResult {
  rows: InventorySearchRow[];
  /** Résultats écartés faute de place — dit, jamais tu. */
  truncated: number;
  /**
   * Zones en cours de comptage sur un appareil.
   * ⚠️ Sans ce nombre, « non compté » mentirait : un Mobile Partner qui compte n’a encore rien transmis,
   * et l’article est peut-être déjà dans sa besace.
   */
  countingZones: number;
}
export interface InventoryDetail {
  campaign: InventoryCampaign | null;
  /** LA PAGE demandée, jamais tout le rapprochement. */
  rows: InventoryReconRow[];
  /** Lignes retenues par la vue et le filtre, TOUTES pages confondues. */
  total: number;
  /** Le même `article_code` en excédent sur une taille et en manque sur une autre : artefact, pas perte. */
  sizeInversions: { articleCode: string; overSize?: string; underSize?: string; qtyMinor: number }[];
  totalDeltaValueCents: number;
  notCounted: number;
  /** Parmi les non comptées, celles qu'on a explicitement décidé de ne pas scanner. */
  excluded: number;
  /** Lignes dont la correction « portes ouvertes » est approximative — le doute se dit. */
  uncertain: number;
  /** Lignes en écart — compté sur TOUT, pour le badge d'onglet. */
  variances: number;
  /** Du stock a-t-il bougé pendant la campagne ? Sinon la correction est sans objet (magasin fermé). */
  liveDuringCampaign: boolean;
  /**
   * LES COMPTEURS DE TUILES, EN PIÈCES — jamais en nombre de lignes (SPEC-Inventaire §3, §5-§7).
   * ⚠️ « 12 écarts » ne dit pas s'il manque 12 pièces ou 1 200 ; « +38 / −412 » se lit d'un coup.
   */
  countedPieces: number;
  theoreticalPieces: number;
  surplusQty: number;
  missingQty: number;
  /** ⚠️ Les théoriques à ZÉRO n'y sont pas : une référence vide qu'on n'a pas vue n'est pas un manque. */
  notCountedPieces: number;
  /**
   * DE QUOI REMPLIR LES TROIS SÉLECTEURS DE « NON SCANNÉS » (marque, famille, saison).
   *
   * ⚠️ CHAQUE LISTE EST CALCULÉE SANS SON PROPRE FILTRE, mais AVEC les deux autres : après avoir choisi
   * une famille, les marques proposées sont celles qui ont quelque chose DANS cette famille. Aucune
   * option offerte ne mène donc à un tableau vide — et l'on peut toujours changer de valeur dans un
   * menu qu'on vient d'utiliser.
   */
  facets: { brand: InventoryFacet[]; family: InventoryFacet[]; season: InventoryFacet[] };
  /**
   * CE QUE LA CLÔTURE FERA — les chiffres de l’assistant, calculés sur l’ENSEMBLE.
   * ⚠️ Ils suivent la RÈGLE D’ÉCRITURE du serveur, pas une approximation d’affichage : l’assistant
   * les annonce à l’instant où l’on décide, donc ils doivent être exactement ce qui sera écrit.
   * ⚠️ « zeroingCount » diffère de « notCounted » : une référence jamais bipée dont le stock vivant
   * est déjà à zéro ne donne lieu à AUCUNE écriture.
   */
  closure: {
    matchedCount: number;
    varianceCount: number;
    zeroingCount: number;
    zeroingPieces: number;
    zeroingValueCents: number;
    excludedCount: number;
    excludedValueCents: number;
  };
  /**
   * Les zones de la campagne — back-office ET terminaux réunis par NOM, avec leurs pièces.
   * ⚠️ `state` ne dit que ce qu'on sait : `done` suppose une soumission de terminal, il n'existe
   * aucun geste « terminer une zone » au back-office.
   */
  countZones: { label: string; pieces: number; state: 'done' | 'counting' | 'todo' }[];
}

/**
 * L'article que ce brouillon double, tel que le SERVEUR l'a calculé.
 *
 * ⚠️ `certain` N'EST PAS UN NIVEAU DE CONFIANCE À NUANCER À L'ÉCRAN : `true` = même code-barres, c'est
 * un fait ; `false` = libellé proche, c'est une hypothèse. Les rendre du même ton ferait fusionner
 * deux articles réels sur une ressemblance de mots.
 */
export interface ArticleDraftDuplicate {
  articleId: string;
  gencode: string | null;
  name: string;
  salePriceInclTaxCents: number;
  certain: boolean;
  /** L'article est archivé : le rattacher le remet en service. */
  archived: boolean;
  /** POURQUOI ce rapprochement — une CLÉ fermée que l'écran nomme ; le serveur n'envoie plus de prose. */
  whyCode: 'gencode' | 'label-and-price' | 'label';
}

/** Un article créé en magasin, en attente de décision. Miroir de `DraftRow` (serveur). */
export interface ArticleDraft {
  id: string;
  /** `sale` = créé À LA VOLÉE pendant une vente (le client était devant) ; `menu` = préparé hors vente ;
   *  `mobile-partner` = créé depuis un terminal de stock, sur un gencode inconnu (spec Mobile Partner §8.6). */
  origin: 'sale' | 'menu' | 'mobile-partner';
  gencode: string | null;
  /** `supplier` = code bippé (identité mondiale) · `reserve` = code interne pré-alloué · `none` = aucun. */
  gencodeSource: 'supplier' | 'reserve' | 'none';
  name: string;
  salePriceInclTaxCents: number;
  vatRateBp: number;
  purchasePriceExclTaxFine: number | null;
  family: string | null;
  supplier: string | null;
  sizeGridCode: string | null;
  note: string | null;
  registerId: string;
  operatorName: string | null;
  /** Ventes DÉJÀ encaissées sur ce brouillon — refuser décide de l'assortiment, jamais de l'encaissement. */
  soldCount: number;
  createdAt: number;
  duplicate: ArticleDraftDuplicate | null;
}

/** Les corrections que le valideur apporte avant de faire entrer l'article. Champ absent = inchangé. */
export interface ArticleDraftPatch {
  name?: string;
  salePriceInclTaxCents?: number;
  vatRateBp?: number;
  purchasePriceExclTaxFine?: number;
  family?: string;
  supplier?: string;
}

export interface EcartRow {
  id: string;
  source: SourceDEcart;
  date: number;
  label: string;
  sub: string;
  /** Centimes signés ; null = valeur inconnue (pas de coût connu), dit à l'écran. */
  valueCents: number | null;
  status: 'todo' | 'done';
  reason: string | null;
  note: string | null;
  by: string | null;
  resolvedAt: number | null;
  gencode: string | null;
  qtyMissing: number | null;
  retroId: string | null;
  countId: string | null;
}
export interface EcartMotif { code: string; label: string; scope: string | null }

// ── Champs personnalisés — le référentiel définit (txt·num·date·bool·list), la fiche remplit ──
export type CustomFieldType = 'txt' | 'num' | 'date' | 'bool' | 'list';
export interface CustomField {
  id: string; label: string; fieldType: CustomFieldType;
  /** Valeurs possibles — ne porte que pour le type `list`. */
  options: string[];
  sortOrder: number;
}
/** Les valeurs d'une fiche : `{ <id de champ>: valeur }` — typées par le champ, validées serveur. */
export type CustomValues = Record<string, string | number | boolean>;

// ── Articles rapides — les tuiles de l'écran d'accueil de la caisse ──
export interface QuickLayout {
  id: string; name: string; isDefault: boolean;
  /** Les caisses rattachées — une caisse n'en a qu'une (clé primaire côté base). */
  registers: string[];
  tiles: number;
}

// ── Le studio des articles rapides — MIROIR de `catalog/quick-studio.ts` (serveur) ──
/** Les natures de tuile — celles du contrat, que la caisse sait dessiner (dossier, article, case vide, trois sources). */
export type StudioTileKind = QuickTileKind;
export interface StudioTile {
  id: string;
  /** `null` = l'accueil de la disposition ; sinon un DOSSIER ou une SOURCE de la même disposition. */
  parentId: string | null;
  kind: StudioTileKind;
  articleId: string | null;
  /** Le libellé du BOUTON — `null` = le nom de l'article ou de la source. Obligatoire pour un dossier. */
  label: string | null;
  w: 1 | 2;
  h: 1 | 2;
  /** La famille, la sous-famille ou la marque qu'une SOURCE déverse. */
  sourceId: string | null;
  /** Les articles qu'une source ne déverse plus (« −N »). */
  hidden: string[];
  /** L'ordre retouché du niveau d'une source (clés `t:<tuile>` / `a:<article>`). */
  order: string[];
  /** La teinte (clé de la charte) — `null` = la teinte par défaut. */
  color: QuickTileColor | null;
  /** Ce que la tuile montre au-dessus de son nom — `null` = le rendu par défaut de la caisse. */
  visual: QuickTileVisual | null;
  /** Le picto (nom du registre de la charte) — `null` = le picto par défaut. */
  icon: string | null;
  /** La photo posée au studio (adresse du stockage du BO) et son cadrage — `null` = aucune. */
  photo: StudioTilePhoto | null;
  /** La taille de la photo — `null` = normale. */
  photoSize: QuickPhotoSize | null;
  /** L'habillage que la tuile donne à SES ARTICLES — une source ou un préréglage ; tout `null` ailleurs. */
  pour: QuickTileLook;
}
/** Cadrage d'une photo de tuile : point focal en centièmes de pourcent (0..10 000), zoom en centièmes (100..300). */
export interface StudioTilePhoto { url: string; thumbUrl: string | null; x: number; y: number; zoom: number }
/** Une famille, une sous-famille ou une marque : son libellé et ce qu'elle déverse VRAIMENT (le compte réel). */
export interface StudioSource { kind: QuickSourceKind; id: string; label: string; active: boolean; count: number }
/** Ce qu'une source déverse, dans l'ordre de la caisse : TOUS ses identifiants, le détail des premiers, le compte réel. */
export interface StudioPour { total: number; ids: string[]; articles: StudioArticle[] }
/**
 * LES RÉGLAGES D'UNE DISPOSITION — colonnes, menu fixe, retour à l'accueil ; le visuel de tout l'écran et de tous ses
 * articles (`null` : rien de posé — « Petit picto » ; ils descendent aux caisses) ; le visuel des tuiles qu'on AJOUTERA
 * (`null` : « Petit picto » — il reste au serveur).
 */
export interface StudioSettings {
  cols: number | null; rail: boolean; home: boolean;
  visual: QuickTileVisual | null; articlesVisual: QuickTileVisual | null; newVisual: QuickTileVisual | null;
}
export interface StudioLayout {
  id: string;
  settings: StudioSettings;
  registers: string[];
  /** L'ordre du tableau EST l'ordre d'un niveau. */
  tiles: StudioTile[];
}
export interface StudioDocument { defaultLayoutId: string; layouts: StudioLayout[] }
/** Un article tel que la caisse l'appliquera — prix `null` = saisi en caisse, `active: false` = pas en caisse. */
/** Les durées sans vente que l'onglet Archivage propose — la liste du serveur. */
export const ARCHIVE_UNSOLD_MONTHS = [12, 18, 24, 36, 60] as const;
export type ArchiveUnsoldMonths = (typeof ARCHIVE_UNSOLD_MONTHS)[number];
/** Les garde-fous de l'archivage — allumés par défaut. */
export interface ArchiveGuards { stockZero: boolean; noOpenOrder: boolean; noPromotion: boolean }
/** Ce qui part aux archives. `seasons` VIDE = TOUTES les saisons ; `brand` `null` = toutes les marques. */
export interface ArchiveCriteria { unsoldMonths: ArchiveUnsoldMonths; seasons: string[]; brand: string | null; guards: ArchiveGuards }
/** Le catalogue d'aujourd'hui, et ce que les critères en retireraient. */
export interface ArchivePreview {
  activeModels: number; activeVariants: number; archivedModels: number;
  models: number; variants: number;
  /** Au-delà, l'écriture est refusée. */
  maxVariants: number;
}
/** Ce qu'un archivage ou une remise en service a réellement fait. */
export interface ArchiveStateChange { models: number; variants: number; stock: number; photos: number; unwritableModels: number }
/** Un modèle archivé, tel que la liste le montre. */
export interface ArchivedModel {
  modelKey: string; name: string; articleCode: string | null; variants: number;
  /** epoch ms */
  archivedAt: number;
  /** `AAAAMMJJHHMMSS` (heure du magasin), ou `null` s'il n'a jamais été vendu. */
  lastSale: string | null;
  family: string | null; season: string | null; brand: string | null;
}
export interface ArchivedPage { rows: ArchivedModel[]; total: number; page: number; pageSize: number }

/** Les critères écrits dans une adresse — le même vocabulaire que le serveur lit. */
function archiveQuery(c: ArchiveCriteria): string {
  const q = new URLSearchParams({ unsoldMonths: String(c.unsoldMonths) });
  for (const s of c.seasons) q.append('season', s);
  if (c.brand) q.set('brand', c.brand);
  for (const k of ['stockZero', 'noOpenOrder', 'noPromotion'] as const) q.set(k, c.guards[k] ? '1' : '0');
  return q.toString();
}

export interface StudioArticle {
  id: string; gencode: string | null; name: string; priceCents: number | null;
  /** `false` = désactivé OU archivé : la caisse ne dessine pas la tuile. */
  active: boolean;
  /** Archivé : l'article n'existe plus sur la caisse. */
  archived: boolean;
  family: string | null; familyColor: string | null;
  /** Ce que la vignette de la caisse affiche aussi : le code en badge, la marque, la taille. */
  articleCode: string | null; brand: string | null; size: string | null;
  /** La photo prise au COMPTOIR pour son coloris (vignette) — elle l'emporte sur tout en caisse ; `null` = aucune. */
  counterPhoto: string | null;
  /** Le visuel de FICHE qui sert en dernier recours (vignette) ; `null` = aucun. */
  catalogPhoto: string | null;
}
/** Un article proposé à l'ajout — une DÉCLINAISON : son coloris et son rayon s'ajoutent à ce que la tuile montre. */
export interface StudioCandidate extends StudioArticle {
  color: string | null; sector: string | null;
}
/** Les filtres de la fenêtre d'ajout — ils s'additionnent. */
export interface StudioSearch { q: string; sector: string; family: string; brand: string }
export interface StudioView {
  layouts: { id: string; name: string; isDefault: boolean }[];
  registers: string[];
  published: StudioDocument | null;
  draft: { document: StudioDocument; updatedAt: number | null; updatedBy: string | null } | null;
  /** Le brouillon enregistré ne se relit plus — dit, jamais ignoré. */
  draftUnreadable: string | null;
  revision: number;
  publishedAt: number | null;
  publishedBy: string | null;
  articles: Record<string, StudioArticle>;
  /** Les sources des tuiles, par `${nature}:${identifiant}` — libellé et compte réel. */
  sources: Record<string, StudioSource>;
  defaultMissing: boolean;
}
export interface StudioPublishReport {
  revision: number;
  tiles: { written: number; removed: number };
  displays: number;
  registersMoved: number;
  defaultChanged: boolean;
  republished: number;
}

/** Le réglage e-mail tel que l'écran le lit — le secret n'y figure jamais, seulement sa date. */
export interface MailSettings {
  transport: 'smtp' | 'mailjet' | 'none';
  fromEmail: string | null;
  fromName: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: boolean;
  smtpUser: string | null;
  apiKey: string | null;
  secretSetAt: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  /** Ce qui s'applique VRAIMENT — pas ce qui est enregistré. */
  effective: 'base' | 'env' | 'aucun';
  /** Pourquoi la base ne s'applique pas (secret illisible, réglage incomplet, envoi désactivé). */
  note?: string;
}

/** Ce qu'on envoie pour régler — le champ secret ABSENT = inchangé (jamais effacé par omission). */
export type MailSettingsInput = Omit<MailSettings, 'secretSetAt' | 'updatedAt' | 'updatedBy' | 'effective' | 'note'> & { secret?: string };

/**
 * LES CANAUX QU'UNE CAMPAGNE SAIT SERVIR — la MÊME liste que `marketing/consent.ts` côté serveur.
 *
 * ⚠️ UN SEUL TYPE POUR TOUT LE WEB, et ce n'est pas de la commodité : la liste était écrite DEUX fois
 * (campagne et modèle), et l'assistant en tenait une TROISIÈME sous un autre nom. Trois copies d'un
 * jeu fermé divergent au premier canal ajouté — c'est exactement ce qui est arrivé au wallet, dessiné
 * dans l'assistant et inconnu des deux autres.
 * ⚠️ `wallet_push` est le nom du SERVEUR, pas un mot d'écran : il voyage dans le corps de requête et
 * s'écrit en base. L'écran le TRADUIT pour l'afficher (`libelleCanal`), il ne le renomme jamais.
 */
export type CampaignChannel = 'email' | 'sms' | 'wallet_push';

/** Une campagne, telle que le back-office la rend. */
export interface Campaign {
  id: string;
  gln: string;
  name: string;
  channel: CampaignChannel;
  /** `draft` → `ready` → `sending` → `done` | `paused` | `failed`. */
  status: string;
  subject: string | null;
  body: string;
  senderLabel: string | null;
  recipientCount: number;
  /** Le curseur de reprise : combien de destinataires ont été TRAITÉS. */
  processed: number;
  sentCount: number;
  failedCount: number;
  /** Écartés parce qu'ils ont cessé d'être joignables PENDANT l'envoi — comptés, jamais effacés. */
  skippedCount: number;
  /** Le motif RÉEL d'un échec — l'écran l'affiche tel quel. */
  lastError: string | null;
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
}

/**
 * L'ACTIVITÉ RÉCENTE du magasin — le repère de tête de l'écran marketing.
 * ⚠️ Compte les messages RÉELLEMENT partis sur la fenêtre, une ligne de destinataire à la fois : les
 * compteurs cumulés d'une campagne n'ont pas de date et ne peuvent pas répondre « sur 30 jours ».
 */
/**
 * CE QUE LES DESTINATAIRES D'UNE CAMPAGNE ONT ACHETÉ après son départ.
 *
 * ⚠️ 🔴 CE N'EST PAS « LE CHIFFRE D'AFFAIRES GÉNÉRÉ PAR LA CAMPAGNE », et l'écran ne doit jamais
 * l'appeler ainsi. C'est celui DES DESTINATAIRES dans les jours qui suivent — une partie serait
 * arrivée sans rien envoyer. Ce que la campagne a CAUSÉ ne se saura qu'avec un groupe témoin.
 * ⚠️ `windowDays` VOYAGE AVEC LE CHIFFRE : sans elle, il ne se compare à rien, et l'écran doit
 * pouvoir l'ÉCRIRE plutôt que de la supposer.
 */
export interface CampaignAttribution {
  campaignId: string;
  windowDays: number;
  /** Les destinataires réellement SERVIS — le dénominateur de tout le reste. */
  served: number;
  buyers: number;
  sales: number;
  /** Somme ALGÉBRIQUE : un retour retranche, comme partout dans ce back-office. */
  ttcCents: number;
  /**
   * LE GROUPE TÉMOIN — `null` quand la campagne n'en a pas.
   * ⚠️ `null` ET « ZÉRO ACHETEUR » SONT DEUX CHOSES : sans témoin il n'y a rien à comparer, avec un
   * témoin où personne n'a acheté il y a une réponse — et c'est la plus nette qu'une campagne puisse
   * produire. Les confondre à l'écran effacerait un résultat.
   */
  holdout: { size: number; buyers: number; sales: number; ttcCents: number } | null;
  /** L'écart de taux d'acheteurs entre servis et témoins. `null` sans témoin, ou sans servi. */
  lift: CampaignLift | null;
}

/**
 * L'ÉCART AU TÉMOIN — le seul chiffre de cet écran qui parle de CAUSE.
 *
 * ⚠️ 🔴 IL SE CALCULE AU SERVEUR, ET L'ÉCRAN NE LE REFAIT JAMAIS. Une seconde définition d'un
 * intervalle de confiance divergerait de la première à la moindre correction, et personne ne verrait
 * laquelle des deux a raison — le dépôt a déjà payé ça sur la marge, avec trois formules concurrentes
 * pour le même article.
 * ⚠️ `conclusive` EST LA SEULE CHOSE QUE L'ÉCRAN A LE DROIT D'AFFIRMER. « +3,1 points » sur dix
 * témoins est du bruit qui a pris la forme d'un chiffre ; l'afficher sans sa réserve ferait relancer
 * une campagne sur une illusion.
 */
export interface CampaignLift {
  /** L'écart, en points de base (servis − témoins). Négatif si la campagne a fait MOINS bien. */
  pointsBp: number;
  /** Les bornes de l'intervalle de confiance à 95 %, en points de base. */
  lowBp: number;
  highBp: number;
  conclusive: boolean;
  /** `too_few` : pas assez de monde. `inconclusive` : assez, mais l'écart ne se distingue pas de zéro. */
  reason: 'too_few' | 'inconclusive' | null;
}

/**
 * UNE AUDIENCE ENREGISTRÉE — des CRITÈRES qu'on rejoue, jamais une liste de gens.
 * ⚠️ Aucun canal : c'est la campagne qui décide par quel canal elle part, et le consentement se relit
 * au moment de l'envoi. Les mêmes « Gold dormants » servent un SMS et un e-mail.
 */
export interface SavedAudience {
  id: string;
  name: string;
  filters: CampaignFilters;
  createdAt: number;
  updatedAt: number;
}

/**
 * UN MODÈLE D'ENVOI AUTOMATIQUE — il n'est pas une campagne, il en ENGENDRE.
 * ⚠️ Chaque tour produit une campagne NEUVE, avec son compte rendu, sa fenêtre d’attribution et son
 * propre tirage de témoin. Rejouer une même campagne accumulerait les trois.
 */
export interface CampaignTemplate {
  id: string;
  name: string;
  channel: CampaignChannel;
  subject: string | null;
  body: string;
  senderLabel: string | null;
  filters: CampaignFilters;
  holdoutPct: number;
  recurrence: CampaignRecurrence;
  isActive: boolean;
  /** Prochaine échéance, instant absolu. ⚠️ `null` = rien de prévu (suspendu, ou envoi unique parti). */
  nextDueAt: number | null;
  lastRunAt: number | null;
  lastCampaignId: string | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * QUAND un modèle part — trois formes, une seule structure.
 * ⚠️ `hour`/`minute` SONT EN HEURE LOCALE DU MAGASIN, et `date` est une date locale : c’est le
 * serveur qui les convertit avec `store.timezone`. Envoyer un instant depuis le navigateur ferait
 * dépendre l’envoi du fuseau de celui qui a cliqué.
 */
export interface CampaignRecurrence {
  kind: 'once' | 'weekly' | 'monthly';
  /** `weekly` : 1 = lundi … 7 = dimanche · `monthly` : les quantièmes · `once` : vide. */
  days: number[];
  hour: number;
  minute: number;
  /** `once` seulement : la date locale `AAAA-MM-JJ`. */
  date?: string;
}

export interface CampaignActivity {
  sentTotal: number;
  windowDays: number;
}

export interface CampaignInput {
  name: string;
  channel: string;
  subject?: string | null;
  body: string;
  senderLabel?: string | null;
}

/** Les critères d'audience EN PLUS du consentement, qui n'est jamais optionnel. */
export interface CampaignFilters {
  qualifications?: string[];
  withLoyaltyBalance?: boolean;
  /** Segments RFM visés — les noms viennent du serveur, l'écran n'en invente aucun. */
  segments?: string[];
  /** Paliers retenus. « Gold et plus » est RÉSOLU EN LISTE par l'écran : l'ordre est un choix produit. */
  tierIn?: string[];
  /**
   * MOIS D'ANNIVERSAIRE RETENUS (1–12) — une LISTE, pas un mois.
   * ⚠️ Sans elle, couvrir un trimestre demande TROIS campagnes : trois messages à rédiger, trois
   * audiences à figer, et trois comptes rendus à recouper pour savoir ce que l'ensemble a donné.
   * ⚠️ Une fiche SANS date de naissance n'entre jamais, quel que soit le mois demandé.
   */
  birthMonths?: number[];
  /** Cagnotte minimale, en centimes. */
  minLoyaltyCents?: number;
  /**
   * LA PÉRIODE OBSERVÉE — achats, panier, visites et « a acheté » s'y lisent. Absente : toute la vie du client.
   * `purchasePeriod` est le RACCOURCI relatif (« 12 derniers mois »), gardé tel quel et rejoué à chaque usage ;
   * `purchaseFrom`/`purchaseTo` des dates choisies (AAAAMMJJ). Le serveur ne garde que l'un des deux.
   */
  purchasePeriod?: CampaignPurchasePeriod;
  purchaseFrom?: string;
  purchaseTo?: string;
  /** Achats minimaux sur la période, en centimes — même définition du CA que la fiche client. */
  minCaCents?: number;
  /** Panier moyen minimal sur la période, en centimes — arrondi comme sur la fiche client. */
  minAvgBasketCents?: number;
  /** Nombre de visites sur la période, bornes incluses. */
  visitsMin?: number;
  visitsMax?: number;
  /** A acheté — ET entre dimensions, OU entre les valeurs d'une dimension. */
  bought?: { dim: CampaignBoughtDimension; values: string[] }[];
  /** Venu au moins une fois dans cette fenêtre (AAAAMMJJ). */
  visitedFrom?: string;
  visitedTo?: string;
  /** Pas venu depuis cette date (AAAAMMJJ) — la relance des dormants. */
  notVisitedSince?: string;
  /** La même dormance en relatif (« depuis 6 mois ») — rejouée à chaque usage. */
  notVisitedFor?: CampaignRelativeSpan;
  /** Fiche créée depuis cette date (AAAAMMJJ) — ou depuis une durée relative. */
  createdSince?: string;
  createdWithin?: CampaignRelativeSpan;
  /** Codes postaux par préfixe (un département) OU villes — un seul critère. */
  postalPrefixes?: string[];
  cities?: { city: string; cp: string }[];
  /** Détient un titre en cours de l'un de ces types (`avoir`, `cadeau`, `fidelite`, `coupon`). */
  holdsTitleKinds?: string[];
  /** « Sauf ceux qui… » — un seul de ces critères suffit pour être écarté ; même période que l'inclusion. */
  exclude?: Omit<CampaignFilters, 'exclude' | 'purchasePeriod' | 'purchaseFrom' | 'purchaseTo'>;
}

/** Les durées relatives d'un critère daté — `'6m'` = six mois, recalculé le jour où l'audience sert. */
export type CampaignRelativeSpan = '1m' | '3m' | '6m' | '12m' | '24m';
/** La période observée en relatif — les N derniers mois, cette année, l'année dernière. */
export type CampaignPurchasePeriod = CampaignRelativeSpan | 'ytd' | 'ly';
/** Les dimensions de « a acheté » — celles des filtres des statistiques. */
export type CampaignBoughtDimension = 'marq' | 'fam' | 'sup' | 'sais';

/** Un lieu des fiches du magasin — ce que propose le critère « Ville / code postal ». */
export interface CampaignPlace {
  cp: string;
  city: string | null;
  clients: number;
}

/** Le solde d'un canal, tel que le back-office le lit chez l'opérateur. */
export interface CampaignCredits {
  channel: string;
  /**
   * Solde disponible, EN CHAÎNE (Esendex rend cinq décimales).
   * ⚠️ `null` = non mesuré, JAMAIS zéro : le motif est dans `unavailable`.
   */
  available: string | null;
  reserved: string | null;
  /** Le compte qui sera débité, et d'où il vient. */
  account: string | null;
  source: 'store' | 'platform' | null;
  provider: string | null;
  /** Pourquoi il n'y a rien à montrer — motif RÉEL, jamais un tiret muet. */
  unavailable: string | null;
  readAt: number;
}

/** Le solde couvre-t-il l'envoi ? ⚠️ `unknown` n'est PAS `ok`. */
export interface CreditVerdict {
  verdict: 'ok' | 'short' | 'unknown';
  availableMessages: number | null;
  missing: number | null;
}

export interface CampaignReport {
  sent: number;
  failed: number;
  skipped: number;
  processed: number;
  /** Où reprendre — le rang du prochain destinataire non traité. */
  cursor: number;
  /** Vrai si l'envoi s'est arrêté avant la fin : la campagne reste reprenable. */
  interrupted: boolean;
}

/**
 * LE RÉGLAGE DE MESSAGERIE D'UN MAGASIN, tel qu'il redescend à l'écran.
 *
 * ⚠️ PAS DE SECRET ICI, et ce n'est pas un oubli : la lecture ne rend que « défini le … ». Un champ
 * pré-rempli, même masqué, serait un mot de passe rendu au navigateur.
 * ⚠️ `effective` dit ce qui S'APPLIQUE, pas ce qui est enregistré — les deux diffèrent dès qu'un
 * réglage est incomplet, et c'est cet écart qui fait chercher pendant une heure.
 */
export interface StoreMailSettings {
  transport: 'none' | 'smtp' | 'mailjet';
  fromEmail: string | null;
  fromName: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: boolean;
  smtpUser: string | null;
  apiKey: string | null;
  /** Quand le secret a été posé — jamais le secret lui-même. */
  secretSetAt: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  /** ⚠️ `env` est IMPOSSIBLE pour un magasin : le repli sur le transport de la plateforme est coupé. */
  effective: 'base' | 'env' | 'aucun';
  /** Pourquoi la base ne s'applique pas (réglage incomplet, secret illisible, envoi désactivé). */
  note?: string;
}

/** Ce qu'on envoie en écriture. ⚠️ `secret` ABSENT = on conserve celui en place. */
export interface StoreMailInput {
  transport: 'none' | 'smtp' | 'mailjet';
  fromEmail?: string; fromName?: string;
  smtpHost?: string; smtpPort?: number; smtpSecure?: boolean; smtpUser?: string;
  apiKey?: string; secret?: string;
}

/**
 * UN COMPTE DE CAISSE, tel que le serveur le rend.
 * ⚠️ AUCUN SECRET : `hasPin` dit qu'il EN A un, jamais lequel. Un code à quatre chiffres identifie à
 * lui seul en caisse — le rendre au navigateur distribuerait les identités du magasin.
 */
export interface RosterOperator {
  id: string;
  name: string;
  initials: string;
  /** Le modèle du magasin qu'il suit ; `null` sur un compte jamais réglé au siège — on ne lui invente pas un modèle. */
  template: string | null;
  /** Ce qui s'écarte de son modèle. */
  overrides: Record<string, 'no' | 'ask' | 'yes'>;
  scan: boolean;
  active: boolean;
  /** Pièces scellées à son nom — la mesure qui pèse dans une désactivation. */
  sales: number;
  hasPin: boolean;
  /** Compte d'administration éditeur : montré, jamais modifiable d'ici. */
  readOnly: boolean;
}

/** Un modèle de droits du magasin — un des trois de l'éditeur, ou créé par le magasin. */
export interface RightsTemplateView {
  id: string;
  /** Le modèle de l'éditeur dont il part. */
  base: 'vend' | 'resp' | 'ger';
  /** Un des trois de l'éditeur : il se rétablit, il ne se supprime pas. */
  editor: boolean;
  /** Le nom donné par le magasin ; `null` = celui de l'éditeur, à traduire. */
  label: string | null;
  values: Record<string, 'no' | 'ask' | 'yes'>;
  /** Les droits réglés autrement que chez l'éditeur. */
  changed: string[];
  /** Plafonds de remise en points de base : sans motif, avec motif. */
  discount: { freeBp: number; maxBp: number };
  discountChanged: boolean;
  /** Comptes actifs qui le suivent. */
  accounts: number;
  /** Comptes qui le suivent, désactivés compris. */
  followers: number;
  canApprove: boolean;
  /** Le magasin y a changé quelque chose (un modèle de l'éditeur peut alors se rétablir). */
  stored: boolean;
}

export interface RosterView {
  operators: RosterOperator[];
  templates: RightsTemplateView[];
  gln: string;
}

/** Ce qu'on change sur un modèle — un champ absent ne change pas. */
export interface RightsTemplateChange {
  label?: string | null;
  rights?: Record<string, 'no' | 'ask' | 'yes'>;
  discount?: { freeBp: number; maxBp: number };
}

/**
 * UN COMPTE WEB DU BACK-OFFICE — à ne pas confondre avec un opérateur de CAISSE.
 * ⚠️ AUCUN SECRET : ni mot de passe, ni empreinte, ni jeton. `twoFactor` dit qu'elle est armée,
 * jamais avec quoi.
 */
export interface AccessAccount {
  id: string;
  email: string;
  name: string;
  /** `admin` · `operator` · `orgazone_staff` — les TROIS que la base porte réellement. */
  role: string;
  roleLabel: string;
  /** `invited` · `active` · `disabled`. */
  status: string;
  twoFactor: boolean;
  /** Epoch ms, ou `null` — « jamais connecté » n'est pas une date vide. */
  lastLoginAt: number | null;
  createdAt: number;
  /** Ses magasins DANS votre périmètre — ceux d'un autre client ne sont jamais nommés. */
  stores: { gln: string; name: string | null }[];
  /** Combien de magasins il ouvre HORS de votre périmètre : un tel compte ne se modifie pas d'ici. */
  outsideStores: number;
  /** Compte de l'ÉDITEUR : montré, jamais modifiable depuis un back-office client. */
  editorStaff: boolean;
  localPassword: boolean;
}

export interface AccessView {
  accounts: AccessAccount[];
  /** L'appelant — l'écran doit savoir ce QU'IL peut faire, sinon il offre ce que le serveur refuse. */
  me: { id: string; role: string };
  /** ⚠️ Faux quand aucun lien ne peut partir : inviter créerait un compte que personne n'activerait. */
  canInvite: boolean;
  inviteBlockedReason: string | null;
}

/**
 * LA TOPOLOGIE D'UN MAGASIN — sa fiche, ses caisses déclarées, ses groupes d'affichage.
 *
 * ⚠️ `registers` et `groups` sont NULLABLES, et les deux nuls ne disent pas « c'est vide » : ils
 * disent « on n'a pas pu lire ». Un magasin jamais synchronisé n'a pas de registre d'appareils ; un
 * tableau vide y affirmerait « aucune caisse », ce qui ne se répare pas au même endroit (§23.0).
 */
export interface StoreTopology {
  /** `phone` en E.164 (l'écran le groupe pour le lire), `null` = jamais renseigné ; `email` de même. */
  store: { gln: string; legalName: string | null; address: string | null; phone: string | null; email: string | null };
  /** Quand le dernier réglage a été enregistré (epoch ms) — `null` = plus au journal, ou illisible. */
  settingsSavedAt: number | null;
  registers: StoreRegisterRow[] | null;
  /** Les terminaux de stock (Mobile Partner) — `null` = lecture impossible ; vide = aucun. */
  terminals: StoreTerminalRow[] | null;
  groups: RegisterGroup[] | null;
}

/** Un terminal de stock : une machine du parc qui parle au siège sans être une caisse. */
export interface StoreTerminalRow {
  deviceId: string;
  /** Le code d'équipement posé par la Console (« MP01 »), `null` tant qu'elle ne l'a pas déclaré. */
  equipmentCode: string | null;
  family: string | null;
  /** Dernier battement de synchronisation, `null` = jamais vu. */
  lastSyncAt: number | null;
}

/** Une caisse déclarée au registre du magasin. */
export interface StoreRegisterRow {
  /** Le n° de caisse — l'identité, que la machine qui le porte peut changer. */
  registerId: string;
  deviceId: string | null;
  firstSeenAt: number | null;
  /** Dernier battement de synchronisation, `null` = jamais vue. */
  lastSyncAt: number | null;
  /**
   * A-t-elle reçu le dernier réglage enregistré ? Jugé par le serveur, seul à savoir lire sa position ;
   * `null` = le journal n'a pas pu être lu, on ne juge pas.
   */
  settings: 'up_to_date' | 'pending' | 'never' | null;
}

/** Un raccourci d'affichage du back-office — jamais descendu aux caisses. */
export interface RegisterGroup {
  id: string;
  name: string;
  registerIds: string[];
  createdBy: string;
  createdAt: number;
}

/**
 * LES HORAIRES DU MAGASIN, et la bascule fiscale qu'ils COMMANDENT.
 *
 * ⚠️ UN SEUL RÉGLAGE SAISI : les horaires. `cut` est DÉRIVÉ (`deriveDayCutoff`, core-nf525) et rendu
 * en lecture — le saisir à part ferait deux valeurs pour le même fait, qui divergeraient au premier
 * oubli sans que l'écart se voie nulle part.
 */
export interface StoreHours {
  /** `null` = jamais déclarés — la caisse applique alors la convention comptable (bascule 00:00). */
  open: string | null;
  close: string | null;
  /** La bascule EN VIGUEUR à l'instant de la lecture, jamais la valeur brute en base. */
  cut: string;
  /** Fermeture après minuit : c'est elle qui explique une bascule décalée, l'écran la DIT. */
  night: boolean;
  /** La bascule qui attend la prochaine journée fiscale, `null` si aucune. */
  cutPending: string | null;
  /** Échéance de cette attente (AAAAMMJJHHMMSS) — le prochain Z. */
  effectiveAt: string | null;
}

/**
 * LE CALENDRIER D’EXPLOITATION D’UN MAGASIN, tel que le serveur le rend.
 * ⚠️ `timezoneSet` / `openDaysSet` DISENT SI LE RÉGLAGE EST POSÉ — `timezone` et `openDays` valent
 * TOUJOURS quelque chose (le repli). Sans ces deux drapeaux, l’écran afficherait « Europe/Paris »
 * pour un magasin qui n’a jamais rien choisi, et personne ne corrigerait un réglage qui a l’air fait.
 */
export interface StoreCalendar {
  timezone: string;
  timezoneSet: boolean;
  /** 1 = lundi … 7 = dimanche, triés. */
  openDays: number[];
  openDaysSet: boolean;
}

/** Une archive telle que le registre la connaît — jamais ses octets. */
export interface FiscalArchive {
  archiveId: string; storeGln: string; seq: number;
  periodKind: string; periodFrom: string; periodTo: string; constitutedAt: string;
  manifestHash: string; carryOver: string; prevSignature: string; signature: string;
  keyId: string; fileCount: number;
}

/** Ce que la vérification de la chaîne a constaté. */
export interface FiscalArchivesView {
  archives: FiscalArchive[];
  chain: { ok: boolean; checked: number; breaks: { index: number; reason: string }[]; badSignatures: { seq: number; keyId: string }[]; missingSeq: number[] };
  /** ⚠️ Le SEUL contrôle qui voie une troncature en QUEUE : les deux chaînes ne se coupent pas au même endroit. */
  crossCheck: { ok: boolean; archivesSansEvenement: string[]; evenementsSansArchive: string[] };
  keys: { keyId: string; publicKey: string; algo: string; isActive: boolean }[];
}

/** Le rapport du contrôle d'intégrité — les fautes sont en français, telles qu'affichées. */
export interface ArchiveIntegrity { archiveId: string; ok: boolean; faults: string[] }

/** Le battement d'une caisse, tel que le poste de conformité le rend. */
export interface RegisterLiveness {
  registerId: string;
  /** Dernier contact (epoch ms), ou `null` si la caisse n'a JAMAIS parlé. */
  lastSeenAt: number | null;
  channels: number;
  /**
   * ⚠️ `null` N'EST PAS `false` : « hors ligne » se dit d'une caisse qui a travaillé puis s'est tue.
   * Une caisse déclarée mais jamais vue est un autre fait — celui qu'on cherche le jour de sa mise en
   * service. L'écran doit rendre TROIS états, pas deux.
   */
  online: boolean | null;
}

/** Ce que le constat de chaîne dit d'UNE caisse. */
export interface RegisterChainHealth {
  registerId: string;
  pieces: number;
  lastPieceNumber: string | null;
  lastSeqNo: number;
  lastDatetimeFiscal: string | null;
  missingSeq: number[];
  missingSeqCount: number;
  brokenLinks: number;
  firstBrokenSeq: number | null;
  /** Têtes de chaîne au milieu de la caisse, datées par un JET 220 — une tablette remplacée, en règle mais à DIRE. */
  declaredRestarts: number;
  payloadMismatch: number;
  ok: boolean;
}

/**
 * Un mois qu'une caisse a travaillé sans que le siège en ait la clôture.
 *
 * ⚠️ 🔴 DEUX ÉTATS, JAMAIS UN SEUL « EN RETARD ». `awaiting` = la caisse n'a rien scellé depuis la fin
 * du mois, donc la clôture « par l'usage » n'a pas eu sa chance : rien n'est cassé, mais la période
 * n'est ni archivable ni exportable. `overdue` = la caisse a continué de sceller APRÈS la fin du mois
 * et la clôture manque quand même. Les fondre ferait clignoter l'écran le 1er de chaque mois.
 */
export interface MissingClosure {
  month: string;
  registerId: string;
  periodId: string;
  pieces: number;
  state: 'awaiting' | 'overdue';
  /** Le dernier mois où cette caisse a scellé — c'est lui qui EXPLIQUE l'état. */
  lastSealedMonth: string;
  monthsSince: number;
}

/** Une pièce dont l'horodatage scellé RECULE par rapport au rang précédent de la même caisse. */
export interface SequenceRecoil {
  registerId: string;
  seqNo: number;
  pieceNumber: string | null;
  datetimeFiscal: string;
  previousDatetimeFiscal: string;
}

/**
 * Une pièce dont l'horodatage scellé s'écarte de l'horloge BRUTE de la caisse.
 * ⚠️ Le SIGNE porte le sens : **négatif** = l'horodatage scellé est en avance sur l'horloge, donc la
 * ligne de flottaison a corrigé un RECUL (le fait qu'on cherche) ; **positif** = l'horloge a pris de
 * l'avance après la lecture de l'heure de confiance, ou le scellement a duré très longtemps.
 */
export interface ClockGap {
  registerId: string;
  seqNo: number;
  pieceNumber: string | null;
  datetimeFiscal: string;
  gapSeconds: number;
}

/** L'état des horloges des caisses d'un magasin. */
export interface StoreClockHealth {
  /** Pièces JUGÉES : celles que nous avons signées. Les reprises n'entrent pas. */
  pieces: number;
  /**
   * 🔴 L'ÉCART A-T-IL PU ÊTRE MESURÉ ? `false` quand le magasin n'a pas de fuseau déclaré.
   * ⚠️ « pas mesuré » N'EST PAS « zéro anomalie » : l'écran doit l'écrire, pas afficher un vert.
   */
  clockGapChecked: boolean;
  /** Le seuil employé, RENDU : l'écran n'en invente pas un second. */
  toleranceSeconds: number;
  recoils: SequenceRecoil[];
  recoilCount: number;
  gaps: ClockGap[];
  gapCount: number;
  ok: boolean;
}

/** Une notification du centre du back-office. */
export interface NotificationItem {
  id: string;
  /** Le magasin concerné, ou `null` quand le fait concerne le compte (réglage, intégration…). */
  storeGln: string | null;
  /**
   * 🔴 LA FAMILLE DU FAIT (ex. `fiscal.closure_overdue`) — et c’est L’ÉCRAN qui en fait une phrase.
   * ⚠️ 🔴 LE SERVEUR N’ENVOIE AUCUN TEXTE. Une phrase rendue par le serveur ne passe par AUCUN
   * catalogue de traduction : elle s’afficherait en français à un compte anglais, et aucune garde du
   * web ne pourrait le voir puisque la chaîne n’y vit pas. Même partage que `whyCode` pour les
   * doublons d’articles : le serveur conclut, l’écran nomme.
   * ⚠️ Typée `string` et non l’union fermée : une ligne écrite par un déploiement plus récent que ce
   * bundle doit rester LISIBLE (l’écran la rend en clair), plutôt que de faire mentir le type.
   */
  kind: string;
  severity: 'info' | 'warn' | 'critical';
  /** Ce que le fait porte — l’écran y puise les valeurs de sa phrase. */
  params: Record<string, unknown>;
  /** Où aller pour agir — un chemin d’écran, jamais une URL absolue ; DÉRIVÉ par le serveur. */
  href: string | null;
  /** Combien de fois le fait a été republié : au-delà de 1, il DURE. */
  occurrences: number;
  createdAt: string;
  lastSeenAt: string;
  /** Posé quand le producteur constate que la condition a cessé. */
  resolvedAt: string | null;
  /** `true` si CET utilisateur l'a lue — « lu » est personnel, le fait ne l'est pas. */
  read: boolean;
}

/** Ce que la cloche affiche. */
/** Ce qu’un producteur a couvert lors de son dernier passage. */
/** Un gabarit d’e-mail de la bibliothèque du magasin. */
export interface EmailTemplateRow {
  id: string;
  /** Ce que le commerçant relit pour choisir — UNIQUE par (magasin, usage). */
  name: string;
  purpose: string;
  subject: string;
  title: string | null;
  body: string;
  /** Les deux moitiés, ou rien : le cœur partagé REFUSE un bouton sans texte ou sans adresse sûre. */
  button: { label: string; url: string } | null;
  updatedAt: number;
}

/** Un usage de gabarit, tel que le serveur le déclare. */
export interface EmailTemplatePurpose {
  purpose: string;
  /** Les jetons que cet usage accepte, avec ce qu’ils valent — un jeton sans explication ne se choisit pas. */
  fields: EmailTemplateField[];
  /**
   * 🔴 UN ENVOI CONSOMME-T-IL DÉJÀ CE GABARIT ?
   * ⚠️ `false` n’interdit PAS d’écrire : il oblige l’écran à DIRE que rien ne part encore. Le taire
   * ferait croire qu’un courrier est envoyé (§23.0) ; l’interdire empêcherait d’en préparer le texte.
   * ⚠️ Le serveur rend le FAIT, l’écran écrit la phrase — une phrase rendue par le serveur ne passe
   * par aucun catalogue de traduction.
   */
  wired: boolean;
  /**
   * Le message part-il HABILLÉ de notre enveloppe (carte, titre, pied) — ou nu, tel que le commerçant l’écrit ?
   * ⚠️ L’écran en déduit le champ « Titre de la carte » et la forme de l’aperçu ; il ne le devine pas d’après
   * le nom de l’usage.
   */
  dressed: boolean;
}

/** Un jeton d’un usage — ce qu’il vaut, ce qui part quand il manque, s’il est exigé, et son exemple. */
export interface EmailTemplateField {
  /** Le jeton TEL QU’IL S’ÉCRIT, accolades comprises. */
  token: string;
  /** ⚠️ Une phrase FRANÇAISE du serveur : l’écran la résout par `t()` (le français est la clé). */
  description: string;
  /** Ce qui part quand la valeur manque — vide = le fragment disparaît. */
  fallback: string;
  /** Le gabarit doit-il le citer ? Refusé à l’écriture sinon. */
  required: boolean;
  /** Ce que l’aperçu « client exemple » met à sa place. */
  example: string;
}

/** Ce qu’un écran envoie pour créer ou modifier un gabarit. */
export interface EmailTemplateInput {
  name: string;
  purpose: string;
  subject: string;
  title: string | null;
  body: string;
  button: { label: string; url: string } | null;
}

export interface NotificationSweep {
  kind: string;
  lastRunAt: string;
  storesSeen: number;
  /** Magasins qu’on n’a PAS pu examiner — « rien à signaler » ne vaut pas pour eux. */
  storesBlocked: number;
}

export interface NotificationsView {
  items: NotificationItem[];
  /**
   * Le compte de la pastille — les notifications OUVERTES et non lues, et elles seules.
   * ⚠️ JAMAIS un nombre inventé : un compteur de notifications se lit d'un coup d'œil et se croit sur
   * parole (§23.0). Une notification close n'y entre pas, sinon rien ne le ferait descendre.
   */
  unread: number;
  /**
   * 🔴 Le centre n’a-t-il JAMAIS rien reçu ? — un FAIT, pas une phrase.
   * ⚠️ Un centre vide est AMBIGU : « aucune alerte » et « personne ne surveille » s’affichent pareil,
   * et seul le second est vrai aujourd’hui (§23.0) — d’où DEUX états vides à l’écran.
   * ⚠️ C’est « jamais rien reçu », PAS « vide maintenant » : dérivé de la liste, le fait ferait
   * ré-annoncer « personne ne surveille » à un producteur qui a tout RÉSOLU.
   */
  unfed: boolean;
  /**
   * 🔴 LES PASSAGES DES PRODUCTEURS — ce qui permet à un centre VIDE de dire quelque chose de VRAI.
   * ⚠️ Un « rien à signaler » nu vaut ce que vaut la surveillance derrière : si des magasins n’ont pas
   * pu être examinés, la phrase est fausse pour eux, et l’écran doit le dire (§23.0).
   */
  sweeps: NotificationSweep[];
}

/** L'état de la chaîne fiscale du magasin actif. */
/** Ce qu'on a pu établir de la signature d'une pièce — la règle du cœur (`core-nf525`, `signature-verdict`). */
export type SignatureVerdictView = 'valide' | 'invalide' | 'cle_absente' | 'temoin_dev' | 'hors_perimetre' | 'pont_absent';
/** Les colonnes du siège disent-elles ce que la charge signée dit ? `unverifiable` = on n'a pas pu conclure. */
export type PayloadOutcomeView = 'match' | 'mismatch' | 'unverifiable';

/** La vérification des signatures d'un magasin, telle que le serveur l'a établie (`fiscal/signature-check.ts`). */
export interface SignatureHealthView {
  /** Clés publiques reçues des caisses. Zéro : aucune signature ne peut encore être prouvée. */
  keys: number;
  /** Pièces pas encore vérifiées — « pas vérifié » n'est jamais « valide ». */
  pending: number;
  registers: { registerId: string; signatures: Record<SignatureVerdictView, number>; payload: Record<PayloadOutcomeView, number> }[];
  /** Les pièces qui accusent, bornées à vingt — `faultCount` dit le reste. */
  faults: { registerId: string; seqNo: number; pieceNumber: string | null; signature: SignatureVerdictView; payload: PayloadOutcomeView; field: string | null }[];
  faultCount: number;
}

export interface FiscalHealthView {
  /** Le seuil d'« en ligne », RENDU : l'écran n'en invente pas un second. */
  onlineAfterMinutes: number;
  registers: RegisterLiveness[];
  chain: {
    registers: RegisterChainHealth[];
    /** Pièces de REPRISE — comptées à part, jamais jugées : leur chaîne n'est pas la nôtre. */
    legacyPieces: number;
    /** Pièces dont l'origine est INCONNUE — à regarder, jamais à taire. */
    unknownAlgoPieces: number;
    ok: boolean;
  };
  archives: { archiveId: string; seq: number; periodKind: string; periodFrom: string; periodTo: string; constitutedAt: string }[];
  /** La vérification des signatures — ou le motif RÉEL pour lequel elle ne se lit pas. */
  signatures: SignatureHealthView | { unavailable: string };
  /** Les clôtures mensuelles que le siège n'a pas — un CONSTAT : le back-office ne clôture rien. */
  closures: {
    /** Le mois courant en `AAAAMM`, RENDU : l'écran n'en calcule pas un second. */
    currentMonth: string;
    /** Le fuseau employé, et s'il a été POSÉ — « choisi » ne se corrige pas comme « par défaut ». */
    timezone: string;
    timezoneSet: boolean;
    missing: MissingClosure[];
  };
  /** L'état des horloges — deux constats de nature différente, dont un qui peut ne pas avoir eu lieu. */
  clock: StoreClockHealth;
  /** 🔴 Ce que ce constat NE prouve PAS — rendu par le serveur, jamais rédigé ici. */
  limits: string[];
}

export const api = {
  // ── Auth (socle @orgazone/auth-web) ──
  authMode: () => req<AuthMode>('GET', '/auth/mode'),
  me: () => req<{ user: SessionUser }>('GET', '/auth/me'),
  login: (email: string, password: string, remember: boolean) =>
    req<{ user?: SessionUser; status?: string }>('POST', '/auth/login', { email, password, remember }),
  logout: () => req<{ ok: true }>('POST', '/auth/logout'),
  // ⚠️ `forgot` répond TOUJOURS 200 (il ne révèle pas si l'adresse existe) : l'écran ne peut donc PAS
  // affirmer qu'un e-mail est parti.
  forgotPassword: (email: string) => req<{ ok: true }>('POST', '/auth/password/forgot', { email }),
  resetPassword: (token: string, password: string) => req<{ ok: true }>('POST', '/auth/password/reset', { token, password }),
  /** Le parcours de réinitialisation est-il praticable sur CE serveur ? (route publique du BO). */
  recoveryStatus: () => req<RecoveryStatus>('GET', '/auth/password/recovery'),
  /** Mot de passe local ou non, sa date, la longueur exigée — ce que « Mon compte » montre et annonce. */
  accountSecurity: () => req<AccountSecurity>('GET', '/auth/security'),
  /**
   * Change le mot de passe LOCAL. ⚠️ Le serveur ferme les AUTRES sessions et garde celle-ci ; il refuse en
   * `invalid_credentials` (mot de passe actuel faux) ou en `password_policy` (règle non respectée).
   */
  changePassword: (current: string, password: string) => req<{ ok: true }>('POST', '/auth/password/change', { current, password }),
  /** Les sessions ouvertes de ce compte. */
  mySessions: () => req<{ sessions: AccountSession[] }>('GET', '/auth/sessions'),
  /** Ferme une session de ce compte — l'appareil devra se reconnecter. */
  closeSession: (id: string) => req<{ ok: true }>('DELETE', `/auth/sessions/${encodeURIComponent(id)}`),

  // ── Surface humaine gardée ──
  stores: () => req<AuthorizedStore[]>('GET', '/admin/stores'),
  /** Suis-je un membre du staff Orgazone entré chez un client, et dans quel magasin ? */
  crossTenant: () => req<CrossTenantContext>('GET', '/admin/cross-tenant'),
  /** Le journal des accès au back-office, cloisonné par le serveur au périmètre de la session. */
  accessJournal: () => req<AccessEvent[]>('GET', '/admin/access-journal'),

  // ── Contexte magasin (bandeau + sélecteur) ──
  /** Magasin actif, magasins ouverts et droit d'en changer — en UNE lecture, donc sans état transitoire. */
  storeContext: () => req<StoreContextView>('GET', '/admin/store-context'),
  /**
   * Choisit le magasin actif. ⚠️ Le SEUL appel qui envoie un GLN — et le serveur le vérifie contre
   * `bo_user_store` avant de l'écrire. Un refus porte son motif ; il se rend par le canal ALERTE.
   */
  chooseStore: (gln: string) => req<StoreContextView>('PUT', '/admin/store-context', { gln }),

  // ── Catalogue ──
  /** Une page de modèles du magasin ACTIF. Tri et pagination sont faits par le serveur. */
  articles: (p: ArticleQueryParams) => req<ArticlePageView>(
    'GET',
    `/admin/articles?${new URLSearchParams({
      page: String(p.page), size: String(p.size), sort: p.sort, dir: p.dir,
      ...(p.q ? { q: p.q } : {}),
      ...(p.fam ? { fam: p.fam } : {}), ...(p.sup ? { sup: p.sup } : {}),
      ...(p.sais ? { sais: p.sais } : {}), ...(p.sect ? { sect: p.sect } : {}),
      // ⚠️ `marq` MANQUAIT ICI (audit 2026-08-25) : le filtre Marque s'affichait en puce, le compteur
      //    annonçait « N modèles correspondent aux filtres posés », et le serveur listait le catalogue
      //    ENTIER. Le pire n'était pas la liste — c'est que l'ÉCRITURE DE MASSE, elle, envoie bien
      //    `marq` : on écrivait donc sur un ensemble que l'écran n'avait jamais montré.
      ...(p.marq ? { marq: p.marq } : {}),
      ...(p.actif && p.actif !== 'all' ? { actif: p.actif } : {}),
      // ⚠️ LE FILTRE STOCK EST SERVEUR, JAMAIS NAVIGATEUR : filtré sur la page reçue, il ne
      //    cacherait que les lignes des 60 premiers résultats et laisserait croire que le catalogue
      //    n'a que ceux-là en stock — en annonçant un total qui ne décrit plus la liste.
      ...(p.inStock ? { stock: '1' } : {}),
      // ⚠️ MÊME RÈGLE QUE LE STOCK : filtré au serveur, jamais sur la page reçue.
      ...(p.secondAxis ? { axes: '2' } : {}),
      // ⚠️ LA CLÉ DU LOT, JAMAIS SES ARTICLES : des milliers d'identifiants ne tiennent pas dans une adresse.
      ...(p.importBatch ? { imp: p.importBatch } : {}),
    }).toString()}`,
  ),
  // ── Réglages d'écran de la personne ──
  /** Ce que j'ai réglé sur ce tableau. Objet VIDE = jamais réglé, ce qui est l'état du premier jour. */
  tablePrefs: (tableId: string) => req<TablePrefs>('GET', `/admin/me/table-prefs/${encodeURIComponent(tableId)}`),
  /** Enregistre le réglage et rend CE QUI A ÉTÉ RETENU (les bornes du serveur s'appliquent). */
  saveTablePrefs: (tableId: string, prefs: TablePrefs) =>
    req<TablePrefs>('PUT', `/admin/me/table-prefs/${encodeURIComponent(tableId)}`, { prefs }),

  /**
   * ⚠️ ROUTE SÉPARÉE du tableau de bord, parce qu'elle appelle un service EXTÉRIEUR : mêlée aux
   * indicateurs, elle ferait attendre le chiffre d'affaires derrière la disponibilité d'un tiers.
   */
  weather: () => req<WeatherView>('GET', '/admin/dashboard/weather'),

  /** Le plan comptable du magasin — postes à renseigner, saisies en place, état de complétude. */
  accountingPlan: () => req<PlanComptableView>('GET', '/admin/accounting/plan'),

  /**
   * Les écritures d'un mois (`AAAA-MM`), UNE PAR JOURNÉE FISCALE.
   * ⚠️ Elles se calculent MÊME sur un plan incomplet : sans aperçu, on remplirait douze comptes à
   * l'aveugle sans jamais voir l'effet. C'est la REMISE qui est interdite, pas la lecture.
   */
  accountingEntry: (month: string) =>
    req<EntryView>('GET', `/admin/accounting/entry?month=${encodeURIComponent(month)}`),

  /** Les formats offerts à ce magasin — le livré d'abord, puis les siens. */
  accountingFormats: () => req<{ formats: FormatDisponible[] }>('GET', '/admin/accounting/formats'),

  /** Les exports déjà produits — c'est le compteur qui prévient avant un rejeu. */
  accountingExports: (month?: string) =>
    req<{ exports: ExportPasse[] }>(
      'GET', `/admin/accounting/exports${month ? `?month=${encodeURIComponent(month)}` : ''}`,
    ),

  /**
   * PRODUIT LE FICHIER d'un mois et le rend au navigateur.
   * ⚠️ En `POST` bien qu'il rende un fichier : chaque production laisse une trace, et un `GET` qu'un
   * navigateur peut rejouer en préchargeant un lien gonflerait le compteur sans que personne ne clique.
   * ⚠️ Passe par `download` de la primitive PARTAGÉE : un `fetch` à la main repartirait de zéro sur le
   * jeton CSRF, le cookie de session et la politique 401.
   */
  accountingExportFile: (month: string, format: string) =>
    client.download('POST', '/admin/accounting/export', { month, format }),
  /** Le FEC réglementaire — plage de mois (du = au : un mois ; janvier→décembre : l'exercice). */
  accountingFecFile: (du: string, au: string) =>
    client.download('POST', '/admin/accounting/fec', { du, au }),
  /** État du connecteur Pennylane (la clé ne voyage jamais vers le navigateur). */
  // Connecteur wallet du MAGASIN ACTIF — la cle n'en sort jamais, seulement son etat et le verdict
  // d'un appel reel ("posee" ne veut pas dire "acceptee").
  // Emet la carte wallet d un client. Le serveur verifie la fiche PUIS appelle le fournisseur, et
  // retient l identite rendue — c est elle qui portera gains, coupons et notifications.
  // Programme de fidelite : reglages EN BASE (une cle absente n est PAS rendue) + mesures mesurees.
  /**
   * LES RÉGLAGES DE CAISSE du magasin actif — catalogue JOINT aux valeurs.
   * ⚠️ `value: null` = JAMAIS posé (la caisse applique son repli), à ne pas confondre avec « vide ».
   */
  /**
   * LES ARCHIVES FISCALES du magasin actif — la chaîne, son verdict, le recoupement, les clés.
   * ⚠️ Le verdict est RECALCULÉ par le serveur à chaque lecture : une santé mémorisée ne dirait
   *    rien de l'altération d'hier.
   */
  fiscalArchives: () => req<FiscalArchivesView>('GET', '/admin/fiscal-archives'),
  /**
   * LE POSTE DE CONFORMITÉ NF525 — battement des caisses, constat de chaîne, archives inscrites.
   * ⚠️ `limits` vient du SERVEUR : ce que le constat ne prouve PAS (l'intégrité du contenu,
   * l'authenticité des signatures) ne se rédige pas à l'écran — deux écrans en écriraient deux listes,
   * et celle qui oublierait une ligne serait la plus rassurante.
   */
  // ── Centre de notifications ──
  /**
   * Ce que la cloche affiche : les notifications du périmètre, le compte non lu, et ce que le centre
   * dit de lui-même. ⚠️ Le périmètre est calculé par le SERVEUR — aucun GLN ne part d'ici.
   */
  /**
   * LA BIBLIOTHÈQUE DE GABARITS DU MAGASIN ACTIF.
   * ⚠️ LE MAGASIN VIENT DE LA SESSION : on ne l’envoie jamais, et le serveur ne le lirait pas.
   */
  emailTemplates: (purpose?: string) => req<{ rows: EmailTemplateRow[] }>(
    'GET', purpose ? `/admin/email-templates?purpose=${encodeURIComponent(purpose)}` : '/admin/email-templates'),
  /**
   * LES USAGES, leurs jetons et leur état de branchement.
   * ⚠️ L’écran ne peut pas les deviner : c’est le serveur qui sait quels champs un usage accepte.
   */
  emailTemplatePurposes: () =>
    req<{ rows: EmailTemplatePurpose[] }>('GET', '/admin/email-templates/purposes'),
  /** Crée un gabarit. ⚠️ 422 nommé si le nom est pris ou si un jeton n’est pas déclaré par l’usage. */
  createEmailTemplate: (input: EmailTemplateInput) =>
    req<{ record: EmailTemplateRow }>('POST', '/admin/email-templates', input),
  /** Modifie un gabarit. ⚠️ 404 si la ligne n’existe plus — l’écran ne doit pas afficher un
   *  enregistrement qui n’a pas eu lieu. */
  updateEmailTemplate: (id: string, input: EmailTemplateInput) =>
    req<{ record: EmailTemplateRow }>('PATCH', `/admin/email-templates/${encodeURIComponent(id)}`, input),
  /**
   * Supprime un gabarit.
   * ⚠️ `removed: false` n’est PAS une erreur : il n’y avait déjà plus rien à retirer (double clic,
   * deux onglets). L’écran sait ainsi s’il vient d’agir ou de constater.
   */
  deleteEmailTemplate: (id: string) =>
    req<{ removed: boolean }>('DELETE', `/admin/email-templates/${encodeURIComponent(id)}`),
  notifications: () => req<NotificationsView>('GET', '/admin/notifications'),
  /** Marquer lues des notifications précises. Le serveur ré-applique le périmètre à l'écriture. */
  markNotificationsRead: (ids: string[]) => req<{ marked: number }>('POST', '/admin/notifications/read', { ids }),
  /** Tout marquer lu — les OUVERTES du périmètre, celles que la pastille compte. */
  markAllNotificationsRead: () => req<{ marked: number }>('POST', '/admin/notifications/read-all'),
  fiscalHealth: () => req<FiscalHealthView>('GET', '/admin/fiscal-health'),
  /** Vérifier un lot de signatures du magasin actif ; rend ce qu'il reste. */
  verifyFiscalSignatures: () =>
    req<{ checked: number; rechecked: number; pending: number }>('POST', '/admin/fiscal-health/signatures/verify'),
  /** Constituer l'archive d'une période. Rejouable : la même période rend l'archive déjà inscrite. */
  constituteArchive: (kind: 'mensuelle' | 'annuelle', from: string, to: string) =>
    req<{ archive: FiscalArchive; jet: { lineNo: number; code: number } }>('POST', '/admin/fiscal-archives', { kind, from, to }),
  /** Contrôler l'intégrité d'une archive (§6.11.4). ⚠️ POST : l'échec s'inscrit au JET. */
  verifyArchive: (id: string) =>
    req<ArchiveIntegrity>('POST', `/admin/fiscal-archives/${encodeURIComponent(id)}/verify`),
  /**
   * Récupérer une archive — un ZIP des fichiers scellés, à conserver hors de chez nous.
   *
   * ⚠️ POST, comme `/verify` : le serveur CONTRÔLE l'intégrité avant de remettre, et trace la sortie
   * au JET 440. Un GET serait déclenché par un préchargement du navigateur.
   * ⚠️ `client.download` décode l'enveloppe d'ERREUR : un refus remonte comme partout ailleurs,
   * au lieu d'être enregistré sur le disque de l'utilisateur sous forme de fichier de message.
   */
  downloadArchive: (id: string) =>
    client.download('POST', `/admin/fiscal-archives/${encodeURIComponent(id)}/download`),
  storeSettings: () => req<{ settings: StoreSettingRow[] }>('GET', '/admin/store-settings'),
  /** La topologie du magasin actif — fiche, caisses déclarées, groupes d'affichage. */
  storeTopology: () => req<StoreTopology>('GET', '/admin/store-topology'),
  /** Les horaires du magasin et la bascule qu'ils commandent. */
  storeHours: () => req<StoreHours>('GET', '/admin/store-hours'),
  /** Enregistrer les horaires — la bascule en découle, et attend la prochaine journée si elle change. */
  saveStoreHours: (open: string, close: string) => req<StoreHours>('PUT', '/admin/store-hours', { open, close }),
  /**
   * LE CALENDRIER DES ENVOIS — fuseau et jours d’ouverture.
   * ⚠️ 🔴 SANS FUSEAU, AUCUNE CAMPAGNE NE PART : le repli `Europe/Paris` protège le reste de la
   * plateforme, jamais un envoi — le message partirait à l’heure d’un autre pays, et l’envoi
   * RÉUSSIRAIT. C’est `timezoneSet` qui dit si le réglage est POSÉ ; `timezone` vaut toujours
   * quelque chose, donc le tester reviendrait à ne rien tester.
   */
  storeCalendar: () => req<StoreCalendar>('GET', '/admin/store-calendar'),
  saveStoreCalendar: (timezone: string, openDays: number[]) =>
    req<StoreCalendar>('PUT', '/admin/store-calendar', { timezone, openDays }),
  /** Créer un groupe VIDE : on nomme d'abord, on range ensuite. Rend la topologie à jour. */
  createRegisterGroup: (name: string) => req<StoreTopology>('POST', '/admin/register-groups', { name }),
  /** Ranger ou sortir une caisse — une bascule, idempotente des deux côtés. */
  setRegisterGroupMember: (groupId: string, registerId: string, member: boolean) =>
    req<StoreTopology>('PUT', '/admin/register-groups/member', { groupId, registerId, member }),
  deleteRegisterGroup: (id: string) =>
    req<StoreTopology>('DELETE', `/admin/register-groups/${encodeURIComponent(id)}`),
  /**
   * ⚠️ CLÉ FRAPPÉE ICI, comme pour la fidélité : le serveur dédoublonne par identifiant sur toute
   *    l'histoire du journal — une clé dérivée du CONTENU ferait qu'un réglage reposé sur une valeur
   *    déjà portée n'écrirait rien, avec un bandeau vert (incident 2026-08-31).
   */
  saveStoreSettings: (settings: Record<string, string>) =>
    req<{ settings: StoreSettingRow[] }>('PUT', '/admin/store-settings', {
      settings, idempotencyKey: cleDeGeste('set:store', Date.now()),
    }),
  /**
   * LE DICTIONNAIRE DU MAGASIN ACTIF — chaque champ d'article avec le mot de la plateforme, celui du
   * métier (descendu par la Console), la surcharge du commerçant et le mot que les écrans affichent.
   */
  storeVocabulary: () => req<StoreVocabularyView>('GET', '/admin/store-vocabulary'),
  /**
   * MA LANGUE — `null` pour revenir à celle de la plateforme.
   * ⚠️ Sous `/admin/me`, donc gardée en lecture : un opérateur en lecture seule choisit sa langue.
   *    Pas de clé de geste ici — reposer la même langue est sans effet et sans trace, contrairement
   *    à un réglage de caisse qui, lui, descend au journal du magasin.
   */
  setMyLang: (lang: string | null) => req<{ lang: string | null }>('PUT', '/admin/me/lang', { lang }),
  /**
   * Poser les mots du commerçant — une valeur vide ou blanche RETIRE la surcharge (le résolveur la
   * traite comme absente). Rend la vue à jour ET le vocabulaire résolu, pour que la SPA parle le nouveau
   * mot sans recharger le contexte magasin.
   * ⚠️ CLÉ FRAPPÉE ICI, comme pour les réglages de caisse : le serveur dédoublonne par identifiant sur
   *    toute l'histoire du journal — une clé dérivée du CONTENU ferait qu'un mot reposé à une valeur
   *    déjà portée n'écrirait rien, avec un bandeau vert. Le paramètre n'existe que pour qu'un appelant
   *    qui REJOUE un geste (réseau coupé au milieu) garde SA clé — jamais pour en fabriquer une stable.
   */
  saveStoreVocabulary: (
    overrides: Partial<Record<VocabularyFieldKey, string>>,
    idempotencyKey: string = cleDeGeste('vocab:store', Date.now()),
  ) =>
    req<StoreVocabularyView & { vocabulary: Vocabulary }>('PUT', '/admin/store-vocabulary', { overrides, idempotencyKey }),
  loyaltySettings: () => req<{
    settings: Record<string, string>;
    mesures: {
      circulationCents: number | null; cardHolders: number | null;
      creditedCents30d: number | null; redeemedCents30d: number | null;
    };
  }>('GET', '/admin/loyalty'),
  // ⚠️ CLÉ FRAPPÉE ICI, pas à l'écran : c'est l'endroit qu'on ne peut pas contourner. Sans elle, le
  //    serveur dérivait sa clé de rejeu du CONTENU — reposer un réglage sur une valeur déjà portée
  //    ne faisait plus rien, avec un bandeau vert (audit 2026-08-31). Le pourquoi est sur `cleDeGeste`.
  saveLoyaltySettings: (settings: Record<string, string>) =>
    req<{ settings: Record<string, string> }>('PUT', '/admin/loyalty', {
      settings, idempotencyKey: cleDeGeste('fid:settings', Date.now()),
    }),
  /**
   * Les retours qui n'ont pas défait la fidélité de leur vente — la liste, puis le geste.
   * ⚠️ LE GESTE NE PORTE QUE LES RETOURS MONTRÉS : le serveur rejoue la règle au moment d'écrire, et ne corrige jamais
   *    une ligne que l'exploitant n'a pas vue.
   */
  returnLoyaltyRepairs: () => req<{ repairs: ReturnLoyaltyRepair[] }>('GET', '/admin/loyalty/return-repairs'),
  repairReturnLoyalty: (returnIds: string[]) => req<ReturnLoyaltyRepairReport>('POST', '/admin/loyalty/return-repairs', { returnIds }),
  setClientWallet: (id: string, active: boolean) =>
    req<{ active: boolean; alreadyExists?: boolean; providerClientId?: string }>(
      'PUT', '/admin/clients/wallet', { id, active }),
  integrationWallet: () => req<{
    canSeal: boolean; token: 'absent' | 'illisible' | 'ok'; apiUrl: string | null;
    sonde: { ok: boolean; detail: string } | null;
    // Les derniers echanges REELS avec le fournisseur — c'est ce qu'on lit pour diagnostiquer.
    exchanges: {
      at: number; method: string; path: string; status: number | null; ok: boolean; durationMs: number; error: string | null;
      /** Le client visé par l'appel, résolu par le serveur. `null` = l'échange ne désigne personne. */
      client: { id: string; name: string; card: string | null } | null;
    }[];
    // Recap MESURE — chaque champ a null quand la donnee n est pas connue, jamais 0.
    recap: {
      cardsIssued: number | null; notifications30d: number | null; pendingBalances: number | null;
      oldestPendingAt: number | null; lastExchangeAt: number | null;
    };
    // Les 4 derniers caracteres de la cle — juste de quoi reconnaitre laquelle est en place.
    tokenTail: string | null;
  }>('GET', '/admin/integrations/wallet'),
  integrationWalletKey: (key: string) =>
    req<{ ok: boolean; detail: string }>('PUT', '/admin/integrations/wallet/key', { key }),
  /**
   * Les gabarits de coupon du compte Eldowallet de ce magasin.
   * ⚠️ `ok: false` N'EST PAS UNE PANNE mais un ÉTAT — wallet non branché, cle refusee — et son motif
   * vient avec : l'écran le DIT au lieu d'afficher une liste vide qu'on prendrait pour « aucun gabarit ».
   */
  integrationWalletCouponTemplates: () =>
    req<{ ok: boolean; templates: { id: string; name: string }[]; error: string | null }>(
      'GET', '/admin/integrations/wallet/coupon-templates',
    ),
  accountingPennylane: () => req<PennylaneEtat>('GET', '/admin/accounting/pennylane'),
  /** Pose la clé API Pennylane — sondée côté serveur avant d'être scellée. */
  accountingPennylaneKey: (key: string) => req<{ ok: boolean; detail: string }>('PUT', '/admin/accounting/pennylane/key', { key }),
  /** Dépose le mois en brouillons — compte rendu par journée. */
  accountingPennylaneDeposit: (month: string) => req<PennylaneDepot>('POST', '/admin/accounting/pennylane/deposit', { month }),

  /** Les valeurs offertes par les filtres — mesurées sur le CATALOGUE entier, jamais sur la vue. */
  /** L'aperçu de la modification de masse — MÊME transformation que l'écriture, côté serveur. */
  massPricePreview: (p: { filters: Record<string, string>; change: MassChange; rounding: MassRounding }) =>
    req<MassPreview>('POST', '/admin/articles/mass-price/preview', p),
  /** L'écriture — verrou d'append, garde d'instant asOf, tout-ou-rien (même parenthèse que /articles/scope). */
  massPriceApply: (p: { scope: { filters: Record<string, string>; excluded: string[]; asOf: number }; change: MassChange; rounding: MassRounding; idempotencyKey: string }) =>
    req<MassApplyReport>('POST', '/admin/articles/mass-price/apply', p),
  /** L'état des soldes — fenêtres légales, campagne en base, éligibilité, aperçu (suit les % tapés).
   *  `excl` absent = le serveur repart des exclusions de la campagne en base. */
  soldesState: (season: 'hiver' | 'ete', marks: number[], excl?: ExclusionSoldes[]) =>
    req<SoldesState>('GET', `/admin/soldes?season=${season}&marks=${marks.join(',')}${excl ? `&excl=${encodeURIComponent(JSON.stringify(excl))}` : ''}`),
  /** Active (ou met à jour) la campagne — les dates sont LÉGALES, calculées serveur, jamais envoyées.
   *  ⚠️ TOUJOURS renvoyer les exclusions voulues : l'activation écrit LE jeu complet (une exclusion
   *  omise est RETIRÉE des caisses — c'est le diff, pas un oubli toléré). */
  soldesActivate: (p: { season: 'hiver' | 'ete'; marks: number[]; exclusions: ExclusionSoldes[] }) =>
    req<{ promotions: number; eteintes: number; retirees: number }>('POST', '/admin/soldes', p),
  /** Éteint la campagne — is_active à 0, jamais une suppression (les ventes passées gardent leur prix). */
  soldesOff: () => req<{ eteintes: number }>('POST', '/admin/soldes/off', {}),
  /** Pose (ou retire, `clear`) les dates fixées par arrêté d'une saison — remplace la règle nationale. */
  soldesDates: (p: { season: 'hiver' | 'ete'; year?: number; start?: string; end?: string; clear?: true }) =>
    req<{ saison: 'hiver' | 'ete'; arrete: DatesArretees | null }>('POST', '/admin/soldes/dates', p),
  // ── MESSAGERIE SORTANTE DU BACK-OFFICE (2026-08-30) ──────────────────────────────────────────
  // Le secret ne redescend JAMAIS : la lecture ne rend que « défini le … ». On le remplace, on ne
  // le relit pas.
  mailSettings: () => req<MailSettings>('GET', '/admin/settings/mail'),
  updateMailSettings: (body: MailSettingsInput) => req<MailSettings>('PUT', '/admin/settings/mail', body),
  /** Éprouve le réglage pour de vrai — le champ error porte le motif RÉEL du transport. */
  testMail: (to: string) => req<{ ok: boolean; error?: string; effective: string }>('POST', '/admin/settings/mail/test', { to }),

  /**
   * Les valeurs choisissables de chaque dimension, en ordre ALPHABÉTIQUE.
   *
   * ⚠️ SANS LE NOMBRE DE MODÈLES PAR VALEUR, et c'est le défaut : mesuré sur un catalogue réel
   * (173 347 articles), **2 319 ms avec les comptes, 182 ms sans** — pour un chiffre qu'aucun
   * sélecteur n'affichait. Le coût n'est pas la lecture des valeurs mais le comptage des modèles, qui
   * oblige à parcourir tout le catalogue.
   * ⚠️ `counts: true` LE REDEMANDE, et trie alors du plus porté au moins porté. Réservé aux emplois où
   * un poids par valeur a un sens — les STATISTIQUES —, jamais pour peupler un sélecteur.
   */
  articleFacets: (opts?: { counts?: boolean }) => req<ArticleFacets>(
    'GET', `/admin/articles/facets${opts?.counts === true ? '?counts=1' : ''}`,
  ),
  /** Les grilles offertes — servies AVEC leurs tailles : on choisit une grille sur son contenu. */
  /**
   * LA CLAUSE DE COEFFICIENT D’UN FOURNISSEUR, par son libellé — lecture légère.
   * ⚠️ PAS le référentiel complet : celui-là compte les articles de chaque fournisseur, donc balaie
   * tout le catalogue. Lire UN nombre ne doit pas coûter 30 000 lignes.
   * ⚠️ `coefficientBp: null` = ce fournisseur n’a pas de clause. C’est une RÉPONSE, pas un échec.
   */
  supplierCoefficient: (supplier: string) =>
    req<{ supplier: string; coefficientBp: number | null }>(
      'GET', `/admin/referentiel/supplier-coefficient?supplier=${encodeURIComponent(supplier)}`,
    ),

  /** Le référentiel fournisseurs & marques — avec leurs clauses de coefficient. */
  /**
   * Le référentiel des GRILLES DE TAILLES — tailles ordonnées, familles, usage.
   * ⚠️ Distincte de `articleSizeGrids`, qui sert le CHOIX d’une grille à la création d’article :
   * celle-là n’a besoin ni des familles ni de l’usage, et la charger d’agrégats ralentirait un
   * sélecteur qu’on ouvre à chaque création.
   */
  /** Les masques d’import du magasin actif — base INTERNE du BO, ils ne descendent nulle part. */
  /**
   * ÉCRIT le fichier : référentiels, grilles complétées, articles — une transaction du magasin.
   * ⚠️ La clé de rejeu est celle de l'ÉCRAN : un second clic sur le même envoi ne double rien.
   */
  importArticles: (rows: ImportArticleRow[], options: ImportArticlesOptions, idempotencyKey: string) =>
    req<ImportArticlesReport>('POST', '/admin/import/articles', { rows, ...options, idempotencyKey }),
  importMasks: () => req<{ masks: ImportMask[] }>('GET', '/admin/import/masks'),
  /** Les familles du magasin et les correspondances apprises pour ce fournisseur. */
  importFamilies: (supplier: string | null) =>
    req<ImportFamilies>('GET', `/admin/import/families${supplier ? `?supplier=${encodeURIComponent(supplier)}` : ''}`),
  /** Les documents ouverts d'un fournisseur — bons pas encore entrés en stock, commandes qui attendent. */
  importDocuments: (kind: 'bl' | 'cmd', supplier: string | null) =>
    req<{ documents: ImportOpenDocument[] }>('GET', `/admin/import/documents?kind=${kind}${supplier ? `&supplier=${encodeURIComponent(supplier)}` : ''}`),
  /** Le prochain numéro libre d'un bon ou d'une commande pour ce fournisseur — « Proposer ». */
  importDocumentNumber: (kind: 'bl' | 'cmd', supplier: string | null) =>
    req<{ number: string }>('GET', `/admin/import/document-number?kind=${kind}${supplier ? `&supplier=${encodeURIComponent(supplier)}` : ''}`),
  /**
   * Enregistrer (ou corriger) un masque.
   * ⚠️ LE MÊME NOM REMPLACE : réenregistrer est une CORRECTION, pas un second masque homonyme.
   */
  saveImportMask: (name: string, supplier: string, mapping: Record<number, string>) =>
    req<ImportMask>('POST', '/admin/import/masks', { name, supplier, mapping }),
  /** Oublier un masque. ⚠️ Un masque absent n’est pas une erreur : le geste visait à ne plus l’avoir. */
  deleteImportMask: (name: string) =>
    req<{ removed: boolean }>('POST', '/admin/import/masks/delete', { name }),
  referentielSizeGrids: () => req<ReferentialGrids>('GET', '/admin/referentiel/size-grids'),
  /** Les secteurs, dans l'ordre du gérant, avec ce qui les porte et les valeurs hors liste. */
  referentielSectors: () => req<ReferentialFacetListing>('GET', '/admin/referentiel/sectors'),
  /** Les familles : couleur, grilles, modèles et stock, la palette et les grilles qu'on peut leur rattacher. */
  referentielFamilies: () => req<ReferentialFamilyListing>('GET', '/admin/referentiel/families'),
  /** Les sous-familles, avec ce qui les porte et les valeurs hors liste. */
  referentielSubFamilies: () => req<ReferentialFacetListing>('GET', '/admin/referentiel/sub-families'),
  /** Les saisons de la liste, dans l'ordre du gérant, et celles que des articles portent hors liste. */
  referentielSeasons: () => req<ReferentialFacetListing>('GET', '/admin/referentiel/seasons'),
  referentielSuppliers: () => req<ReferentialSuppliers>('GET', '/admin/referentiel/suppliers'),
  /** ⚠️ Les INACTIFS sortent aussi : ils expliquent l'historique et doivent pouvoir être réactivés. */
  referentielPaymentMethods: () =>
    req<{ rows: ReferentialPaymentMethod[] }>('GET', '/admin/referentiel/payment-methods'),
  referentielJustifications: () =>
    req<{ rows: ReferentialJustification[]; registerReasons: RegisterReasonsPending }>('GET', '/admin/referentiel/justifications'),
  referentielExpenseCategories: () =>
    req<{ rows: ReferentialExpenseCategory[] }>('GET', '/admin/referentiel/expense-categories'),
  referentielVat: () => req<ReferentialVat>('GET', '/admin/referentiel/vat'),
  vatDefaults: () => req<VatDefaults>('GET', '/admin/referentiel/vat/defaults'),
  nonDiscountable: () => req<NonDiscountableView>('GET', '/admin/referentiel/non-discountable'),
  /**
   * ALIGNER — ramène les modèles désignés (tous si `null`) au taux par défaut de leur famille ; le serveur relit ce taux au
   * moment d'écrire.
   */
  alignVatRates: (modelKeys: readonly string[] | null, intention: string) =>
    req<{ models: number; variants: number }>('POST', '/admin/referentiel/vat/align', {
      modelKeys, idempotencyKey: cleDeGeste(intention, Date.now()),
    }),
  /**
   * REPRENDRE LES MOTIFS DE REMISE QUE LA CAISSE PROPOSAIT — le serveur recalcule la liste, crée ce qui manque en
   * portée « Remises », ne réécrit jamais un code existant ; relancé, il ne recrée rien.
   */
  importRegisterReasons: (intention: string) =>
    req<{ created: number; unreadable: string[] }>('POST', '/admin/referentiel/justifications/import-register-reasons', {
      idempotencyKey: cleDeGeste(intention, Date.now()),
    }),
  /** Les unités de mesure du magasin actif — LECTURE seule : la Console les sème, le magasin ne les écrit pas. */
  referentielMeasurementUnits: () =>
    req<{ rows: MeasurementUnitUsage[] }>('GET', '/admin/referentiel/measurement-units'),
  /** La colonne des Référentiels : compte, reste à traiter et valeurs cherchables de chaque liste, en une lecture. */
  referentielOverview: () =>
    req<{ entries: ReferentialOverviewEntry[] }>('GET', '/admin/referentiel/overview'),
  /** Le parc de périphériques du magasin actif, AVEC les caisses où les rattacher. */
  storePeripherals: () => req<ParcPeripheriques>('GET', '/admin/store-peripherals'),
  /** L'échéancier de rappel du magasin actif — LECTURE seule : une échéance se crée au comptoir. */
  relances: () => req<EcheancierDuSiege>('GET', '/admin/relances'),
  /** Les grilles offertes à la création — ET le plafond de déclinaisons par appel, servi par le serveur
   *  plutôt que recopié ici : recopié, il divergerait au premier changement. */
  articleSizeGrids: () => req<{ grids: SizeGridOption[]; dimensions: SecondAxisOption[]; truncated: boolean; maxVariants: number }>('GET', '/admin/articles/size-grids'),

  /**
   * CRÉER des déclinaisons sur un modèle — « Ajouter » et le mode scan.
   * ⚠️ SEUL ENDROIT OÙ LE NAVIGATEUR ENVOIE UN GENCODE, et pour une raison précise : il vient de la
   * DOUCHETTE, pas de la base. C’est une SAISIE, pas une clé qu’on lui aurait confiée.
   * ⚠️ `gencode` absent ⇒ le serveur ALLOUE un code maison ; présent ⇒ c’est le code fournisseur lu.
   */
  /**
   * FAIRE ENTRER DES QUANTITÉS EN STOCK — des MOUVEMENTS, jamais un total posé sur la photo.
   * ⚠️ `stock` est consolidé par le serveur depuis le grand livre : y écrire une quantité
   * directement créerait une seconde vérité, que le prochain mouvement écraserait sans le dire.
   * ⚠️ `lot` fonde des identifiants DÉTERMINISTES : rejouer (double clic, reprise après coupure) ne
   * fait pas entrer la marchandise deux fois — et un stock faux ne se voit qu’à l’inventaire.
   */
  createStockEntries: (entries: { gencode: string; qty: number }[], lot: string) =>
    req<{ movements: number; pieces: number }>('POST', '/admin/stock/entries', { entries, lot }),
  /**
   * ÉCRIRE UN PRIX PAR DÉCLINAISON — les dérogations au tarif commun.
   * ⚠️ ROUTE DISTINCTE de `setArticleFields`, qui pose la MÊME valeur sur toutes les déclinaisons.
   * Ici chacune reçoit la sienne, et le serveur vérifie qu’elles appartiennent bien à ce modèle.
   */
  setArticleVariantPrices: (
    model: string,
    prices: { gencode: string; priceCents: number }[],
    idempotencyKey: string,
  ) => req<{ variants: number; written: number; duplicates: number }>(
    'POST', '/admin/articles/variant-prices', { model, prices, idempotencyKey },
  ),

  /**
   * Réserve N codes-barres internes SANS créer d’article.
   * ⚠️ Chaque appel rend des codes NEUFS : les numéros d’un formulaire abandonné sont perdus, et
   * c’est voulu — rejouer l’appel pour redonner les mêmes ferait poser deux fois le même code.
   */
  allocateGencodes: (count: number) =>
    req<{ gencodes: string[] }>('POST', '/admin/articles/gencodes', { count }),
  createArticleVariants: (
    model: string,
    // ⚠️ `size2` FAIT PARTIE DE LA CASE, comme au serveur (`VariantToCreate`) : les deux moitiés de ce
    //    contrat se lisent l'une en face de l'autre, et un test traverse la frontière (forme BP).
    variants: { color: string | null; size: string | null; size2?: string | null; gencode?: string | null }[],
    idempotencyKey: string,
    // ⚠️ LA GRILLE VOYAGE AVEC LES DÉCLINAISONS, par son NOM : le serveur n'écrit une taille qu'avec sa
    //    ligne, donc il doit connaître la grille AVANT les tailles — un modèle qui naît ici n'en hérite
    //    d'aucune. Sans elle, la déclinaison hérite de la grille du modèle existant.
    sizeGrid?: string | null,
    // ⚠️ ET LA GRILLE DU SECOND AXE, pour la même raison exactement : une longueur ne s'écrit qu'avec
    //    sa ligne, donc sa grille doit précéder les valeurs. Elle ne se déduit PAS de la première.
    sizeGrid2?: string | null,
    // ⚠️ LE RAPPORT EST CELUI DU PRODUCTEUR (`WriteReport`) — `created`/`updated`, jamais `variants`
    //    (correctif d'audit 2026-08-25). Ce type-ci annonçait un champ `variants` que le serveur n'a
    //    jamais envoyé : `req<…>` est déclaré à la MAIN, donc rien ne pouvait le contredire, et l'écran
    //    affichait « undefined déclinaison créée ». Le champ voisin, `allocated`, existe bel et bien —
    //    c'est ce qui rendait le défaut invisible à la relecture.
  ) => req<{ created: number; updated: number; written: number; gencodes: string[]; allocated: number }>(
    'POST', '/admin/articles/variants', { model, variants, idempotencyKey, ...(sizeGrid ? { sizeGrid } : {}), ...(sizeGrid2 ? { sizeGrid2 } : {}) },
  ),
  /**
   * Cherche une valeur de filtre DANS LE CATALOGUE, au-dela des valeurs deja recues.
   * ⚠️ C est ce qui rend le plafond de la liste inoffensif : sans elle, une valeur au-dela du plafond
   * n est ni affichee ni trouvable, et on en conclut qu elle n existe pas dans ce magasin.
   */
  articleFacetSearch: (dim: ArticleFacet, q: string) => req<FacetValue[]>(
    'GET', `/admin/articles/facets/search?dim=${dim}&q=${encodeURIComponent(q)}`,
  ),
  // ── Tableau de bord ──
  /**
   * Les indicateurs du magasin ACTIF sur une période, et la même période un an plus tôt.
   * ⚠️ Les deux bornes sont OBLIGATOIRES : le serveur ne devine pas « aujourd'hui » (il tourne en UTC,
   * `datetime_fiscal` est l'heure locale du magasin).
   */
  dashboard: (p: { from: string; to: string }) => req<DashboardView>(
    'GET', `/admin/dashboard?${new URLSearchParams({ from: p.from, to: p.to }).toString()}`,
  ),
  // ── Statistiques › Ventes ──
  /** Les articles sans coût connu d'une période — la liste qui permet de rendre la marge mesurable. */
  salesMissingCosts: (p: MissingCostsParams) => req<{ rows: MissingCostArticleView[] }>(
    'GET',
    `/admin/stats/sales/missing-costs?${new URLSearchParams({
      from: p.from, to: p.to,
      ...(p.q ? { q: p.q } : {}),
      ...(p.filters && Object.values(p.filters).some((v) => v && v.length) ? { filters: JSON.stringify(p.filters) } : {}),
    }).toString()}`,
  ),
  /** Le Résumé d'une période du magasin ACTIF — N-1 aux mêmes dates quand aucune comparaison n'est choisie. */
  /** Statistiques › Vendeurs › Résumé : qui fait le chiffre, sur la barre partagée des statistiques. */
  salesSellers: (p: StockSummaryParams) => req<SalesSellersView>('GET', `/admin/stats/sellers/summary?${windowSearch(p)}`),
  /** Statistiques › Vendeurs › Reporting vendeurs : le Reporting ventes, une ligne par vendeur, sans bloc ni niveau. */
  sellersReport: (p: StockSummaryParams) => req<SalesReportView>('GET', `/admin/stats/sellers/report?${windowSearch(p)}`),
  /** Statistiques › Vendeurs › Vendeurs par période : sans comparaison (l'onglet n'en a pas), et le grain des colonnes. */
  sellerPeriods: (p: Omit<StockSummaryParams, 'cmpFrom' | 'cmpTo'> & { grain: StockPeriodGrain }) =>
    req<SellerPeriodsView>('GET', `/admin/stats/sellers/periods?${windowSearch(p)}&${new URLSearchParams({ grain: p.grain }).toString()}`),
  /** Statistiques › Clients › Résumé : N-1 au même jour de semaine quand aucune comparaison n'est choisie. */
  clientsSummary: (p: StockSummaryParams) => req<ClientsSummaryView>('GET', `/admin/stats/clients/summary?${windowSearch(p)}`),
  /** Statistiques › Clients › Fidélité : la période et sa comparaison seulement — le serveur refuse les filtres d'article. */
  /** Statistiques › Clients › Campagnes : toutes les campagnes du magasin, sans période. */
  clientsCampaigns: () => req<ClientsCampaignsView>('GET', '/admin/stats/clients/campaigns'),
  /** Statistiques › Clients › Reporting clients : les fiches regroupées par l'axe demandé, sans période. */
  clientsReport: (axis: ClientsReportView['axis']) => req<ClientsReportView>('GET', `/admin/stats/clients/report?${new URLSearchParams({ axis }).toString()}`),
  clientsLoyalty: (p: { from: string; to: string; cmpFrom?: string; cmpTo?: string }) => req<ClientsLoyaltyView>('GET', `/admin/stats/clients/loyalty?${windowSearch({ ...p, q: '', filters: {} })}`),
  /** Statistiques › Ventes › Remises : le total des remises, réparti, sur la barre partagée des onglets. */
  salesDiscounts: (p: StockSummaryParams) => req<SalesDiscountsView>('GET', `/admin/stats/sales/discounts?${windowSearch(p)}`),
  salesSummary: (p: SalesSummaryParams) => req<SalesSummaryView>(
    'GET',
    `/admin/stats/sales/summary?${new URLSearchParams({
      from: p.from, to: p.to, axis: p.axis,
      ...(p.cmpFrom && p.cmpTo ? { cmpFrom: p.cmpFrom, cmpTo: p.cmpTo } : {}),
      ...(p.q ? { q: p.q } : {}),
      ...(Object.values(p.filters).some((v) => v && v.length) ? { filters: JSON.stringify(p.filters) } : {}),
    }).toString()}`,
  ),
  /** Le Résumé du stock du magasin ACTIF — N-1 au même jour de semaine quand aucune comparaison n'est choisie. */
  stockSummary: (p: StockSummaryParams) => req<StockSummaryView>('GET', `/admin/stats/stock/summary?${windowSearch(p)}`),
  /** Le Reporting ventes du magasin ACTIF — sans comparaison choisie, ni période B ni écart. */
  salesReport: (p: SalesReportParams) => req<SalesReportView>('GET', `/admin/stats/sales/report?${reportSearch(p)}`),
  /** Le Reporting stock : les mêmes réglages que le Reporting ventes. */
  stockReport: (p: SalesReportParams) => req<StockReportView>('GET', `/admin/stats/stock/report?${reportSearch(p)}`),
  /** Le Reporting taux de sortie : les mêmes réglages. */
  stockExitReport: (p: SalesReportParams) => req<StockExitView>('GET', `/admin/stats/stock/exit-rate?${reportSearch(p)}`),
  /** Le Stock par périodes : les réglages des reportings, sans comparaison (le type n'en porte pas), et le grain. */
  stockPeriods: (p: Omit<SalesReportParams, 'cmpFrom' | 'cmpTo'> & { grain: StockPeriodGrain }) =>
    req<StockPeriodsView>('GET', `/admin/stats/stock/periods?${reportSearch(p)}&grain=${p.grain}`),
  /** Le Stock par tailles : les réglages des reportings, sans comparaison, et la grille choisie (`null` = la plus stockée). */
  stockSizes: (p: Omit<SalesReportParams, 'cmpFrom' | 'cmpTo'> & { grid: string | null }) =>
    req<StockSizesView>('GET', `/admin/stats/stock/sizes?${reportSearch(p)}${p.grid ? `&grid=${encodeURIComponent(p.grid)}` : ''}`),
  /** La bibliothèque des vues d'un onglet de reporting, partagée par le magasin ACTIF. `unreadable` : vues dont la période ne se lit plus. */
  statsViews: (tab: StatsViewTab) =>
    req<{ tab: StatsViewTab; views: StatsView[]; unreadable: number }>('GET', `/admin/stats/views?tab=${tab}`),
  /** Enregistre une vue. Sans `replace`, un nom déjà pris est refusé (409 `view_exists`) — l'écran demande s'il faut remplacer. */
  saveStatsView: (tab: StatsViewTab, name: string, settings: StatsViewSettings, replace = false) =>
    req<{ view: StatsView; replaced: boolean }>('PUT', '/admin/stats/views', { tab, name, settings, ...(replace ? { replace: true } : {}) }),
  /** Supprime une vue. `removed: false` : elle n'existait plus. */
  deleteStatsView: (tab: StatsViewTab, name: string) =>
    req<{ removed: boolean }>('DELETE', `/admin/stats/views/${tab}/${encodeURIComponent(name)}`),
  /** Le reporting articles du magasin ACTIF — ce qu'on a reçu, vendu et ce qu'il reste, du modèle au gencode. */
  articleReport: (p: ArticleReportParams) => req<ArticleReportView>(
    'GET',
    `/admin/stats/sales/articles?${new URLSearchParams({
      from: p.from, to: p.to, recFrom: p.recFrom, recTo: p.recTo, axes: p.axes.join(','),
      ...(p.q ? { q: p.q } : {}),
      ...(Object.values(p.filters).some((v) => v && v.length) ? { filters: JSON.stringify(p.filters) } : {}),
    }).toString()}`,
  ),
  /** Le suivi jour après jour des objectifs du magasin ACTIF, `from`–`to` en `AAAAMMJJ`. */
  objectiveTracking: (p: { from: string; to: string }) => req<ObjectiveTrackingView>(
    'GET', `/admin/stats/sales/objectives?${new URLSearchParams({ from: p.from, to: p.to }).toString()}`,
  ),
  // ── Objectifs ──
  /** L'exercice du magasin ACTIF — sans année, l'année en cours du magasin. */
  objectivesYear: (year?: number) => req<ObjectiveYearView>('GET', `/admin/objectives${year ? `?year=${year}` : ''}`),
  /** L'objectif d'un mois `AAAAMM` — `null` le retire. */
  setMonthObjective: (month: string, amountCents: number | null, idempotencyKey: string = cleDeGeste(`obj:month:${month}`, Date.now())) =>
    req<ObjectiveWriteReport>('PUT', `/admin/objectives/months/${encodeURIComponent(month)}`, { amountCents, idempotencyKey }),
  /** Pré-remplir un exercice depuis le réalisé de l'an dernier, majoré de `changeBp`. */
  prefillObjectives: (year: number, changeBp: number, idempotencyKey: string = cleDeGeste(`obj:prefill:${year}`, Date.now())) =>
    req<ObjectiveWriteReport>('POST', '/admin/objectives/prefill', { year, changeBp, idempotencyKey }),
  /** Recopier les objectifs de l'exercice précédent sur `year`. */
  copyObjectives: (year: number, idempotencyKey: string = cleDeGeste(`obj:copy:${year}`, Date.now())) =>
    req<ObjectiveWriteReport>('POST', '/admin/objectives/copy', { year, idempotencyKey }),
  /** Les poids des sept jours, lundi d'abord — `null` revient au profil de l'historique. */
  setObjectiveWeights: (weights: number[] | null, idempotencyKey: string = cleDeGeste('obj:weights', Date.now())) =>
    req<ObjectiveWriteReport>('PUT', '/admin/objectives/weights', { weights, idempotencyKey }),
  /** Un jour exceptionnel `AAAAMMJJ`. */
  setObjectiveDay: (day: string, kind: 'closed' | 'event', changeBp: number | null, idempotencyKey: string = cleDeGeste(`obj:day:${day}`, Date.now())) =>
    req<ObjectiveWriteReport>('PUT', `/admin/objectives/days/${encodeURIComponent(day)}`, { kind, changeBp, idempotencyKey }),
  /** Fixe l'objectif d'un jour ; le reste du mois se répartit sur les autres jours. */
  fixObjectiveDay: (day: string, amountCents: number, idempotencyKey: string = cleDeGeste(`obj:day-fix:${day}`, Date.now())) =>
    req<ObjectiveWriteReport>('PUT', `/admin/objectives/days/${encodeURIComponent(day)}`, { kind: 'fixed', amountCents, idempotencyKey }),
  /** Retire un jour particulier — un jour fixé revient au calcul. */
  removeObjectiveDay: (day: string, idempotencyKey: string = cleDeGeste(`obj:day-out:${day}`, Date.now())) =>
    req<ObjectiveWriteReport>('POST', `/admin/objectives/days/${encodeURIComponent(day)}/delete`, { idempotencyKey }),
  /** Rend au calcul tous les jours fixés d'un mois `AAAAMM` — son objectif ne change pas ; `written` : les jours rendus. */
  unfixMonthDays: (month: string, idempotencyKey: string = cleDeGeste(`obj:month-unfix:${month}`, Date.now())) =>
    req<ObjectiveWriteReport>('POST', `/admin/objectives/months/${encodeURIComponent(month)}/unfix-days`, { idempotencyKey }),
  // ── Journal des ventes ──
  /** Une page du journal du magasin ACTIF. Tri, pagination et bornes de période sont appliqués serveur. */
  sales: (p: SalesQueryParams) => req<SalesPageView>(
    'GET',
    `/admin/sales?${new URLSearchParams({
      page: String(p.page), size: String(p.size), sort: p.sort, dir: p.dir, tab: p.tab,
      ...(p.from ? { from: p.from } : {}), ...(p.to ? { to: p.to } : {}),
      ...(p.register ? { register: p.register } : {}), ...(p.q ? { q: p.q } : {}),
      ...(p.operator ? { operator: p.operator } : {}), ...(p.method ? { method: p.method } : {}),
    }).toString()}`,
  ),
  /**
   * ACTIVER / RETIRER des modèles. Rend ce qui a RÉELLEMENT été touché.
   * ⚠️ `variants` et non `models` est le chiffre à montrer : l'opérateur coche des lignes, mais ce
   * sont les déclinaisons qui changent d'état — et il y en a souvent douze par ligne.
   */
  setArticlesActive: (modelKeys: string[], active: boolean, idempotencyKey: string) =>
    req<BulkReport>('POST', '/admin/articles/activation', { modelKeys, active, idempotencyKey }),

  /**
   * L'ARCHIVAGE — ce que les critères désigneraient, sans rien écrire (une lecture : un GET).
   * ⚠️ Les saisons se RÉPÈTENT dans l'adresse : une saison peut porter une virgule.
   */
  archivePreview: (c: ArchiveCriteria) => req<ArchivePreview>('GET', `/admin/articles/archive/preview?${archiveQuery(c)}`),
  /** Archive ce que les critères désignent AU MOMENT D'ÉCRIRE — le compte rendu dit ce qui a été fait. */
  archiveArticles: (criteria: ArchiveCriteria, idempotencyKey: string) =>
    req<ArchiveStateChange>('POST', '/admin/articles/archive', { criteria, idempotencyKey }),
  /** Les modèles archivés — la seule liste où ils se retrouvent. */
  archivedArticles: (q: string, page: number) =>
    req<ArchivedPage>('GET', `/admin/articles/archived?${new URLSearchParams({ q, page: String(page) }).toString()}`),
  /** Remet des modèles en service : ils redescendent en caisse avec leur stock et leurs photos. */
  unarchiveArticles: (modelKeys: string[], idempotencyKey: string) =>
    req<ArchiveStateChange>('POST', '/admin/articles/unarchive', { modelKeys, idempotencyKey }),

  /**
   * RECLASSER des modèles dans une famille.
   * ⚠️ La famille voyage en LIBELLÉ, pas en identifiant : `article.family` est du texte, et le
   * référentiel est le catalogue lui-même. Un identifiant supposerait une table de familles.
   */
  setArticlesFamily: (modelKeys: string[], family: string, idempotencyKey: string) =>
    req<BulkReport>('POST', '/admin/articles/family', { modelKeys, family, idempotencyKey }),

  /**
   * ÉCRIRE SUR TOUT CE QUI CORRESPOND AU FILTRE — la sélection par critère.
   *
   * ⚠️ `asOf` EST L'INSTANT OÙ L'ÉCRAN A COMPTÉ ce qu'il montrait, et c'est la garantie principale :
   * tout modèle modifié depuis est écarté par le serveur et compté à part. L'envoyer « maintenant »
   * au moment du clic annulerait la garde — il doit venir du préflight.
   * ⚠️ ON N'ENVOIE PAS LES CLÉS : le filtre voyage. C'est ce qui rend l'opération indépendante du
   * nombre de lignes, et ce qui empêche l'écran de définir un ensemble que le serveur redéfinirait.
   */
  articlesScopeWrite: (
    scope: { filters: Record<string, string>; excluded: string[]; asOf: number },
    fields: Record<string, string | boolean | null>,
    idempotencyKey: string,
  ) => req<ScopeReport>('POST', '/admin/articles/scope', { scope, fields, idempotencyKey }),

  /** Ce qu'une portée pèse AVANT d'écrire — et l'instant qui sert de référence à la garde. */
  articlesScopePreflight: (filters: Record<string, string>) =>
    req<ScopePreflight>('GET', `/admin/articles/scope/preflight?${new URLSearchParams(filters).toString()}`),

  /**
   * CRÉER UNE VALEUR DE RÉFÉRENTIEL (famille, secteur, sous-famille, collection, fournisseur, marque…).
   *
   * ⚠️ ELLE PASSE PAR LE PRODUCTEUR, comme toute écriture : la ligne est créée dans le JOURNAL, donc elle
   * descendra aux caisses. Un INSERT direct réussirait et resterait invisible du reste de la flotte.
   * ⚠️ L'IDENTITÉ EST LE CODE NORMALISÉ : recréer « Vestes » quand « vestes » existe ne fabrique pas de
   * doublon, ça retombe sur la même ligne. C'est la même règle que partout ailleurs dans le producteur.
   */
  createReferentielEntry: (
    entity: string,
    // ⚠️ PAS QUE DES CHAÎNES : le coefficient est un NOMBRE, et `null` porte un sens propre —
    // « retirer la clause » pour un fournisseur, « détacher » pour une marque. Les convertir en
    // texte les rendrait indistinguables de l’absence.
    // ⚠️ ET DES LISTES : les tailles d'une grille et les valeurs d'un second axe sont des tableaux
    //    ORDONNÉS, et le producteur EXIGE un tableau. Les envoyer en texte JSON — ce que ce type
    //    forçait — faisait refuser chaque écriture de grille avec « sizes doit être une liste ».
    entry: Record<string, string | number | boolean | null | readonly string[]>,
    /**
     * Ce que le geste FAIT — « detach:brand:JACK », « coef:supplier:PVH ». Un libellé de lecture,
     * pas la clé : elle est frappée ci-dessous.
     */
    intention: string,
  ) =>
    api.writeMasterData(entity, [entry], intention),

  /**
   * CRÉER une valeur de référentiel — REFUSÉE si son code existe déjà (« cb » quand « CB » existe), au lieu de
   * réécrire la ligne existante comme le fait `createReferentielEntry`. Pour les écrans dont le geste est
   * « ajouter » et où la ligne existante porte un rang, une activité ou une nature qu'un ajout ne doit pas écraser.
   */
  createMasterDataOnly: (
    entity: string,
    entry: Record<string, string | number | boolean | null | readonly string[]>,
    intention: string,
  ) =>
    req<{ entity: string; written: number }>('POST', `/admin/masterdata/${encodeURIComponent(entity)}`, {
      entries: [entry], createOnly: true, idempotencyKey: cleDeGeste(intention, Date.now()),
    }),

  /**
   * ÉCRIRE UN LOT DE DONNÉES MAÎTRES — TOUT OU RIEN.
   *
   * ⚠️ GÉNÉRALISÉE À SON DEUXIÈME USAGE (règle de 2, §24.3) : le plan comptable s'enregistre poste par
   * poste, et une saisie moitié enregistrée produirait une écriture déséquilibrée. Le moteur refuse
   * déjà un lot fautif en bloc — encore fallait-il pouvoir lui en envoyer un.
   */
  writeMasterData: (
    entity: string,
    entrees: readonly Record<string, string | number | boolean | null | readonly string[]>[],
    intention: string,
  ) =>
    req<{ entity: string; written: number }>('POST', `/admin/masterdata/${encodeURIComponent(entity)}`, {
      entries: entrees,
      // ⚠️ FRAPPÉE ICI, pas au point d'appel : quatre écrans écrivaient des référentiels, et les
      // quatre avaient posé une clé stable. La bonne place d'une règle que tout le monde manque est
      // l'endroit qu'on ne peut pas contourner. Le pourquoi est sur `cleDeGeste`.
      idempotencyKey: cleDeGeste(intention, Date.now()),
    }),

  /**
   * SUPPRIMER UNE VALEUR DE RÉFÉRENTIEL — un TOMBSTONE, jamais un DELETE en table.
   *
   * ⚠️ Retirer une ligne en base la ferait disparaître du serveur ET DE NULLE PART AILLEURS : le pull
   * ne transporte que ce que le journal contient, donc une caisse qui a déjà l’élément le garderait
   * indéfiniment. Le tombstone est la seule suppression qui DESCEND.
   * ⚠️ Le serveur REFUSE si la valeur est encore portée — et il le MESURE dans la transaction. Ce
   * n’est pas à l’écran d’en décider : son compte a une seconde de retard, et une seconde suffit.
   */
  deleteReferentielEntry: (
    entity: string,
    entry: Record<string, string | number | boolean | null | readonly string[]>,
    intention: string,
  ) =>
    req<{ entity: string; deleted: number }>(
      'POST', `/admin/masterdata/${encodeURIComponent(entity)}/delete`,
      { entries: [entry], idempotencyKey: cleDeGeste(intention, Date.now()) },
    ),

  /**
   * ÉCRIRE LES CHAMPS D'UN MODÈLE — ce que la fiche enregistre.
   * ⚠️ SEULS LES CHAMPS TOUCHÉS partent (carte de différences, pas un formulaire entier) : un envoi
   * complet écraserait des valeurs que personne n'a modifiées.
   * ⚠️ `null` DÉTACHE, la chaîne vide est REFUSÉE par le serveur — deux sens pour un même caractère.
   * ⚠️ Le navigateur envoie une clé de MODÈLE : il ne voit jamais un gencode, c'est le serveur qui résout.
   */
  setArticleFields: (modelKey: string, fields: Record<string, string | boolean | null>, idempotencyKey: string) =>
    req<BulkReport>('POST', '/admin/articles/fields', { modelKeys: [modelKey], fields, idempotencyKey }),

  /** Le détail SCELLÉ d'une pièce (dépliage de ligne). */
  salesPiece: (id: string) => req<SalesDetail>('GET', `/admin/sales/piece?piece=${encodeURIComponent(id)}`),
  /** Les paniers suspendus du magasin ACTIF — sans période ni pagination : il y en a des unités. */
  salesPending: () => req<PendingSalesView>('GET', '/admin/sales/pending'),
  /** Une page du journal d'activité du magasin ACTIF, la plus récente en tête — la suivante se demande par son curseur. */
  activity: (p: ActivityParams) => req<ActivityPageView>(
    'GET',
    `/admin/activity?${new URLSearchParams({
      ...(p.from && p.to ? { from: p.from, to: p.to } : {}),
      ...(p.domains?.length ? { domain: p.domains.join(',') } : {}),
      ...(p.actor ? { actor: p.actor } : {}),
      ...(p.q ? { q: p.q } : {}),
      ...(p.before ? { before: p.before } : {}),
      ...(p.size ? { size: String(p.size) } : {}),
    }).toString()}`,
  ),
  /** Les ventes abandonnées d'une période (`AAAAMMJJ`, jours du magasin) — mêmes caisses et même vendeur que le journal. */
  salesAbandoned: (p: { from: string; to: string; register: string; operator: string; payment: boolean; page: number; size: number }) =>
    req<AbandonedPageView>('GET', `/admin/sales/abandoned?${new URLSearchParams({
      from: p.from, to: p.to, register: p.register, operator: p.operator, payment: p.payment ? '1' : '', page: String(p.page), size: String(p.size),
    }).toString()}`),
  salesAbandonedDetail: (id: string) => req<AbandonedDetailView>('GET', `/admin/sales/abandoned/one?id=${encodeURIComponent(id)}`),
  /** ⚠️ UNE SEULE BORNE : la journée FISCALE `AAAAMMJJ`. La caisse date déjà son tiroir en heure
   *  locale du magasin, bascule comprise — il n'y a plus rien à convertir, ni ici ni au serveur. */
  cashDay: (day: string) => req<CashDayView>('GET', `/admin/cash/day?day=${encodeURIComponent(day)}`),
  /** ⚠️ TOUT L'HISTORIQUE, sans page : c'est le mois ancien resté ouvert qu'on cherche, et une
   *  pagination l'aurait justement mis hors de vue. */
  cashMonths: () => req<CashMonthsView>('GET', '/admin/cash/months'),
  /** ⚠️ OUVERTE OU CLOSE : une caisse en cours de journée a déjà un fond, un comptage et des
   *  prélèvements à montrer. `z: null` est un résultat, pas une panne. */
  sessionDetail: (sessionId: string) =>
    req<{ detail: SessionDetail | null }>('GET', `/admin/cash/session?session=${encodeURIComponent(sessionId)}`),
  /** ⚠️ LE RELEVÉ MENSUEL DIT QU'UN MOIS EST BLOQUÉ ; celui-ci montre OÙ, jour par jour. */
  cashMonthDays: (ym: string) =>
    req<{ ym: string; days: CashMonthDay[] }>('GET', `/admin/cash/month-days?ym=${encodeURIComponent(ym)}`),
  /** ⚠️ AUCUN MONTANT : justifier n'est pas corriger. L'auteur vient de la session, pas d'ici. */
  saveCashVarianceNote: (countId: string, motifCode: string, note: string) =>
    req<{ id: string }>('POST', '/admin/cash/variance-note', { countId, motifCode, note }),

  /**
   * La FICHE d'un modèle. ⚠️ Un modèle disparu rend un 404 `model_not_found` que l'écran distingue d'un
   * échec de lecture : la liste dont on vient peut dater, et « introuvable » n'est pas « en panne ».
   */
  articleModel: (modelKey: string) =>
    req<ArticleModelDetail>('GET', `/admin/articles/model?model=${encodeURIComponent(modelKey)}`),

  /** Les déclinaisons d'un modèle (dépliage de ligne) — bornées côté serveur. */
  articleVariants: (modelKey: string) =>
    req<ArticleVariantSet>('GET', `/admin/articles/variants?model=${encodeURIComponent(modelKey)}`),

  /** Le grand livre d’un modèle (onglet Mouvements) — borné côté serveur, plus récent d’abord. */
  articleMovements: (modelKey: string, gencode?: string) =>
    req<StockMovementSet>('GET', `/admin/articles/movements?model=${encodeURIComponent(modelKey)}${gencode ? `&gencode=${encodeURIComponent(gencode)}` : ''}`),

  /** Requalifie le code-barres d’une déclinaison — 409 `gencode_locked` si un des quatre interdits s’applique. */
  renameArticleGencode: (from: string, to: string) =>
    req<{ blocked: string | null }>('POST', '/admin/articles/gencode', { from, to }),

  /** L’état de stock par déclinaison (onglet Stock) : entrées, ventes, stock. */
  articleStockState: (modelKey: string) =>
    req<StockStateSet>('GET', `/admin/articles/stock-state?model=${encodeURIComponent(modelKey)}`),

  /** Les visuels d’un modèle, tous coloris (onglet Photos). */
  articlePhotos: (modelKey: string) =>
    req<{ rows: ArticlePhoto[]; maxPerColor: number }>('GET', `/admin/articles/photos?model=${encodeURIComponent(modelKey)}`),

  /**
   * Dépose des visuels sur un coloris.
   * ⚠️ EN `FormData` BRUT, sans `Content-Type` pose a la main : le navigateur doit ecrire lui-meme la
   * frontiere du multipart. La fixer casse le decoupage cote serveur, silencieusement.
   */
  /**
   * Dépose des visuels. `thumbs[i]` est la VIGNETTE de `files[i]` — `null` accepté.
   * ⚠️ APPARIEMENT PAR INDICE DANS LE NOM DU CHAMP (`file0`/`thumb0`), jamais par ordre d'arrivée : une
   * photo sans vignette décalerait deux listes parallèles d'un cran, et chaque image se retrouverait
   * avec la miniature de sa voisine — un défaut qu'aucun type ne peut attraper.
   */
  addArticlePhotos: (modelKey: string, color: string | null, files: File[], thumbs: (File | null)[] = []) => {
    const fd = new FormData();
    fd.append('model', modelKey);
    if (color) fd.append('color', color);
    files.forEach((f, i) => {
      fd.append(`file${i}`, f);
      const v = thumbs[i];
      if (v) fd.append(`thumb${i}`, v);
    });
    return req<{ written: number }>('POST', '/admin/articles/photos', fd);
  },

  /** L’ordre des visuels — et donc le visuel principal, qui est celui de tete. */
  arrangeArticlePhotos: (modelKey: string, color: string | null, order: string[]) =>
    req<{ written: number }>('POST', '/admin/articles/photos/arrange', { model: modelKey, color, order }),

  /**
   * Bascule LA photo du modele (vignette des listes). Recliquer la meme la DEMARQUE.
   * ⚠️ Sans coloris : ce drapeau vaut pour l article entier, pas pour une couleur.
   */
  setArticleMainPhoto: (modelKey: string, id: string) =>
    req<{ isMain: boolean }>('POST', '/admin/articles/photos/main', { model: modelKey, id, idempotencyKey: cleDeGeste(`photo-main:${modelKey}:${id}`, Date.now()) }),

  /** Retire un visuel de la fiche. ⚠️ Il DEREFERENCE : les octets restent (ils peuvent etre partages). */
  removeArticlePhoto: (modelKey: string, color: string | null, id: string) =>
    req<{ removed: number; remaining: number }>('POST', '/admin/articles/photos/remove', { model: modelKey, color, id, idempotencyKey: cleDeGeste(`photo-remove:${modelKey}:${id}`, Date.now()) }),

  /** La performance d’un modèle (onglet Stats) — mesurée, jamais estimée. */
  articleStats: (modelKey: string) =>
    req<ArticleStats>('GET', `/admin/articles/stats?model=${encodeURIComponent(modelKey)}`),

  /** L’historique des modifications d’un modèle (onglet Historique) — lu dans le journal. */
  articleHistory: (modelKey: string) =>
    req<ArticleHistory>('GET', `/admin/articles/history?model=${encodeURIComponent(modelKey)}`),

  /** Les réceptions valorisées d’un modèle (pied de l’onglet Prix) — ce qui compose le PMP. */
  articlePurchaseEntries: (modelKey: string) =>
    req<PurchaseEntrySet>('GET', `/admin/articles/entries?model=${encodeURIComponent(modelKey)}`),

  // ── Bons de livraison ──────────────────────────────────────────────────────────────────────────
  /** Le fichier clients — liste paginée, puces et repères sur le premier lot seulement. */
  clients: (p: { q?: string; chip?: ClientChip | ''; sort?: ClientSortKey; dir?: 'asc' | 'desc'; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (p.q) qs.set('q', p.q);
    if (p.chip) qs.set('chip', p.chip);
    if (p.sort) qs.set('sort', p.sort);
    if (p.dir) qs.set('dir', p.dir);
    if (p.limit != null) qs.set('limit', String(p.limit));
    if (p.offset != null) qs.set('offset', String(p.offset));
    return req<ClientsPage>('GET', `/admin/clients${qs.size ? `?${qs}` : ''}`);
  },
  /** La fiche complète — 6 lectures bornées côté serveur, 404 nommé si la fiche n'est pas du magasin. */
  client: (id: string) => req<ClientDetail>('GET', `/admin/clients/one?id=${encodeURIComponent(id)}`),
  /** Les communes d'une saisie (code postal ou début de nom) — la requête de la caisse, sur la base du magasin. */
  searchCommunes: (q: string) => req<CommuneSearchResult>('GET', `/admin/clients/communes?q=${encodeURIComponent(q)}`),
  /** Création — 409 « conflict » = doublon PROBABLE nommé dans le message ; `force` passe outre. */
  createClient: (input: ClientInput, force = false) => req<{ id: string }>('POST', '/admin/clients', { ...input, force }),
  saveClient: (id: string, input: ClientInput) => req<{ id: string }>('PUT', '/admin/clients/one', { id, ...input }),
  anonymizeClient: (id: string) => req<{ id: string }>('POST', '/admin/clients/anonymize', { id }),
  /**
   * Les fiches SANS numéro client — l'état, puis l'outil qui répare.
   * ⚠️ DEUX APPELS ET PAS UN : consulter l'état ne doit rien écrire. Sur la base bêta, 11 450 fiches
   *    reprises attendent leur numéro ; sans numéro, le producteur ne peut pas les adresser et leur
   *    palier de fidélité n'est jamais calculé.
   */
  clientNumbering: () => req<ClientNumberingReport>('GET', '/admin/clients/numbering'),
  runClientNumbering: (batchSize?: number) => req<ClientNumberingReport>('POST', '/admin/clients/numbering', batchSize ? { batchSize } : {}),
  /** Le registre des titres & bons — LECTURE SEULE (un titre s'émet et se consomme en caisse). */
  vouchers: (p: { q?: string; type?: string; state?: VoucherState | ''; from?: string; to?: string; sort?: TitreSortKey; dir?: 'asc' | 'desc'; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (p.q) qs.set('q', p.q);
    if (p.type) qs.set('type', p.type);
    if (p.state) qs.set('state', p.state);
    if (p.from) qs.set('from', p.from);
    if (p.to) qs.set('to', p.to);
    if (p.sort) qs.set('sort', p.sort);
    if (p.dir) qs.set('dir', p.dir);
    if (p.limit != null) qs.set('limit', String(p.limit));
    if (p.offset != null) qs.set('offset', String(p.offset));
    return req<TitresPage>('GET', `/admin/vouchers${qs.size ? `?${qs}` : ''}`);
  },
  /** Le détail d'un titre et sa VIE (faits datés) — lecture seule elle aussi. */
  voucher: (code: string) => req<TitreDetail>('GET', `/admin/vouchers/one?code=${encodeURIComponent(code)}`),
  /** Le référentiel des qualifications — avec l'USAGE de chacune (on ne désactive pas à l'aveugle). */
  qualifications: () => req<{ rows: ClientQualification[] }>('GET', '/admin/clients/qualifications'),
  /** Supprimer une qualification ET la retirer de toutes les fiches — la réponse dit combien. */
  deleteQualification: (code: string) =>
    req<{ code: string; fichesTouchees: number }>('DELETE', `/admin/clients/qualifications?code=${encodeURIComponent(code)}`),
  /** `create` : le geste « Ajouter » — le serveur REFUSE un code existant au lieu de le réécrire. */
  saveQualification: (q: { code: string; label: string; sortOrder?: number; isActive?: boolean; color?: string; create?: boolean }) =>
    req<{ code: string }>('POST', '/admin/clients/qualifications', q),
  setQualificationActive: (code: string, isActive: boolean) =>
    req<{ code: string; isActive: boolean }>('PUT', '/admin/clients/qualifications/active', { code, isActive }),

  /** Les contrôles Mobile Partner en attente — les tuiles du bandeau de l'onglet BL. */
  deliveryControls: () => req<{ rows: ReceptionControl[] }>('GET', '/admin/deliveries/controls'),
  /** Le contrôle REJOINT le bon `number` : rapproché s'il existe, créé sous ce numéro sinon. */
  settleControl: (zoneId: string, number: string) =>
    req<ControlSettlement>('POST', '/admin/deliveries/controls/settle', { zoneId, number }),
  discardControl: (zoneId: string) => req<{ zoneId: string; retiredAt: number }>('POST', '/admin/deliveries/controls/discard', { zoneId }),
  deliveries: () => req<{ rows: BonResume[] }>('GET', '/admin/deliveries'),
  delivery: (number: string) => req<DeliveryNoteDetail>('GET', `/admin/deliveries/one?number=${encodeURIComponent(number)}`),
  createDelivery: (input: {
    number?: string; supplierId?: string | null; supplierLabel?: string | null; sourceRef?: string | null;
    orderRef?: string | null; deliveredOn?: number | null; parcels?: string | null; note?: string | null;
    /** `scan` = né douchette en main (le scan ajoute sans demander) ; défaut serveur : `manual`. */
    source?: SourceDuBon;
    lines: { gencode: string; expectedQty: number; unitPriceFine?: number | null }[];
  }) => req<DeliveryNoteDetail>('POST', '/admin/deliveries', input),
  /** Le contrôle : ce qu’on a compté, et le prix si on l’a corrigé (`undefined` = on n’y touche pas). */
  checkDelivery: (number: string, lines: { gencode: string; checkedQty: number | null; unitPriceFine?: number | null }[]) =>
    req<DeliveryNoteDetail>('POST', '/admin/deliveries/check', { number, lines }),
  scanDelivery: (number: string, gencode: string) =>
    req<DeliveryNoteDetail>('POST', '/admin/deliveries/scan', { number, gencode }),
  /** Fait entrer en stock ce qui est compté et pas encore entré. `enteredOn` : jamais dans le futur. */
  integrateDelivery: (number: string, enteredOn: number | null) =>
    req<RapportDIntegration>('POST', '/admin/deliveries/integrate', { number, enteredOn }),
  /**
   * L'ÉDITEUR DE BL : un BL par fournisseur, au réel reçu — enregistré (`save`), ou intégré dans le même geste, entrée
   * datée du BL. Tout ou rien ; la clé du geste rend un double clic inoffensif.
   */
  saveDeliveryBatch: (input: { key: string; mode: 'save' | 'integrate'; groups: DeliveryGroupInput[] }) =>
    req<{ rows: SavedDelivery[] }>('POST', '/admin/deliveries/batch', input),
  closeDelivery: (number: string) => req<DeliveryNoteDetail>('POST', '/admin/deliveries/close', { number }),
  deleteDelivery: (number: string) => req<{ removed: boolean }>('POST', '/admin/deliveries/delete', { number }),

  /** Supprime UNE ligne d'un bon ouvert — connue jamais entrée, ou fantôme scannée par erreur. */
  deleteDeliveryLine: (number: string, gencode: string) =>
    req<DeliveryNoteDetail>('POST', '/admin/deliveries/line-delete', { number, gencode }),

  /**
   * « Corriger le stock » après intégration : le BL reste figé, chaque écart devient un mouvement
   * signé — motif OBLIGATOIRE (référentiel fermé), cible comparée à ce que CE bon a mis en stock.
   */
  correctDelivery: (number: string, corrections: { gencode: string; correctedTo: number }[], reason: StockCorrectionReason, motifLabel: string) =>
    req<RapportDeCorrection>('POST', '/admin/deliveries/correct', { number, corrections, reason, motifLabel }),

  // ── Commandes fournisseur ──────────────────────────────────────────────────────────────────────
  orders: () => req<{ rows: CommandeResume[] }>('GET', '/admin/orders'),
  order: (number: string) => req<CommandeDetaillee>('GET', `/admin/orders/one?number=${encodeURIComponent(number)}`),
  /** Les commandes encore ouvertes — ce que l'éditeur de BL propose de reprendre, sous leur fournisseur. */
  openOrders: () => req<{ rows: { number: string; supplierId: string | null; supplierLabel: string | null; remaining: number }[] }>('GET', '/admin/orders/open'),
  createOrder: (input: {
    number?: string; supplierId?: string | null; supplierLabel?: string | null; sourceRef?: string | null;
    note?: string | null; orderedOn?: number | null;
    lines: { gencode: string; orderedQty: number; unitPriceFine?: number | null }[];
  }) => req<CommandeDetaillee>('POST', '/admin/orders', input),
  /**
   * L'ÉDITEUR : enregistre la saisie — en brouillon ou validée, une commande par fournisseur, tout ou rien.
   * ⚠️ La clé du geste rend un double clic inoffensif : rejouée, elle rend les mêmes commandes.
   */
  saveOrderBatch: (input: { key: string; mode: 'draft' | 'validate'; groups: OrderGroupInput[]; discardDrafts?: { number: string; updatedAt: number | null }[] }) =>
    req<{ rows: { number: string; supplierLabel: string; draft: boolean; pieces: number }[] }>('POST', '/admin/orders/batch', input),
  /** L'ÉDITEUR : la recherche par nom, code, famille, coloris, marque ou fournisseur ; un code-barres exact désigne sa case. */
  orderEditorSearch: (q: string) =>
    req<{ rows: OrderModelHit[]; truncated: boolean }>('GET', `/admin/orders/editor/search?q=${encodeURIComponent(q)}`),
  /** L'ÉDITEUR : les modèles prêts pour la grille. */
  orderEditorModels: (modelKeys: readonly string[]) =>
    req<{ rows: OrderModel[] }>('GET', `/admin/orders/editor/models?${new URLSearchParams(modelKeys.map((k) => ['model', k]))}`),
  /** L'ÉDITEUR : les ventes nettes d'un fournisseur entre deux dates civiles, par déclinaison. */
  orderRestock: (supplierId: string, from: string, to: string) =>
    req<{ rows: { modelKey: string; gencode: string; qty: number }[]; truncated: boolean; limit: number }>(
      'GET', `/admin/orders/editor/restock?supplier=${encodeURIComponent(supplierId)}&from=${from}&to=${to}`),
  /** Clôture « reliquat abandonné » (D6) — seule une commande partiellement livrée l'accepte. */
  closeOrder: (number: string) => req<CommandeDetaillee>('POST', '/admin/orders/close', { number }),
  deleteOrder: (number: string) => req<{ removed: boolean }>('POST', '/admin/orders/delete', { number }),
  /**
   * LE BON DE COMMANDE EN PDF — l'adresse, pas le contenu : le navigateur l'affiche (ou l'enregistre avec `download`)
   * et porte le cookie de session. Le même octet que la pièce jointe de l'envoi.
   */
  orderPdfUrl: (number: string, download = false) =>
    `/v1/admin/orders/pdf?number=${encodeURIComponent(number)}${download ? '&download=1' : ''}`,
  /**
   * ENVOIE LE BON DE COMMANDE au fournisseur, par la messagerie du magasin, à l'adresse de commande de sa fiche.
   * ⚠️ Un refus (messagerie non réglée, fournisseur sans adresse, transport) arrive avec son motif réel : la
   *    commande reste alors « à transmettre ».
   */
  sendOrder: (number: string) => req<{ sentAt: number; sentTo: string }>('POST', '/admin/orders/send', { number }),

  // ── Rétrocessions ──────────────────────────────────────────────────────────────────────────────
  retros: () => req<{ rows: RetroResume[] }>('GET', '/admin/retrocessions'),
  retro: (id: string) => req<RetroDetaillee>('GET', `/admin/retrocessions/one?id=${encodeURIComponent(id)}`),
  /** Destinations = même enseigne (automatique) + le CARNET des destinataires hors plateforme (D38). */
  retroPartners: () => req<{ rows: { gln: string; name: string }[]; external: { label: string; extGln: string | null }[]; tenantKnown: boolean }>('GET', '/admin/retrocessions/partners'),
  createRetro: (input: {
    kind?: NatureDeRetrocession; partnerGln?: string; note?: string | null;
    /** Hors plateforme : envoi unilatéral, destinataire en clair, prix de cession facultatif par ligne. */
    external?: boolean; partnerLabel?: string; partnerExtGln?: string | null;
    lines: { gencode: string; qty: number; unitPriceFine?: number | null }[];
  }) =>
    req<RetroDetaillee>('POST', '/admin/retrocessions', input),
  retroAccept: (id: string) => req<RetroDetaillee>('POST', '/admin/retrocessions/accept', { id }),
  retroRefuseRequest: (id: string, reason: string | null) => req<RetroDetaillee>('POST', '/admin/retrocessions/refuse-request', { id, reason }),
  retroShip: (id: string) => req<RetroDetaillee>('POST', '/admin/retrocessions/ship', { id }),
  retroCheck: (id: string, lines: { gencode: string; checkedQty: number | null }[]) =>
    req<RetroDetaillee>('POST', '/admin/retrocessions/check', { id, lines }),
  retroReceive: (id: string) => req<{ movements: number; pieces: number; piecesSansPrix: number; variance: number; restoredModels: number; doc: RetroDetaillee }>('POST', '/admin/retrocessions/receive', { id }),
  retroRefuseShipment: (id: string, reason: string | null) => req<RetroDetaillee>('POST', '/admin/retrocessions/refuse-shipment', { id, reason }),
  retroCancel: (id: string) => req<RetroDetaillee>('POST', '/admin/retrocessions/cancel', { id }),
  retroArbitrate: (id: string, gencode: string, resolution: 'recount' | 'loss') =>
    req<RetroDetaillee>('POST', '/admin/retrocessions/arbitrate', { id, gencode, resolution }),

  // ── Retours fournisseur ────────────────────────────────────────────────────────────────────────
  supplierReturns: () => req<{ rows: RetourFournisseurResume[] }>('GET', '/admin/supplier-returns'),
  supplierReturn: (id: string) => req<RetourFournisseurDetaille>('GET', `/admin/supplier-returns/one?id=${encodeURIComponent(id)}`),
  createSupplierReturn: (input: { supplierId?: string | null; supplierLabel: string; note?: string | null; lines: { gencode: string; qty: number; unitPriceFine?: number | null }[] }) =>
    req<RetourFournisseurDetaille>('POST', '/admin/supplier-returns', input),
  setSupplierReturnPrice: (id: string, gencode: string, unitPriceFine: number | null) =>
    req<RetourFournisseurDetaille>('POST', '/admin/supplier-returns/set-price', { id, gencode, unitPriceFine }),
  shipSupplierReturn: (id: string) => req<RetourFournisseurDetaille>('POST', '/admin/supplier-returns/ship', { id }),
  cancelSupplierReturn: (id: string) => req<RetourFournisseurDetaille>('POST', '/admin/supplier-returns/cancel', { id }),

  // ── Champs personnalisés ───────────────────────────────────────────────────────────────────────
  customFields: () => req<{ rows: CustomField[] }>('GET', '/admin/custom-fields'),
  createCustomField: (input: { label: string; fieldType: CustomFieldType; options?: string[] }) =>
    req<CustomField>('POST', '/admin/custom-fields', input),
  /** Supprime le champ ET ses valeurs sur toutes les fiches — rend le compte réel de fiches purgées. */
  deleteCustomField: (id: string) => req<{ removed: boolean; purged: number }>('POST', '/admin/custom-fields/delete', { id }),
  articleCustomValues: (modelKey: string) =>
    req<{ fields: CustomField[]; values: CustomValues }>('GET', `/admin/articles/custom-values?model=${encodeURIComponent(modelKey)}`),
  setArticleCustomValue: (modelKey: string, fieldId: string, value: string | number | boolean | null) =>
    req<{ value: string | number | boolean | null }>('POST', '/admin/articles/custom-values', { model: modelKey, fieldId, value }),

  // ── Articles rapides ───────────────────────────────────────────────────────────────────────────
  // ── Promotions ──
  /** Les promotions DANS L'ORDRE OÙ LA CAISSE LES APPLIQUERA, avec le périmètre mesuré de chacune. */
  promotions: () => req<{ rows: PromotionRow[] }>('GET', '/admin/promotions'),
  /** Crée (sans `id`) ou modifie (avec) — une seule route, donc un seul jeu de règles à tenir. */
  savePromotion: (p: PromotionSave, idempotencyKey: string) =>
    req<{ id: string }>('POST', '/admin/promotions', { ...p, idempotencyKey }),
  /** ⚠️ Route à part : basculer l'interrupteur ne renvoie pas les champs qu'on n'a pas touchés. */
  setPromotionActive: (id: string, active: boolean) =>
    req<{ id: string }>('PUT', `/admin/promotions/${encodeURIComponent(id)}/active`, { active }),
  /** ⚠️ LA LISTE COMPLÈTE, dans l'ordre voulu : l'ordre décide du prix encaissé, il ne se devine pas. */
  reorderPromotions: (ids: string[], idempotencyKey: string) =>
    req<{ ordered: number }>('PUT', '/admin/promotions/order', { ids, idempotencyKey }),
  deletePromotion: (id: string) =>
    req<{ id: string }>('DELETE', `/admin/promotions/${encodeURIComponent(id)}`),

  // ── Étiquettes ──
  /**
   * Les gabarits du magasin, ET les standards.
   * ⚠️ LES STANDARDS VIENNENT DU SERVEUR bien qu'ils vivent dans le cœur : en porter une copie ici
   * ferait diverger deux géométries, et c'est celle du navigateur qui imprimerait.
   */
  labels: () => req<{ standard: LabelTemplate[]; custom: LabelTemplate[]; hidden: string[] }>('GET', '/admin/labels'),
  /** Crée ou corrige un gabarit personnalisé — la clé décide, il n'y a qu'une route. */
  saveLabelTemplate: (template: LabelTemplate) =>
    req<LabelTemplate>('POST', '/admin/labels/save', { template }),
  /** ⚠️ UN SEUL GESTE : supprime le personnalisé, MASQUE le standard — c'est le serveur qui sait lequel. */
  removeLabelTemplate: (key: string) =>
    req<{ removed?: boolean; hidden?: boolean }>('POST', '/admin/labels/remove', { key }),
  restoreLabelTemplate: (key: string) =>
    req<{ restored: boolean }>('POST', '/admin/labels/restore', { key }),
  /** UN VRAI ARTICLE du magasin pour l'aperçu — `row: null` quand le catalogue est vide (§23.0). */
  labelSample: (index: number) =>
    req<{ row: LabelSampleRow | null; total: number }>('GET', `/admin/labels/sample?index=${index}`),
  /** Les lignes d'un panier d'impression. ⚠️ `missing` dit ce qu'aucune déclinaison ne porte. */
  labelData: (gencodes: string[]) =>
    req<{ rows: LabelSampleRow[]; missing: string[] }>('POST', '/admin/labels/data', { gencodes }),

  createQuickLayout: (name: string) => req<QuickLayout>('POST', '/admin/quick-layouts', { name }),
  renameQuickLayout: (id: string, name: string) => req<{ id: string }>('POST', '/admin/quick-layouts/rename', { id, name }),
  /**
   * Supprime la disposition ET ses tuiles — rend le compte réel de tuiles parties. `heir` : celle qui devient la défaut
   * quand on supprime la défaut — la remplaçante que la confirmation a nommée (le serveur refuse la défaut sans elle).
   */
  deleteQuickLayout: (id: string, heir: string | null) =>
    req<{ removed: boolean; tiles: number }>('POST', '/admin/quick-layouts/delete', { id, heir }),
  /** La première disposition d'un magasin qui n'en a aucune — sans effet s'il en existe déjà une. */
  ensureFirstQuickLayout: (name: string) => req<{ created: boolean; id: string }>('POST', '/admin/quick-layouts/first', { name }),
  /** Renvoyer aux caisses : réémet l'état publié et fait monter la génération (`null` = jamais composée au studio). */
  republishQuickLayout: (id: string) =>
    req<{ republished: number; generation: number | null }>('POST', `/admin/quick-layouts/${encodeURIComponent(id)}/republish`),
  quickStudio: () => req<StudioView>('GET', '/admin/quick-studio'),
  saveQuickStudioDraft: (revision: number, document: StudioDocument) =>
    req<{ revision: number; dirty: boolean }>('PUT', '/admin/quick-studio/draft', { revision, document }),
  discardQuickStudioDraft: (revision: number) =>
    req<{ revision: number }>('POST', '/admin/quick-studio/draft/discard', { revision }),
  publishQuickStudio: (revision: number) => req<StudioPublishReport>('POST', '/admin/quick-studio/publish', { revision }),
  /**
   * Dépose la photo d'une tuile (et sa vignette) — rend son adresse, que le brouillon portera.
   * ⚠️ EN `FormData` BRUT : le navigateur écrit lui-même la frontière du multipart (voir `addArticlePhotos`).
   */
  uploadQuickStudioPhoto: (file: File, thumb: File | null) => {
    const fd = new FormData();
    fd.append('file', file);
    if (thumb) fd.append('thumb', thumb);
    return req<{ url: string; thumbUrl: string | null }>('POST', '/admin/quick-studio/photo', fd);
  },
  /** Les familles, sous-familles ou marques qu'une tuile peut déverser — celles qui ont au moins une déclinaison vendable. */
  quickStudioSources: (kind: QuickSourceKind) =>
    req<{ kind: QuickSourceKind; rows: StudioSource[] }>('GET', `/admin/quick-studio/sources?${new URLSearchParams({ kind }).toString()}`),
  /** Ce qu'une source déverse, dans l'ordre de la caisse. */
  quickStudioPour: (kind: QuickSourceKind, id: string) =>
    req<StudioPour>('GET', `/admin/quick-studio/pour?${new URLSearchParams({ kind, id }).toString()}`),
  quickStudioSearch: (f: StudioSearch) =>
    req<{ rows: StudioCandidate[]; truncated: boolean; facets: { sectors: string[]; families: string[]; brands: string[] } }>(
      'GET', `/admin/quick-studio/search?${new URLSearchParams({ q: f.q, sector: f.sector, family: f.family, brand: f.brand }).toString()}`),
  /**
   * Ce que l'envoi depuis le catalogue donnerait, disposition par disposition. `already` est compté par le
   * serveur sur le document de TRAVAIL (le brouillon s'il existe), à l'accueil de chaque disposition — là où
   * l'envoi pose ses tuiles, et c'est ce nombre qui fait choisir la destination. `home` compte les articles posés à
   * l'accueil. `registers` = les caisses ATTRIBUÉES à la disposition ; `free` = celles qu'aucune autre ne prend, que
   * seule la disposition par défaut affiche (vide pour les autres) — rendues à part pour être nommées comme telles.
   */
  quickDestinations:(modelKeys: string[]) =>
    req<{ models: number; variants: number; rows: (QuickLayout & { free: string[]; home: number; already: number })[] }>(
      'POST', '/admin/quick-layouts/destinations', { modelKeys },
    ),
  /**
   * Envoie une sélection du CATALOGUE (par modèle) au BROUILLON du studio, à l'accueil de la disposition — expansée
   * en déclinaisons. Rien ne descend aux caisses avant la publication.
   */
  addModelsToStudio: (layoutId: string | null, modelKeys: string[]) =>
    req<{ added: number; duplicates: number; models: number; variants: number; revision: number }>(
      'POST', '/admin/quick-studio/from-models', { layoutId, modelKeys },
    ),

  // ── Campagnes d'inventaire (§6.3) ─────────────────────────────────────────────────────────────
  /** La liste + le vocabulaire des types : l'écran ne recopie pas ce dernier, qui deviendrait faux. */
  inventories: () => req<{ rows: InventoryCampaign[]; kinds: Record<string, string> }>('GET', '/admin/inventories'),
  /**
   * Le rapprochement d'une campagne, PAR PAGE — photo figée contre comptages, calcul fait par le serveur.
   *
   * ⚠️ LA VUE ET LE FILTRE PARTENT AU SERVEUR, ils ne se rejouent pas ici : filtrer une PAGE donnerait
   * « 3 écarts » sur les deux cents lignes reçues alors qu'il y en a neuf cents. Les compteurs, eux,
   * viennent de l'ensemble.
   */
  inventory: (id: string, page?: InventoryFilter & { offset?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (page?.view) qs.set('view', page.view);
    if (page?.offset) qs.set('offset', String(page.offset));
    if (page?.limit) qs.set('limit', String(page.limit));
    if (page?.q) qs.set('q', page.q);
    if (page?.zone) qs.set('zone', page.zone);
    if (page?.brand) qs.set('brand', page.brand);
    if (page?.family) qs.set('family', page.family);
    if (page?.season) qs.set('season', page.season);
    const suffixe = qs.toString();
    return req<InventoryDetail>('GET', `/admin/inventories/${encodeURIComponent(id)}${suffixe ? `?${suffixe}` : ''}`);
  },
  /**
   * RETROUVER UN ARTICLE DANS LA CAMPAGNE (SPEC §4).
   * ⚠️ Route à part du rapprochement : on la sollicite une frappe après l'autre, pièce en main.
   */
  searchInventory: (id: string, q: string) =>
    req<InventorySearchResult>('GET', `/admin/inventories/${encodeURIComponent(id)}/search?q=${encodeURIComponent(q)}`),
  /**
   * OUVRIR UNE CAMPAGNE — rien n'est figé tant qu'on ne la démarre pas.
   * ⚠️ Le TYPE décide de la dimension du périmètre : le serveur ne retient que celle-là, un périmètre
   * d'une autre dimension serait une étiquette qui ment sur ce que la photo couvre.
   */
  createInventory: (label: string, kind: string, scope: Record<string, string[]>, targetDate: string) =>
    req<InventoryCampaign>('POST', '/admin/inventories', { label, kind, scope, targetDate }),
  /** DÉMARRER — le geste qui FIGE le théorique. Il ne se reprend pas. */
  startInventory: (id: string) => req<InventoryCampaign>('POST', `/admin/inventories/${encodeURIComponent(id)}/start`, {}),
  /**
   * BIPER — chaque appel AJOUTE une pièce. ⚠️ Distinct de `countInventory`, qui REMPLACE : taper « 12 »
   * dit « il y en a douze », biper dit « en voici une de plus ».
   */
  /**
   * CORRIGER D'UNE PIÈCE — le même geste que le bip, en plus ou en moins.
   * ⚠️ ADDITIF, JAMAIS ABSOLU : c'est ce qui le rend juste quel que soit l'endroit où le reste a été
   * compté. Le serveur refuse de creuser une zone sous zéro.
   */
  adjustInventory: (id: string, gencode: string, zone: string, delta: number) =>
    req<{ name: string; gencode: string; total: number; theoreticalQtyMinor: number }>(
      'POST', `/admin/inventories/${encodeURIComponent(id)}/bump`, { gencode, zone, delta }),
  bumpInventory: (id: string, gencode: string, zone: string) =>
    req<{ name: string; gencode: string; total: number; theoreticalQtyMinor: number }>('POST', `/admin/inventories/${encodeURIComponent(id)}/bump`, { gencode, zone, delta: 1 }),
  countInventory: (id: string, gencode: string, qtyMinor: number, zone: string) =>
    req<{ gencode: string }>('POST', `/admin/inventories/${encodeURIComponent(id)}/count`, { gencode, qtyMinor, zone }),
  /**
   * Les zones d'Mobile Partner disponibles pour cette campagne.
   *
   * ⚠️ APPEL SÉPARÉ DU RAPPROCHEMENT, et c'est ce qui rend le rafraîchissement supportable : l'écran
   * l'interroge à intervalle régulier pour voir arriver les zones, sans relire tout le rapprochement.
   */
  inventoryZones: (id: string) =>
    req<{ rows: InventoryZone[] }>('GET', `/admin/inventories/${encodeURIComponent(id)}/zones`),
  /**
   * OUVRIR une zone depuis le back-office — le pendant clavier de l'annonce d'un terminal.
   * ⚠️ IDEMPOTENTE : l'identifiant vient de la campagne et du nom, donc un double clic retombe sur la
   * MÊME zone. C'est elle qui fait apparaître la tuile, et qui la fait SURVIVRE au rechargement.
   */
  openInventoryZone: (id: string, label: string) =>
    req<{ zoneId: string; already: boolean }>('POST', `/admin/inventories/${encodeURIComponent(id)}/zones`, { label }),
  /**
   * RETIRER une zone de l'inventaire — toute zone, comptée ou non, back-office ou terminal.
   * ⚠️ Sans `withCounts`, une zone qui porte des pièces est REFUSÉE en nommant le compte : c'est la
   * confirmation qui protège, pas le refus. Avec, ses comptages sortent du cumul (le stock, jamais).
   */
  deleteInventoryZone: (id: string, zoneId: string, withCounts = false) =>
    req<{ zoneId: string; label: string; removedPieces: number }>('DELETE',
      `/admin/inventories/${encodeURIComponent(id)}/zones/${encodeURIComponent(zoneId)}${withCounts ? '?withCounts=1' : ''}`),
  /**
   * DÉCLARER une zone terminée, ou la ROUVRIR. ⚠️ Ne fige aucun comptage — il reste modifiable jusqu'à
   * la clôture ; c'est seulement ce qui dit ce qu'il RESTE à compter.
   */
  finishInventoryZone: (id: string, zoneId: string, finished: boolean) =>
    req<{ label: string; finishedAt: number | null }>('POST',
      `/admin/inventories/${encodeURIComponent(id)}/zones/${encodeURIComponent(zoneId)}/finish`, { finished }),
  /** LA VENTILATION PAR ARTICLE d'une zone — même source que le total de sa carte (§5). */
  inventoryZoneLines: (id: string, zoneId: string) =>
    req<{ rows: { gencode: string; name: string; articleCode: string; sizeLabel: string; qtyMinor: number; zoneKey: string }[] }>(
      'GET', `/admin/inventories/${encodeURIComponent(id)}/zones/${encodeURIComponent(zoneId)}/lines`),
  /** RATTACHER une zone — geste HUMAIN : une zone scannée au mauvais rayon ne doit pas se verser seule. */
  attachInventoryZone: (id: string, zoneId: string) =>
    req<{ counted: number; skipped: number }>('POST', `/admin/inventories/${encodeURIComponent(id)}/zones/${encodeURIComponent(zoneId)}/attach`, {}),
  /**
   * ABANDONNER — les comptages et les zones sont jetés, le stock n'est pas touché.
   * ⚠️ Ce n'est PAS une clôture : aucun mouvement, aucun rapport, aucune remise à zéro.
   */
  abandonInventory: (id: string) =>
    req<{ discarded: number; zones: number }>('POST', `/admin/inventories/${encodeURIComponent(id)}/abandon`, {}),
  /** VALIDER — écrit les ajustements et ferme. Le compte-rendu dit ce qui n'a PAS été ajusté. */
  closeInventory: (id: string) =>
    req<{ adjusted: number; zeroed: number; excluded: number; notCounted: number; deltaValueCents: number }>('POST', `/admin/inventories/${encodeURIComponent(id)}/close`, {}),
  /**
   * Le procès-verbal SCELLÉ d'une campagne close.
   *
   * ⚠️ ON LIT LE DOCUMENT, ON NE LE RECALCULE PAS : la fenêtre de mouvements du rapprochement court
   * jusqu'à « maintenant », donc un PV régénéré plus tard ne rendrait pas les mêmes nombres.
   * ⚠️ 404 sur une campagne non close — un PV n'existe qu'à partir du scellement.
   */
  inventoryReport: (id: string, page?: { offset?: number; limit?: number; variancesOnly?: boolean }) => {
    const qs = new URLSearchParams();
    if (page?.offset) qs.set('offset', String(page.offset));
    if (page?.limit) qs.set('limit', String(page.limit));
    if (page?.variancesOnly) qs.set('variances', '1');
    const suffixe = qs.toString();
    return req<{ header: InventoryReportHeader; rows: InventoryReportLine[]; total: number }>(
      'GET', `/admin/inventories/${encodeURIComponent(id)}/report${suffixe ? `?${suffixe}` : ''}`);
  },
  /** EXCLURE (ou réintégrer) des références : un exclu GARDE son théorique au lieu d'être remis à zéro. */
  /**
   * LE PROCÈS-VERBAL EN PDF — l'URL, pas le contenu.
   *
   * ⚠️ ELLE REND UNE ADRESSE, ET C'EST DÉLIBÉRÉ. Passer le fichier par le code obligerait à le tenir
   * en mémoire puis à fabriquer un lien de téléchargement à la main ; le navigateur sait déjà faire
   * les deux. Il porte aussi le cookie de session, donc l'authentification suit.
   * ⚠️ Le serveur nomme le fichier (`content-disposition`) : « rapport.pdf » dans un dossier de
   * téléchargements est un fichier qu'on ne retrouve pas, et qu'on écrase au second export.
   */
  /**
   * L'ENVOI PAR MAIL EST-IL POSSIBLE ? — demandé AVANT d'offrir un bouton.
   * ⚠️ Le back-office n'a pas d'écran de réglages e-mail : son transport vient de l'environnement du
   * serveur, donc la réponse dépend du déploiement et ne se devine pas depuis le navigateur.
   */
  mailReadiness: () => req<{ ready: boolean; why: string }>('GET', '/admin/mail/readiness'),
  /** Envoie le procès-verbal en PIÈCE JOINTE — le destinataire n’a pas de compte au back-office. */
  mailInventoryReport: (id: string, to: string, message: string) =>
    req<{ to: string }>('POST', `/admin/inventories/${encodeURIComponent(id)}/report/mail`, { to, message }),
  inventoryReportPdfUrl: (id: string) =>
    `/v1/admin/inventories/${encodeURIComponent(id)}/report.pdf`,
  /**
   * CE QUE « HORS SUIVI DE STOCK » TOUCHERAIT — le drapeau est de MODÈLE, pas de déclinaison.
   * ⚠️ Lecture seule : elle sert à écrire la question, jamais à agir.
   */
  previewUntrack: (id: string, gencode: string) =>
    req<{ modelKey: string; name: string; variants: number; theoreticalQtyMinor: number; inSnapshot: number }>(
      'GET', `/admin/inventories/${encodeURIComponent(id)}/untrack?gencode=${encodeURIComponent(gencode)}`),
  /**
   * SORTIR UN ARTICLE DU SUIVI DE STOCK, depuis l'inventaire.
   * ⚠️ Deux effets : le drapeau de catalogue (campagnes à venir) ET la sortie de la campagne en cours,
   * dont la photo est déjà figée — sans quoi la clôture effacerait le stock qu'on vient de dissocier.
   */
  untrackFromInventory: (id: string, gencode: string, idempotencyKey: string) =>
    req<{ modelKey: string; variants: number; excluded: number }>(
      'POST', `/admin/inventories/${encodeURIComponent(id)}/untrack`, { gencode, idempotencyKey }),
  excludeFromInventory: (id: string, gencodes: string[], excluded: boolean, reason: string) =>
    req<{ changed: number }>('POST', `/admin/inventories/${encodeURIComponent(id)}/exclude`, { gencodes, excluded, reason }),
  /**
   * EXCLURE (ou réintégrer) TOUT CE QUE LE FILTRE DÉSIGNE — au-delà de la page affichée.
   *
   * ⚠️ POURQUOI PAS UNE LISTE DE CODES. Une case maîtresse ne coche que ce qui est chargé : sur neuf
   * cents références filtrées dont deux cents sont à l'écran, on croirait avoir tout exclu et la
   * clôture remettrait à zéro les sept cents autres. Le serveur résout le filtre avec le prédicat de
   * la LECTURE — c'est la seule garantie que l'exclu est exactement ce qui était affiché.
   */
  excludeInventoryByFilter: (id: string, filter: InventoryFilter, excluded: boolean, reason: string) =>
    req<{ changed: number }>('POST', `/admin/inventories/${encodeURIComponent(id)}/exclude`, { filter, excluded, reason }),

  // ── Articles créés en magasin — la file de validation (§6.1bis) ───────────────────────────────
  /**
   * La file ET son rapprochement, en UN appel.
   *
   * ⚠️ Les motifs de refus viennent du SERVEUR (`refusalReasons`), jamais d'une liste recopiée ici :
   * ils servent à COMPTER pourquoi on refuse, et une copie deviendrait fausse au premier motif ajouté.
   */
  articleDrafts: () => req<{ rows: ArticleDraft[]; refusalReasons: Record<string, string> }>('GET', '/admin/article-drafts'),
  /** VALIDER — le brouillon devient un article ; c'est là, et seulement là, qu'un gencode interne est posé. */
  validateArticleDraft: (id: string, patch: ArticleDraftPatch) =>
    req<{ id: string; gencode: string | null }>('POST', `/admin/article-drafts/${encodeURIComponent(id)}/validate`, patch),
  /** FUSIONNER — le brouillon désigne un article existant. Rien n'est réécrit : la décision résout. */
  mergeArticleDraft: (id: string, targetArticleId: string) =>
    req<{ id: string; targetArticleId: string | null }>('POST', `/admin/article-drafts/${encodeURIComponent(id)}/merge`, { targetArticleId }),
  /** REFUSER — motif EXIGÉ, pris dans la liste fermée du serveur. */
  refuseArticleDraft: (id: string, reason: string, note: string | null) =>
    req<{ id: string; reasonCode: string | null }>('POST', `/admin/article-drafts/${encodeURIComponent(id)}/refuse`, { reason, note }),

  // ── Centre d'écarts ────────────────────────────────────────────────────────────────────────────
  variances: () => req<{ rows: EcartRow[]; motifs: EcartMotif[] }>('GET', '/admin/variances'),
  /**
   * Le RÉSUMÉ pour la tuile du tableau de bord. ⚠️ `stockExploitable` distingue « aucun écart » de
   * « je n’ai rien à regarder » : la détection de stock négatif est MUETTE sur un magasin dont le
   * stock n’a jamais été renseigné, et une tuile verte y affirmerait que tout va bien.
   */
  variancesResume: () => req<{ open: number; bySource: Record<SourceDEcart, number>; stockExploitable: boolean; top: EcartRow[] }>('GET', '/admin/variances/resume'),
  justifyVariance: (id: string, reason: string, note: string | null) =>
    req<{ id: string }>('POST', '/admin/variances/justify', { id, reason, note }),
  fixNegativeStock: (id: string) => req<{ id: string }>('POST', '/admin/variances/fix-stock', { id }),

  // ── État du stock ──────────────────────────────────────────────────────────────────────────────
  stock: (f: StockQuery = {}) => req<StockPage>('GET', `/admin/stock${stockQs(f)}`),
  stockLedger: (gencode: string) =>
    req<{ rows: StockLedgerLine[] }>('GET', `/admin/stock/${encodeURIComponent(gencode)}/ledger`),
  /**
   * Les corrections, EN LOT et en un appel — l'écran corrige plusieurs lignes puis enregistre.
   * ⚠️ On envoie la quantité VOULUE, pas l'écart : le serveur calcule le delta contre le stock qu'il
   * a lui-même, donc une photo qui a bougé entre l'affichage et l'enregistrement ne fausse rien.
   */
  correctStock: (rows: { gencode: string; targetQty: number }[], reason: StockCorrectionReason, note: string | null) =>
    req<{ written: number; skipped: number }>('POST', '/admin/stock/corrections', { rows, reason, note }),
  stockGrid: (f: StockQuery = {}) => req<{ sections: StockGridSection[] }>('GET', `/admin/stock/grid${stockQs(f)}`),
  stockPivot: (rows: StockPivotDimension, cols: StockPivotDimension, measure: StockPivotMeasure, f: StockQuery = {}) =>
    req<StockPivot>('GET', `/admin/stock/pivot${stockQs({ ...f, rows, cols, measure })}`),
  stockRemote: (stores: string[], f: StockQuery = {}) =>
    req<{ stores: StockRemoteStore[] }>('GET', `/admin/stock/remote${stockQs({ ...f, stores: stores.join(',') })}`),
  stockRemoteStores: () => req<{ stores: { gln: string; name: string }[] }>('GET', '/admin/stock/remote/stores'),
  stockMovements: (f: MovementsQuery = {}) => {
    const p = new URLSearchParams();
    if (f.q) p.set('q', f.q);
    if (f.days != null) p.set('days', String(f.days));
    if (f.kind) p.set('kind', f.kind);
    if (f.direction) p.set('direction', f.direction);
    if (f.sort) p.set('sort', f.sort);
    if (f.dir) p.set('dir', f.dir);
    if (f.offset) p.set('offset', String(f.offset));
    const qs = p.toString();
    return req<MovementsPage>('GET', `/admin/stock/movements${qs ? `?${qs}` : ''}`);
  },
  // ── CAMPAGNES ───────────────────────────────────────────────────────────────────────────
  // ⚠️ `canSend` vient du SERVEUR : lui seul sait si un transport est câblé. L'écran ne le devine
  //    pas, sinon il offrirait un bouton d'envoi qui rendrait 404.
  campaigns: () => req<{
    rows: Campaign[]; activity: CampaignActivity; attribution: CampaignAttribution[]; canSend: boolean;
    /**
     * Motif qui EMPÊCHE toute campagne dans ce magasin — aujourd'hui : aucun fuseau déclaré.
     * ⚠️ ABSENT QUAND TOUT VA BIEN, jamais une chaîne vide : `blocked` présent veut dire « rien ne
     * peut partir », et une chaîne vide ferait afficher un bandeau muet.
     */
    blocked?: string;
  }>('GET', '/admin/clients/campaigns'),
  /**
   * Le solde de crédits du canal, et — si l'on donne un effectif — le verdict de couverture.
   * ⚠️ LE MÊME APPEL rend les deux : les dédoubler ferait deux lectures là où l'écran en veut une.
   */
  campaignCredits: (channel: string, recipients?: number) => {
    const qs = new URLSearchParams({ channel });
    if (recipients !== undefined) qs.set('recipients', String(recipients));
    /**
     * ⚠️ `channelBlocked` ET NON `blocked` : la liste des campagnes rend déjà un `blocked` qui veut
     * dire « aucune campagne ne peut partir de ce magasin » (fuseau non posé). Celui-ci dit qu’UN
     * CANAL ne peut pas partir — suspendu par Orgazone, ou wallet désactivé par le magasin —, avec son motif :
     * deux faits différents, deux noms.
     */
    return req<{ credits: CampaignCredits; verdict?: CreditVerdict; channelBlocked?: string | null }>(
      'GET', `/admin/clients/campaigns/credits?${qs.toString()}`);
  },
  /** Les lieux des fiches du magasin — les villes et codes postaux où vit au moins un client. */
  campaignPlaces: () =>
    req<{ rows: CampaignPlace[]; truncated: boolean }>('GET', '/admin/clients/campaigns/places'),
  /** Les segments RFM du magasin, avec leur effectif — le cœur nomme, le back-office compte. */
  campaignSegments: () =>
    req<{ counts: Record<string, number>; total: number; names: string[] }>('GET', '/admin/clients/campaigns/segments'),
  campaign: (id: string) =>
    req<{ record: Campaign; tally: Record<string, number>; canSend: boolean }>(
      'GET', `/admin/clients/campaigns/one?id=${encodeURIComponent(id)}`),
  createCampaign: (input: CampaignInput) =>
    req<{ record: Campaign }>('POST', '/admin/clients/campaigns', input),
  updateCampaign: (id: string, input: CampaignInput) =>
    req<{ record: Campaign }>('PUT', '/admin/clients/campaigns', { id, ...input }),
  /**
   * Combien de personnes, SANS rien figer — le nombre à comparer au solde de crédits.
   * ⚠️ `minHoldout` VIENT DU SERVEUR : c'est la plus petite taille de groupe témoin qui puisse un
   * jour conclure, DÉRIVÉE du seuil statistique du calcul d'écart. Le recopier ici en ferait une
   * seconde vérité, qui survivrait au jour où le calcul change.
   */
  /** Les audiences enregistrées du magasin, la plus récemment modifiée d'abord. */
  savedAudiences: () =>
    req<{ rows: SavedAudience[] }>('GET', '/admin/clients/campaigns/audiences'),
  /**
   * Enregistre (ou REMPLACE) une audience sous ce nom.
   * ⚠️ ON ENVOIE DES CRITÈRES, JAMAIS UNE LISTE DE GENS : « Gold dormants » doit désigner les Gold
   * dormants du jour où l'on s'en sert, pas ceux d'il y a six mois.
   */
  saveAudience: (name: string, filters: CampaignFilters) =>
    req<{ record: SavedAudience }>('POST', '/admin/clients/campaigns/audiences', { name, ...filters }),
  deleteAudience: (id: string) =>
    req<{ ok?: true }>('DELETE', `/admin/clients/campaigns/audiences/${encodeURIComponent(id)}`),
  campaignAudience: (channel: string, filters: CampaignFilters = {}) =>
    req<{ total: number; minHoldout: number }>('POST', '/admin/clients/campaigns/audience', { channel, ...filters }),
  /**
   * Fige l'audience — et, si on le demande, en réserve une part comme GROUPE TÉMOIN.
   * ⚠️ `holdoutPct` EST UNE DÉCISION COMMERCIALE : ces gens-là ne recevront pas l'offre. Zéro par
   * défaut, ici comme au serveur — un témoin ne s'installe jamais tout seul.
   */
  freezeCampaign: (id: string, filters: CampaignFilters = {}, holdoutPct = 0) =>
    req<{ record: Campaign }>('POST', '/admin/clients/campaigns/freeze', { id, ...filters, holdoutPct }),
  /**
   * LES MODÈLES D’ENVOI AUTOMATIQUE du magasin.
   * ⚠️ 404 SI L’ORDONNANCEUR N’A PAS D’INDICE : le serveur refuse de monter ces routes plutôt que
   * d’accepter un envoi programmé qui dormirait jusqu’au prochain balayage complet. L’écran doit
   * donc traiter l’absence comme « pas disponible ici », pas comme une panne.
   */
  campaignTemplates: () =>
    req<{ rows: CampaignTemplate[] }>('GET', '/admin/clients/campaigns/templates'),
  /**
   * Crée un modèle — et le serveur calcule son échéance DANS LE FUSEAU DU MAGASIN.
   * ⚠️ ON N’ENVOIE NI INSTANT NI FUSEAU : une heure calculée dans le navigateur ferait partir le
   * message à l’heure de celui qui a cliqué, pas à celle du magasin.
   */
  createCampaignTemplate: (input: CampaignInput & { recurrence: CampaignRecurrence; holdoutPct?: number } & CampaignFilters) =>
    req<{ record: CampaignTemplate }>('POST', '/admin/clients/campaigns/templates', input),
  /** Suspend ou remet en service. ⚠️ Suspendre EFFACE l’échéance ; reprendre la recalcule depuis maintenant. */
  setCampaignTemplateActive: (id: string, active: boolean) =>
    req<{ record: CampaignTemplate }>('PATCH', `/admin/clients/campaigns/templates/${encodeURIComponent(id)}`, { active }),
  /** Retire un modèle. ⚠️ Les campagnes déjà engendrées RESTENT : leur compte rendu ne lui appartient pas. */
  deleteCampaignTemplate: (id: string) =>
    req<{ ok?: true }>('DELETE', `/admin/clients/campaigns/templates/${encodeURIComponent(id)}`),
  /** Lance (ou REPREND) l'envoi. Rend le compte rendu ET le compte débité. */
  sendCampaign: (id: string) =>
    req<{ report: CampaignReport; account: string; source: 'store' | 'platform'; provider: string }>(
      'POST', '/admin/clients/campaigns/send', { id }),

  // ── Messagerie du magasin ──
  // ⚠️ AUCUN GLN N'EST ENVOYÉ : le magasin est celui de la session, côté serveur. La seule exception
  //    est `copyStoreMail`, et c'est précisément là que le serveur revérifie le droit d'accès.
  /** Le réglage du magasin actif. */
  storeMail: () => req<StoreMailSettings>('GET', '/admin/store/mail'),
  /** Enregistre, et rend l'état RELU — pour que l'écran montre la base, pas ce qu'il vient de taper. */
  setStoreMail: (input: StoreMailInput) => req<StoreMailSettings>('PUT', '/admin/store/mail', input),
  /**
   * Éprouve le réglage en envoyant vraiment.
   * ⚠️ Un échec d'envoi rend 200 avec `ok: false` : le réglage EXISTE, c'est l'envoi qui a raté, et
   * c'est le message du transport qui dit quoi corriger.
   */
  testStoreMail: (to: string) =>
    req<{ ok: boolean; to: string; error: string | null }>('POST', '/admin/store/mail/test', { to }),
  /** Reprend la configuration d'un autre magasin, secret compris. Rend l'état relu. */
  copyStoreMail: (from: string) => req<StoreMailSettings>('POST', '/admin/store/mail/copy', { from }),

  // ── Accès back-office (comptes WEB) ──
  // ⚠️ AUCUN JETON NE REDESCEND : inviter ENVOIE un lien, il ne le rend pas. Chaque écriture rend la
  //    liste RELUE — l'écran montre la base, jamais ce qu'il vient de taper.
  accessAccounts: () => req<AccessView>('GET', '/admin/access/accounts'),
  inviteAccess: (email: string, name: string, role: string, glns: string[]) =>
    req<{ accounts: AccessAccount[] }>('POST', '/admin/access/invite', { email, name, role, glns }),
  /** Renvoie le lien d'invitation ou de réinitialisation. ⚠️ Rend seulement « parti », jamais le jeton. */
  resendAccessInvite: (email: string) => req<{ sent: true }>('POST', '/admin/access/resend', { email }),
  setAccessRole: (id: string, role: string) =>
    req<{ accounts: AccessAccount[] }>('PUT', '/admin/access/role', { id, role }),
  /** ⚠️ Révoquer ferme AUSSI les sessions ouvertes — sinon l'onglet déjà ouvert continue de travailler. */
  setAccessStatus: (id: string, active: boolean) =>
    req<{ accounts: AccessAccount[] }>('PUT', '/admin/access/status', { id, active }),
  setAccessStores: (id: string, glns: string[]) =>
    req<{ accounts: AccessAccount[] }>('PUT', '/admin/access/stores', { id, glns }),
  /** ⚠️ Réarme, ne dispense pas : le secret est effacé, le compte devra en poser un nouveau. */
  resetAccessTwoFactor: (id: string) =>
    req<{ accounts: AccessAccount[] }>('POST', '/admin/access/2fa/reset', { id }),

  // ── Opérateurs & droits ──
  // ⚠️ AUCUN GLN N'EST ENVOYÉ : le roster est celui du magasin de la session, côté serveur. Chaque
  //    écriture rend le roster RELU — l'écran montre la base, jamais ce qu'il vient de taper.
  operators: () => req<RosterView>('GET', '/admin/operators'),
  /**
   * Tire un code libre pour ce magasin.
   * ⚠️ LE NAVIGATEUR NE PEUT PAS LE FAIRE LUI-MÊME : les PIN sont hachés, donc seul le serveur sait
   * lesquels sont pris. Et ce tirage ne RÉSERVE rien — c'est la création qui tranche l'unicité.
   */
  suggestOperatorPin: () => req<{ pin: string }>('GET', '/admin/operators/pin/suggest'),
  setOperatorRights: (id: string, template: string, overrides: Record<string, string>) =>
    req<RosterView>('PUT', '/admin/operators/rights', { id, template, overrides, idempotencyKey: cleDeGeste('ops:rights', Date.now()) }),
  /**
   * Réapplique les modèles à tous les comptes — après un changement du catalogue des droits.
   * ⚠️ Relançable : un compte déjà aligné n'est pas réécrit ; la réponse dit combien l'ont été, avec le roster relu.
   */
  reapplyRightsTemplates: () =>
    req<{ updated: number; unchanged: number; roster: RosterView }>('POST', '/admin/operators/rights/reapply', { idempotencyKey: cleDeGeste('ops:rights-reapply', Date.now()) }),
  setOperatorActive: (id: string, active: boolean) =>
    req<RosterView>('PUT', '/admin/operators/status', { id, active, idempotencyKey: cleDeGeste('ops:active', Date.now()) }),
  /** Remplace le code d un compte. ⚠️ On REMPLACE, on ne révèle jamais : aucun chemin ne rend un PIN. */
  /**
   * Supprime un compte QUE RIEN NE DÉSIGNE.
   * ⚠️ Le serveur MESURE avant : un compte qui a encaissé est refusé, en nommant ce qui le porte.
   */
  deleteOperator: (id: string) => req<RosterView>(
    'DELETE', `/admin/operators/${encodeURIComponent(id)}?idempotencyKey=${encodeURIComponent(cleDeGeste('ops:del', Date.now()))}`),
  setOperatorPin: (id: string, pin: string) =>
    req<RosterView>('PUT', '/admin/operators/pin', { id, pin, idempotencyKey: cleDeGeste('ops:pin', Date.now()) }),
  /** ⚠️ Le PIN part en clair (quelqu'un doit le choisir) et n'est JAMAIS rendu — le serveur le hache. */
  createOperator: (name: string, template: string, pin: string) =>
    req<RosterView>('POST', '/admin/operators', { name, template, pin, idempotencyKey: cleDeGeste('ops:new', Date.now()) }),
  /**
   * Les modèles de droits du magasin. ⚠️ Chaque écriture change aussi les comptes qui suivent le modèle : la réponse dit
   * combien, avec le roster relu.
   */
  saveRightsTemplate: (id: string, change: RightsTemplateChange) =>
    req<{ accounts: number; roster: RosterView }>('PUT', `/admin/operators/templates/${encodeURIComponent(id)}`, { ...change, idempotencyKey: cleDeGeste('ops:tpl', Date.now()) }),
  createRightsTemplate: (label: string, from: string) =>
    req<{ id: string; roster: RosterView }>('POST', '/admin/operators/templates', { label, from, idempotencyKey: cleDeGeste('ops:tpl-new', Date.now()) }),
  resetRightsTemplate: (id: string) =>
    req<{ accounts: number; roster: RosterView }>('POST', `/admin/operators/templates/${encodeURIComponent(id)}/reset`, { idempotencyKey: cleDeGeste('ops:tpl-reset', Date.now()) }),
  applyRightsTemplate: (id: string) =>
    req<{ accounts: number; roster: RosterView }>('POST', `/admin/operators/templates/${encodeURIComponent(id)}/apply`, { idempotencyKey: cleDeGeste('ops:tpl-apply', Date.now()) }),
  deleteRightsTemplate: (id: string) => req<{ roster: RosterView }>(
    'DELETE', `/admin/operators/templates/${encodeURIComponent(id)}?idempotencyKey=${encodeURIComponent(cleDeGeste('ops:tpl-del', Date.now()))}`),

  stockViews: () => req<{ views: SavedStockView[] }>('GET', '/admin/stock/views'),
  saveStockView: (name: string, rows: StockPivotDimension, cols: StockPivotDimension, measure: StockPivotMeasure) =>
    req<SavedStockView>('POST', '/admin/stock/views', { name, rows, cols, measure }),
  deleteStockView: (name: string) =>
    req<{ name: string }>('DELETE', `/admin/stock/views/${encodeURIComponent(name)}`),
};
